// Boot and the render loop. This file owns the order things happen in and nothing else:
// every piece of behaviour lives in a module named by tests/test_contract.mjs.
//
// The order matters and is deliberate. The world draws BEFORE any network call finishes, because
// a visitor on a slow phone should see Earth in the first second and watch the satellites arrive,
// rather than watch a spinner and then get everything at once. Spec 0005's budget says the same
// thing in numbers.

import * as THREE from '../vendor/three.module.min.js';
import { clock } from './clock.js';
import { createRenderer } from './scene/renderer.js';
import { stage } from './scene/stage.js';
import { propagate } from './propagate/index.js';
import { parseFrame } from './propagate/frames.js';
import { createWorlds, WORLDS, positionOf, MAPS_HELD } from './scene/worlds.js';
import { createStarfield } from './scene/starfield.js';
import { createGlyphLayer } from './scene/glyphs.js';
import { createHeroes, closeUpDistance, SELECTED_PX, MODEL_SPAN, warmModels } from './scene/heroes.js';
import { limbFraming, fitDistance, discDistance, litOffset, groundDistanceKm, nightGroundPose, openingPlan, OPENING_KEY } from './scene/framing.js';
import { createCameraRig, worldFramingDistance, steadyDistance } from './scene/camera.js';
import { createViewShift, MAX_SHIFT_FRACTION, PILL_GAP_PX } from './scene/viewshift.js';
import { parsePlaceValue, keptPlace, browserStorage } from './sky/placelink.js';
import { dropFrom, readMoment, writeMoment, bootLink, laterLink, linkChange, read as readUrlKeys, write as writeUrlState, clear as clearUrlState, stopIndex, parseCam } from './ui/urlstate.js'; // 0077 W3-e
import { guessObserver, roundPlace } from './sky/guessplace.js';
import { COPY, CITIES, t as fill } from './copy/en.js';
import { LAYERS, loadLayer, addSystemRows, addPlanetAliases } from './data/layers.js';
import * as sources from './data/sources.js';
import { createSkyView } from './sky/skyview.js';
import { showCard, hideCard, wantCards } from './ui/cardgate.js';
import { createShell } from './ui/shell.js';
import { createExplore } from './ui/explore.js';
import { createRail } from './ui/rail.js';
import { toast } from './ui/share.js';
import { createTimePill } from './ui/timepill.js';
import { createSceneNote } from './ui/scenenote.js';
import { buildIndex, findMatches, LINK_MIN_SCORE } from './ui/search.js';
import { satIdOf } from './data/catnum.js';
import { createDensity } from './ui/density.js';
import { createTripGate } from './ui/tripgate.js';
import { createVeil } from './ui/veil.js';
import { createAudio } from './audio/engine.js';
import { createLoader } from './audio/load.js';
import { createBeds } from './audio/beds.js';
import { createStings } from './audio/stings.js';
import { rungOf } from './audio/pick.js';
import { AUDIO } from './data/audio.js';
import { decide, reachFor, LONG_PRESS_MS, TAP_SLOP_PX } from './scene/pickrank.js';
import { createLod } from './scene/lod.js';
import { createStars3d, NAMED_STARS, SUN_RADIUS_KM, SUN_TEFF_K } from './scene/stars3d.js';
import { createGalaxy } from './scene/galaxy.js';
import { createDsoGlow } from './scene/dsoglow.js';
import { createExposure, DEFAULT_EXPOSURE, createDrawn, DEFAULT_DRAWN } from './scene/exposure.js';
import { isLadderStage, isSystemStage } from './scene/stage.js';
import { createSystems, loadIndex as loadSystemIndex } from './scene/systems.js';
import { SUN_INERTIAL, STAGES } from './scene/stage.js';
import { showChooser, hideChooser } from './ui/chooser.js';
import { createLabels, labelName } from './ui/labels.js';
import { createHud } from './ui/hud.js';
import { createOrbitLine } from './scene/orbitline.js';
import { createGroundTrack } from './scene/groundtrack.js';
import { createTrackLabels } from './ui/tracklabels.js';
import { createOrbitRings, periodMsOfWorld, MARKER_PX } from './scene/orbitrings.js';
import { tripOwnsCard } from './ui/tripstate.js';
import { createFrameLatch, createScaleGovernor, shouldSaveData, chooseTier, createTierPromoter, createIdleGate, idleCapWanted, lookHeld, movingReasons, qualityPlan, qualityAfterLatch, readQualityChoice, writeQualityChoice, offeredQualities } from './scene/quality.js';
import { createLiveClouds } from './scene/liveclouds.js';
import { createTextureTiers, gpuMiB, variantFor, LIVE_CLOUDS_MIB } from './scene/texturetiers.js';
import { TEXTURES } from './data/textures.js';
import { setEarthMap, earthMapsSettled, setEarthAirQuality, setEarthCloudHeight } from './scene/earth.js';
import { keyById, bucketOf } from './data/colorkeyrules.js';

const MOMENTS = ['wonder', 'now', 'next'];
// The camera rig's nearest approach in scene units (scene/camera.js's own default), and how far out
// a star is met when its width is known: twelve radii, a disc a fifth of the screen wide.
const RIG_MIN_DISTANCE = 1e-4;
const STAR_ARRIVAL_RADII = 12;
// The camera's co-latitude over a star system's orbits; the same 40 degrees as scene/systems.js OVERVIEW_POLAR.
const SYSTEM_OVERVIEW_POLAR = (40 * Math.PI) / 180;

/** How long after sr:layers-ready the aurora's module is fetched (OFF THE FIRST VISIT, in boot). */
const AURORA_IMPORT_MS = 4000;

/**
 * ctx.aurora until scene/aurora.js has loaded, and for good on a connection that saves data: the
 * same calls the card, the Sources sheet, the layers panel and the frame loop make, answered with
 * what is true before any forecast is held. Its words are the real module's (copy/en.js COPY.aurora).
 */
function auroraStandIn(saveData, layerOn) {
  const A = COPY.aurora;
  const api = {
    failed: false,
    start() {},
    tick() {},
    setTier() {},
    state: () => ({ phase: saveData ? 'off' : api.failed ? 'failed' : 'waiting', reason: saveData ? 'saveData' : null, summary: null, visible: false }),
    line: () => (saveData ? A.saveData : !layerOn() ? A.switchedOff : api.failed ? A.failed : A.waiting),
    credit: () => [A.credit],
    peak: () => (saveData || api.failed ? 0 : undefined),
    rightNow: () => null,
    showMe: () => null,
  };
  return api;
}

/** How long after sr:layers-ready the worlds' lights are fetched (scene/worldlights.js, OFF THE FIRST VISIT, in boot). */
const WORLDLIGHTS_IMPORT_MS = 5000;

/** How long after sr:layers-ready the weather's modules are fetched (OFF THE FIRST VISIT, in boot). */
const WEATHER_IMPORT_MS = 4500;

/**
 * ctx.weather until scene/weather/index.js has loaded, and for good where weather is not drawn: at
 * tier 0 and on a connection that saves data (spec 0066 requirement 8). The same calls the card, the
 * Sources sheet, the layers panel and the frame loop make. A world's line is null -- nothing is
 * drawn for it yet, so there is nothing to say; the Earth's says why there is no lightning.
 */
function weatherStandIn(off, layerOn) {
  const L = COPY.weather.lightning;
  const api = {
    failed: false,
    start() {},
    tick() {},
    latch() {},
    state: () => ({ phase: off ? 'off' : api.failed ? 'failed' : 'waiting' }),
    line: (worldId) => (worldId !== 'earth' ? null : off ? L.off : !layerOn() ? L.switchedOff : api.failed ? L.failed : L.waiting),
    credit: () => [L.credit],
    perMinute: () => (off || api.failed ? 0 : undefined),
  };
  return api;
}

/**
 * OFF THE FIRST VISIT (2026-10-01, internal #188). The named stars (data/stars3d.names.json, 288 kB)
 * and the exoplanet table (data/exoplanets.csv, 582 kB, the fallback CI and a visit without our
 * snapshot read) are drawn only from the ladder's rungs and a star system's stage, never on the
 * Earth the first screen shows. They were 870 kB of the 6.35 MB first visit all the same. They now
 * load LATER_LAYERS_MS after sr:layers-ready, in an idle moment (past the two seconds
 * tests/test_first_visit_bytes.mjs lets the first visit settle), or at once when something needs
 * them sooner: a ladder or system stage, the search box, a trip or an `at` the map cannot resolve
 * without them (ctx.loadAfterFirstVisit).
 */
// DEEP SKY TOO (2026-10-06, internal #405): data/dso.json is 116 kB and its layer is `ladderOnly`,
// drawn from the ladder's rungs like the other two; the ground sky's pictures look their record up
// when it has landed (sky/groundsky.js), and a trip, a link and the search box already wait here.
/**
 * A layer record by id, without the frame loop allocating a closure to find it (internal #529).
 * The index is rebuilt only when the table's length changes (addSystemRows appends rows).
 */
const layerIndex = new Map();
let layerIndexLen = -1;
function layerRec(id) {
  if (layerIndexLen !== LAYERS.length) {
    layerIndex.clear();
    for (const l of LAYERS) if (!layerIndex.has(l.id)) layerIndex.set(l.id, l);
    layerIndexLen = LAYERS.length;
  }
  return layerIndex.get(id);
}
const LATER_LAYERS = new Set(['stars', 'exoplanets', 'deep-sky', 'launch-sites', 'landing-sites', 'meteor-showers']); // 0079 a6: the last three
// 0079 a3: begin
// The moons (data/moons.js: 69 kB and 111 kB, off by default) arrive with them, after the first screen and searchable from then on.
LATER_LAYERS.add('moons');
LATER_LAYERS.add('companions');
// 0079 a3: end
// 0079 r15-a45: the four compact catalogues of release 1.5.0 stages A4 and A5 (data/a45.js): deferred like an on-demand layer, but small, so not `load: 'on-demand'`
// (tests/test_layer_boot.mjs keeps that word for a catalogue-sized layer). ctx.loadMoreCatalogues fetches them.
const MORE_CATALOGUES = new Set(['extremes-more', 'pulsars', 'beyond-more', 'quasars']);
const LATER_LAYERS_MS = 3000;
/** How long after sr:layers-ready the controls hint is imported and may show (ui/keyhint.js): after
 * the later layers and the aurora, when the first view has settled and before a visitor gives up. */
const KEYHINT_MS = 5000;
// The email row under Coming up (ui/subscribe.js) is asked for this long after the layers settle,
// as the trip pictures are: below the sidebar's fold, and not a first visit's cost.
const SUBSCRIBE_MS = 3000;
/** How long after sr:layers-ready the service worker is registered (ui/offline.js): last of all. */
const OFFLINE_MS = 6000;
/** The timeline in the time pill (ui/scrubber.js) and the home's dated cards (ui/today.js): after
 * the two seconds tests/test_first_visit_bytes.mjs waits past sr:layers-ready. */
const SCRUBBER_MS = 4000;
/** The scene summary for a screen reader (ui/scenesummary.js, 0076 W4-c): with the first of the later modules. */
const SUMMARY_MS = 3000;
const TODAY_MS = 4500;
/** The first visit's three lines and two buttons (ui/welcome.js), the launch chip (ui/launchchip.js)
 * and the way home (ui/base.js): with the timeline, the first things after the first visit. */
const WELCOME_MS = 4000;
/** A first visit's arrow at the tour's button (ui/onboarding.js): after the welcome and the keys hint have had their turn. */
const TOUR_MS = 8000;
const LAUNCHDAY_MS = 4800;
/** Two snapshots of the view inside this many ms are one move (a tab sets the stage, then the moment). */
const ONE_MOVE_MS = 120;
/**
 * OFF THE FIRST VISIT (2026-10-06, internal #405): the card (ui/cards.js), the trips (ui/trip.js and
 * data/tours.js) and the model shapes (scene/models.js) are 640 kB that the first screen -- the
 * Earth and its dots -- does not use. Each is fetched when it is first wanted (a selection, a trip
 * opened or linked, a camera close enough for geometry), and otherwise WARM_MS after
 * sr:layers-ready, one after another in an idle moment (requestIdleCallback), so the first tap does not wait for a
 * download and the trip cards can be planned against today's sky. Not on a connection that asked
 * to save data: there each waits to be wanted.
 */
const WARM_MS = 3500;
/** How long after sr:layers-ready a laptop's sharper maps may start to come (scene/texturetiers.js). */
const TIERS_MS = 3000;

/**
 * WHERE THE FIRST VISIT ENDS (2026-10-07, internal #415 item 3). Everything that is not the first
 * screen's waits for sr:layers-ready and then some seconds more: the far catalogues, the aurora, the
 * weather, the timeline, the dated cards, the keys hint, the card and the trips, the 4k maps, the
 * service worker. Each had its own setTimeout, some with an idle callback and some bare, and
 * tests/test_first_visit_bytes.mjs measured "sr:layers-ready and two seconds" on ITS clock: on a busy
 * machine the page's three seconds could end inside the test's two, and the gate read megabytes
 * that no first visit pays. So there is one way to wait, and it does two things:
 *   - the moment the first of these timers fires, it writes `window.__srFirstVisitOver` (the wall
 *     clock, ms). A request that starts after that is not the first visit's, and the byte test and
 *     tools/cdp.mjs --bytes leave it out by that mark, not by their own stopwatch;
 *   - the work itself runs in an idle moment (requestIdleCallback, 4 s at most), so on a machine
 *     still busy with its first frames it does not land beside work that is already late.
 * No delay handed to it is under 3 s: tests/test_boot_diet.mjs reads the constants.
 */
function afterFirstVisit(ms, run) {
  return setTimeout(() => {
    if (typeof window !== 'undefined' && !window.__srFirstVisitOver) window.__srFirstVisitOver = Date.now();
    if (typeof requestIdleCallback === 'function') requestIdleCallback(() => run(), { timeout: 4000 });
    else run();
  }, ms);
}

// 0076 W4-c (internal #912, #836): the Motion row in What to show. Off is read here and from scene/camera.js and
// ui/trip.js as `data-sr-motion="off"` on the page, so a flight is an instant set and a trip reads at the reader's
// pace, as under prefers-reduced-motion. The row writes the same attribute and `sr:motion` (ui/whattoshow.js).
const motionStill = () => typeof document !== 'undefined' && document.documentElement.dataset.srMotion === 'off';

export async function boot({ setStatus } = {}) {
  const say = setStatus || (() => {});
  // `/?from=ig` is the home page: the tag is taken off the address bar before anything reads it (ui/urlstate.js).
  dropFrom();
  // 0076 W4-c: the choice the Motion row remembered, before anything flies.
  try { if (window.localStorage.getItem('sr:motion') === 'off') document.documentElement.dataset.srMotion = 'off'; } catch { /* no memory: motion as the device says */ }
  // RENDER MODE (spec 0070, ui/rendermode.js): `?render=1#trip=<id>` is tools/render-trip.mjs
  // filming a trip one frame at a time. It takes over what the page calls time, so it is installed
  // before anything below asks what time it is; the module is imported only for that address
  // (OFF THE FIRST VISIT, tests/test_first_visit_bytes.mjs).
  const film = /[?&]render=1(?:&|$)/.test(location.search)
    ? await import('./ui/rendermode.js').then((m) => m.install(m.renderOptions(location.search)))
    : null;
  // THE EMBED (public #439, ui/embed.js): `?embed=1&at=<id>` is the map inside someone else's page.
  // The scene, the object's tag and one link; its module is imported only for that address, and it
  // hides the panels before any is built. Everything marked `!embed` below is what a frame under
  // another site's headline does not fetch.
  const embed = /[?&]embed=1(?:&|$)/.test(location.search)
    ? await import('./ui/embed.js').then((m) => m.installEmbed(location.search))
    : null;
  const canvas = document.getElementById('stage');

  say('Building the sky…');
  const rendererApi = createRenderer(canvas);
  const { renderer, scene, camera, resize, render } = rendererApi;
  // The arrow keys fly the camera (scene/camera.js). Not during a trip: ui/tripframe.js gives the
  // arrows to the stops there -- left and right are previous and next -- and one key doing two
  // things at once is how a control stops being trusted.
  const cameraRig = createCameraRig(camera, canvas, {
    keysEnabled: () => !(ctx && ctx.trip && ctx.trip.state && ctx.trip.state.phase !== 'idle'),
  });
  // A phone's card is a sheet over the lower half of the canvas; this keeps the centre of the view
  // in the part left uncovered (scene/viewshift.js says why and how).
  const viewShift = createViewShift(camera, canvas);

  // The camera is how worlds.js decides a planet is near enough to be worth its map; without it the
  // eight planets, the Moon and the Sun would stay in their mean colours for good.
  const worlds = createWorlds(scene, { camera });
  const starfield = createStarfield(scene, {
    starsBin: 'data/stars.bin',
    linesJson: 'data/constellations.lines.json',
    namesJson: 'data/constellation-names.json',
    milkyWayTexture: 'textures/2k_stars_milky_way.webp',
  });

  // Records, keyed by layer id. Layers fill in as their data lands; the loop reads whatever is
  // there. A layer that never loads is simply absent -- it never blocks a frame.
  const layerRecords = new Map();
  const glyphLayers = new Map();
  let selected = null;
  let observer = null;
  let moment = readMomentFromHash();
  // The link, read NOW, before any of the app's own writers below can touch the hash; its clock
  // keys are applied here and the rest waits for the layers (ui/urlstate.js bootLink says why).
  const link = bootLink(clock, embed && embed.link);
  // `go=` is read here, raw: ui/urlstate.js hands it on as `at`, and the landing needs to know which it was.
  // 0077 W3-e: begin (public #564: read() marks an `at` that came from `go=` alone, so a link with both keeps the `at`)
  const goId = link && link.goOnly ? link.at : '';
  // 0077 W3-e: end
  // A SCREEN THAT PLAYS ON ITS OWN (spec 0036): `#ambient=…`. The class goes on before anything is
  // built, so a kiosk never shows the panels it is about to hide; ui/autopilot.js does the rest.
  const ambient = !embed && !!(link && link.ambient && link.ambient !== '0');
  if (ambient) document.documentElement.classList.add('sr-ambient');

  const ctx = {
    clock, stage, scene, camera, cameraRig, viewShift, worlds, renderer, rendererApi, sources,
    layers: LAYERS,
    records: () => [...layerRecords.values()].flat(),
    recordsFor: (id) => layerRecords.get(id) || [],
    // The dot layers, for one reader: sky/skyview.js puts them in the same air as the stars.
    glyphLayers,
    recordById: (id) => ctx.records().find((r) => r.id === id) || (ctx.deep && ctx.deep.volumeRecord ? ctx.deep.volumeRecord(id) : null) || (ctx.cometField && ctx.cometField.recordById ? ctx.cometField.recordById(id) : null), // 0076 W4-a: and the local volume's galaxies; 0079 a2 and a8: and the catalogue's (volumeRecord asks it), and any comet of the feed
    selected: () => selected,
    select,
    deselect,
    flyToRecord: (record, ms, flight) => { leaveGround(); return needsStageHop(record) ? hopTo(record, ms, flight) : flyToRecord(record, ms, flight); },
    get observer() { return observer; },
    // The one door a place comes in by. The browser's own answer is rounded here as well as where it
    // is read (ui/place.js), so no caller can hand the app a precise position (spec 0051 req 3).
    setObserver: (o) => { observer = o && o.source === 'geolocation' ? roundPlace(o) : o; window.dispatchEvent(new CustomEvent('sr:observer', { detail: observer })); },
    get moment() { return moment; },
    setMoment,
    isSecure: window.isSecureContext === true,
  };

  // The scale ladder's level of detail (spec 0028 req 10): a table in registry/lod.yaml, hooks here.

  // A PLACE FROM BEFORE (internal #137): one carried in the link somebody shared on purpose (`p`),
  // else the one this visitor asked this browser to remember. Both are rounded to 0.1 degree where
  // they are written (sky/placelink.js). Neither is a request: nothing is asked of anybody.
  try {
    const before = parsePlaceValue(readUrlKeys().p) || keptPlace(browserStorage());
    if (before) observer = before;
  } catch { /* no place from before */ }
  const stars3d = createStars3d(scene);
  ctx.stars3d = stars3d;
  // The naked-eye sky, on ctx for one reader: ui/trip.js stretches both star draws on a ladder
  // flight (spec 0034), and the two must never disagree during the lod crossfade between them.
  ctx.starfield = starfield;
  const galaxy = createGalaxy(scene);
  ctx.galaxy = galaxy;
  // Deep-sky objects as big as they are, when that is bigger than their dot (scene/dsoglow.js).
  const dsoGlow = createDsoGlow(scene);
  ctx.dsoGlow = dsoGlow;
  window.addEventListener('sr:layer', (e) => {
    if (!e.detail || e.detail.id !== 'deep-sky') return;
    dsoGlow.setRecords(ctx.recordsFor('deep-sky'));
    if (ctx.nebulae) ctx.nebulae.setRecords(ctx.recordsFor('deep-sky'));
  });
  // THE SHUTTER (spec 0067, scene/exposure.js): Eye, Camera or Deep, remembered. The Milky Way wears
  // it from the first frame, and so do the deep-sky glows and heroes. 0078 r14-neb-b: no photograph is drawn in
  // the scene (Ivan, 2026-10-26); scene/nebulae.js is fetched only by the card's "Compare with the photograph".
  // A shared link carries the shutter it was made at (`exp`, public #460): the picture in the post
  // and the sky the link opens are the same exposure. For this page only: a friend's link does not
  // overwrite the visitor's own remembered choice.
  const exposure = createExposure({ initial: link && !link.unknownVersion ? link.exp : undefined });
  ctx.exposure = exposure;
  starfield.setExposure(exposure.look().milkyWay);
  // The Milky Way model and the deep-sky glows are the same faint light: one number for all three.
  galaxy.setExposure(exposure.look().milkyWay);
  dsoGlow.setExposure(exposure.look().milkyWay);
  stars3d.setMagLimit(exposure.look().starLimit);
  let skyStrength = 1;
  let nebulaeImport = null;
  ctx.wantNebulae = () => {
    if (nebulaeImport) return nebulaeImport;
    nebulaeImport = import('./scene/nebulae.js').then((m) => {
      const nebulae = m.createNebulae(scene, { look: exposure.look() }); // 0078 r14-neb-b: the slider's photograph only
      nebulae.setRecords(ctx.recordsFor('deep-sky'));
      ctx.nebulae = nebulae;
      return nebulae;
    }).catch((e) => { console.warn('the nebula pictures did not load', e); nebulaeImport = null; return null; });
    return nebulaeImport;
  };
  // DEEP SPACE (internal spec 0075, scene/deep/deepsky.js): the coordinator of the deep sky's levels,
  // holders and heroes. Fetched the first time a rung of the ladder is entered (sr:stage below), never
  // at boot; ticked once a frame beside the nebulae. `?deepdebug=1` is for tools, not a visitor's key.
  let deepImport = null;
  ctx.wantDeep = () => deepImport || (deepImport = import('./scene/deep/deepsky.js').then((m) => {
    const reduced = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    ctx.deep = m.createDeepSky(THREE, {
      scene, records: () => ctx.recordsFor('deep-sky') || [], recordById: (id) => ctx.recordById(id),
      pictureShare: (id) => (ctx.nebulae ? ctx.nebulae.drawn(id) : 0), compareShare: (id) => (ctx.nebulae && ctx.nebulae.compareShare ? ctx.nebulae.compareShare(id) : null) /* 0077 W2-d */, picturesBusy: () => !!(ctx.nebulae && ctx.nebulae.busy && ctx.nebulae.busy()),
      selectedId: () => (ctx.selected() ? ctx.selected().id : null), subjectId: () => (ctx.labels ? ctx.labels.subject() : null),
      quality: () => { const d = ctx.quality ? ctx.quality.describe() : {}; return { tier: Number.isFinite(d.tier) ? d.tier : 1, top: !!(d.plan && d.plan.steps === 'top'), latched: !!(ctx.latch && ctx.latch.latched), scaleStep: ctx.scaler ? ctx.scaler.step : 0 }; },
      look: () => ctx.look(), reduced: () => !!(reduced && reduced.matches) || motionStill(), frozen: () => !!(ctx.latch && ctx.latch.held),
      debug: /[?&]deepdebug=1\b/.test(location.search), glowVisible: (on) => { ctx.deepGlowOff = !on; },
    });
    ctx.deep.setExposure(exposure.look().milkyWay);
    return ctx.deep;
  }).catch((e) => { console.warn('the deep sky did not load', e); deepImport = null; return null; }));
  exposure.onChange((mode, look, byVisitor) => {
    starfield.setExposure(look.milkyWay);
    if (ctx.nebulae) ctx.nebulae.setExposure(look); // 0078 r14-neb-b: the shutter no longer fetches photographs
    // The Milky Way model and the deep-sky glows are the same faint light (internal #343).
    galaxy.setExposure(look.milkyWay);
    dsoGlow.setExposure(look.milkyWay);
    if (ctx.deep) ctx.deep.setExposure(look.milkyWay);
    stars3d.setMagLimit(look.starLimit);
    if (ctx.unplaced) ctx.unplaced.setMagLimit(look.starLimit); // 0079 r15-a45
    // The address bar says what is on screen: the key goes when the shutter is back at its default.
    writeUrlState({ exp: mode === DEFAULT_EXPOSURE ? null : mode });
    window.dispatchEvent(new CustomEvent('sr:exposure', { detail: { mode } }));
  });
  // MEASURED OR FILM (spec 0073 requirement 8, scene/exposure.js): the second word on the same control.
  // ONE question on the context, `ctx.look()`, which the loop hands to the three places that draw
  // generated detail (the weather, the Sun close up, the aurora's folds); each answers from its own
  // declared rule. A shared link carries it as `drawn` when it is not Film, exactly as `exp`.
  const drawn = createDrawn({ initial: link && !link.unknownVersion ? link.drawn : undefined });
  ctx.drawn = drawn;
  ctx.look = () => drawn.mode();
  // Saturn's ring grains are generated (scene/worlds.js RING_GRAIN): not under the Measured look.
  worlds.setLook(drawn.mode());
  drawn.onChange((mode) => {
    worlds.setLook(mode);
    if (ctx.weather && ctx.weather.setLook) ctx.weather.setLook(mode);
    writeUrlState({ drawn: mode === DEFAULT_DRAWN ? null : mode });
    window.dispatchEvent(new CustomEvent('sr:look', { detail: { mode } }));
    // The cards' lines say which look is on: the weather tells its own (setLook above), and the Sun's listens to sr:tier.
    if (ctx.quality) window.dispatchEvent(new CustomEvent('sr:tier', { detail: ctx.quality.describe() }));
  });
  // The canvas says what is selected (public #315): its name is the page's own sentence until then.
  const stageEl = document.getElementById('stage');
  const sceneName = stageEl ? stageEl.getAttribute('aria-label') : '';
  window.addEventListener('sr:select', (e) => {
    const record = e && e.detail;
    if (stageEl) stageEl.setAttribute('aria-label', record && record.name ? fill(COPY.app.sceneSelected, { name: record.name }) : sceneName);
    // 0078 r14-neb-b: selecting fetches no photograph; another selection only puts the slider back to the model.
    if (ctx.nebulae) ctx.nebulae.want(record && record.klass === 'dso' ? record.id : null);
  });
  // OTHER LIGHT (public #456, scene/otherlight.js): the sky in infrared, microwaves or gamma rays,
  // faded over the visible one. This is the whole of it at boot: two values and who listens. The
  // layer, its registry and its pictures are imported when a visitor first picks a band in What to
  // show (ui/otherlight.js); sky/groundsky.js reads the same two values for the sky from the ground.
  const otherLight = {
    band: null,
    mix: 1,
    layer: null,
    listeners: new Set(),
    set(band, mix) {
      const nextMix = Number.isFinite(mix) ? Math.min(1, Math.max(0, mix)) : this.mix;
      if (band === this.band && nextMix === this.mix) return;
      this.band = band || null;
      this.mix = nextMix;
      if (this.band && !this.layer && !this.asked) {
        this.asked = import('./scene/otherlight.js').then((m) => {
          this.layer = m.createOtherLight({ parent: starfield.group, saveData: typeof navigator !== 'undefined' && shouldSaveData(navigator.connection) });
        }).catch((e) => { console.warn('the other-light layer did not load', e); this.asked = null; });
      }
      for (const f of this.listeners) f(this.band, this.mix);
    },
    onChange(f) { this.listeners.add(f); return () => this.listeners.delete(f); },
  };
  ctx.otherLight = otherLight;
  const _olDir = new THREE.Vector3();
  const _olQ = new THREE.Quaternion();
  function updateOtherLight() {
    const layer = otherLight.layer;
    if (!layer) return;
    layer.set(otherLight.band);
    layer.setMix(otherLight.mix);
    // The star sphere's own axes are equatorial J2000: the camera's direction, turned into them.
    ctx.camera.getWorldDirection(_olDir).applyQuaternion(starfield.group.getWorldQuaternion(_olQ).invert());
    const el = renderer.domElement;
    layer.update({
      dirEq: [_olDir.x, _olDir.y, _olDir.z],
      fovDeg: ctx.camera.fov,
      heightPx: el.clientHeight || 800,
      aspect: ctx.camera.aspect,
      // It fades with the star sphere it lies on (registry/lod.yaml `sky-panorama`), and under the
      // sky view's veil the ground sky draws its own.
      strength: ctx.skyView && ctx.skyView.ownsSky ? 0 : skyStrength,
    });
  }
  ctx.updateOtherLight = updateOtherLight;

  // 0079 r15-a45: begin -- the 10 224 HYG stars with a direction and no distance (scene/unplaced.js), on a world's sky sphere only.
  // Fetched (123 kB) the first time the 3D stars fade in, never at boot.
  ctx.unplaced = null;
  let unplacedImport = null;
  let unplacedK = 0;
  let unplacedOn = true;
  const wantUnplaced = () => unplacedImport || (unplacedImport = import('./scene/unplaced.js')
    .then((m) => {
      ctx.unplaced = m.createUnplaced(scene);
      ctx.unplaced.setMagLimit(exposure.look().starLimit);
      ctx.unplaced.setVisible(unplacedOn);
      ctx.unplaced.setOpacity(unplacedK);
      return ctx.unplaced;
    })
    .catch((e) => { console.warn('the stars with no distance did not load', e); return null; }));
  ctx.wantUnplaced = wantUnplaced;
  const unplacedOpacity = (k) => { unplacedK = k; if (ctx.unplaced) ctx.unplaced.setOpacity(k); else if (k > 0) wantUnplaced(); };
  // 0079 r15-a45: end
  const lod = createLod({
    'sky-panorama': (k) => {
      skyStrength = k;
      if (starfield.setSkyOpacity) starfield.setSkyOpacity(k);
    },
    'stars-3d': (k) => { stars3d.setOpacity(k); unplacedOpacity(k); }, // 0079 r15-a45: the stars with no distance fade in with the others
    'galaxy-model': (k) => galaxy.setOpacity(k),
  });
  // A star and its planets at the system's own scale (spec 0040, scene/systems.js): built when its
  // stage is entered, dropped when it is left, nothing at boot.
  const systems = createSystems(scene, ctx);
  ctx.systems = systems;
  // 0076 W2-b (exposure to the worlds)
  worlds.setExposure(exposure.mode());
  exposure.onChange((mode) => worlds.setExposure(mode));
  window.addEventListener('sr:stage', (e) => {
    stars3d.rebuild(); galaxy.rebuild(); dsoGlow.rebuild();
    if (ctx.unplaced) ctx.unplaced.rebuild(); // 0079 r15-a45
    const id = e && e.detail ? e.detail.worldId : stage.worldId;
    if (ctx.nebulae) ctx.nebulae.rebuild();
    if (ctx.deep) ctx.deep.rebuild();
    else if (isLadderStage(id)) ctx.wantDeep();
    if (isSystemStage(id)) systems.enter(id);
    else systems.leave();
    // 0076 W2-a
    // 0076 W2-c
    // Plumes where they were seen (scene/plumes.js): fetched when Io, Enceladus or Triton gets its stage.
    if (!ctx.wantPlumes) ctx.wantPlumes = () => ctx.wantPlumes.p || (ctx.wantPlumes.p = import('./scene/plumes.js').then((m) => (ctx.plumes = m.createPlumes(ctx))).catch((e) => { console.warn('the plumes did not load', e); ctx.wantPlumes.p = null; return null; }));
    if (ctx.plumes) ctx.plumes.stage(id);
    else if (id === 'io' || id === 'enceladus' || id === 'triton') ctx.wantPlumes().then((p) => p && p.stage(stage.worldId));
    // The Milky Way is fetched, built and its shaders compiled while the stellar rung is up (internal
    // #549: entering the galaxy rung stalled about three seconds), not on the galaxy rung's first frame.
    // Not on a metered connection, where the 2.2 MB file waits for the rung that draws it.
    if (id === 'stellar' && !galaxyPrewarm) {
      const saver = typeof navigator !== 'undefined' && navigator.connection && navigator.connection.saveData;
      if (!saver) galaxyPrewarm = setTimeout(() => { galaxy.prewarm(renderer, ctx.camera); }, 2500);
    }
  });
  let galaxyPrewarm = 0;
  ctx.lod = lod;
  ctx.skyView = createSkyView(ctx);
  const heroes = createHeroes(scene, ctx);
  ctx.heroes = heroes; // the status panel reads count(); a browser check reads poolCap()
  // Constructed BEFORE the layers load, because the trip counts which layers have landed by
  // listening for `sr:layer` -- and a layer that landed before anybody was listening is a layer
  // the trip would then wait eight seconds for.
  // THE ONE BLACK (spec 0034 req 1): over the canvas and the labels, under every panel, the card
  // and the trip's own bars. A stage change in a trip goes through it; the reduced-motion
  // cross-fade is it. Mounted on <body> beside the canvas, because the trip frame is a stacking
  // context of its own and anything inside it sits over the card.
  ctx.veil = createVeil(document.body, {
    reducedMotion: () => !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) || motionStill(),
  });
  // ui/tripgate.js: the trip's own object once a trip is wanted, an idle stand-in until then.
  ctx.trip = createTripGate(ctx);
  // ONE FLIGHT UP THE LADDER (internal #410, public #451; scene/climb.js). Scrolling out from the
  // Earth hands the camera to the Sun's stage, the stellar rung, the galaxy and the Local Group with
  // its place and direction kept, and scrolling back in hands it back. Never at boot: the module is
  // fetched the first time a visitor's own dolly has taken the camera away from the Earth's
  // neighbourhood (or off the Earth's stage), or when the ladder's control or a trip stop asks.
  let climbLoad = null;
  ctx.wantClimb = () => {
    if (!climbLoad) {
      climbLoad = import('./scene/climb.js').then((m) => {
        ctx.climb = m.createClimb(ctx);
        return ctx.climb;
      }).catch((e) => { console.warn('the continuous flight did not load', e); climbLoad = null; return null; });
    }
    return climbLoad;
  };
  const CLIMB_WANTED_UNITS = 200; // 200 000 km from the Earth: half way to the Moon
  cameraRig.onUserInput((kind) => {
    const dolly = kind === 'dolly' || kind === 'pinch' || kind === 'keys';
    if (ctx.climb) {
      if (dolly) ctx.climb.noteDolly();
      else ctx.climb.cancel('cancelled');
      return;
    }
    if (dolly && (stage.worldId !== 'earth' || camera.position.length() > CLIMB_WANTED_UNITS)) {
      ctx.wantClimb().then((c) => { if (c) c.noteDolly(); });
    }
  });
  // WHAT A TRIP STOP ADDS TO THE SCENE (2026-10-05): constellation figures that draw themselves
  // (scene/figures3d.js), one measured map over the Earth (scene/earthoverlay.js) and the stop's
  // own shutter. OFF THE FIRST VISIT: both modules are dynamic imports, made when a trip that
  // needs one reaches its intro, or when a visitor picks an overlay in What to show; the overlay
  // asks NASA GIBS for its one picture only then. The trip says what it wants in its state
  // (ui/trip.js `sky`, `overlay`, `exposure`) and this is the one place that acts on it.
  ctx.figures = null;
  let figuresImport = null;
  ctx.wantFigures = () => figuresImport || (figuresImport = import('./scene/figures3d.js')
    .then((m) => { ctx.figures = m.createFigures3d(ctx); return ctx.figures; })
    .catch((e) => { console.warn('the constellation figures did not load', e); figuresImport = null; return null; }));
  ctx.earthOverlay = null;
  let overlayImport = null;
  let overlayAsked = null; // the id on the globe now, or wanted on it
  let overlayOwn = null; // the visitor's own choice, which a trip suspends and leaving puts back
  // The key in the sidebar's Earth tab (ui/overlaykey.js, internal #386): fetched the first time a
  // map is asked for, so a visit that never lays one on never pays for it.
  let overlayKey = null;
  const wantOverlayKey = () => overlayKey || (overlayKey = import('./ui/overlaykey.js')
    .then((m) => m.mountOverlayKey(ctx))
    .catch((e) => { console.warn('the overlay key did not load', e); overlayKey = null; return null; }));
  const tellOverlay = () => { if (overlayAsked) wantOverlayKey(); window.dispatchEvent(new CustomEvent('sr:overlay')); };
  const wantOverlay = () => overlayImport || (overlayImport = import('./scene/earthoverlay.js')
    .then((m) => {
      ctx.earthOverlay = m.createEarthOverlay({
        earth: () => worlds.meshFor('earth'),
        saveData: typeof navigator !== 'undefined' && shouldSaveData(navigator.connection),
        onChange: tellOverlay,
      });
      return ctx.earthOverlay;
    })
    .catch((e) => { console.warn('the Earth overlays did not load', e); overlayImport = null; return null; }));
  // THE WIND (internal #362) is one of the overlays to the visitor and a module of its own here:
  // scene/wind.js, fetched when "Wind" is chosen, which then asks a NOAA-funded server for one
  // forecast hour (data/wind.js). One overlay at a time: choosing it takes a GIBS map away.
  /** Where the camera stands round its target, for a link's `cam` (ui/share.js shareState). */
  ctx.camPose = () => {
    const s = cameraRig.saveState();
    const deg = 180 / Math.PI;
    return { azimuthDeg: s.azimuth * deg, polarDeg: s.polar * deg, distanceKm: s.distance * stage.unitKm };
  };
  ctx.wind = null;
  let windImport = null;
  const WIND_OVERLAY = 'wind';
  const wantWind = () => windImport || (windImport = import('./scene/wind.js')
    .then((m) => {
      ctx.wind = m.createWind({
        earth: () => worlds.meshFor('earth'),
        saveData: typeof navigator !== 'undefined' && shouldSaveData(navigator.connection),
        tier: ctx.quality ? ctx.quality.tier : 1, // 600, 1200 or 2400 streaks (scene/wind.js COUNT_BY_TIER)
        reducedMotion: !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) || motionStill(),
        onChange: tellOverlay,
      });
      return ctx.wind;
    })
    .catch((e) => { console.warn('the wind did not load', e); windImport = null; return null; }));
  // OCEAN CURRENTS are the wind's sibling: scene/currents.js, fetched when "Ocean currents" is chosen,
  // which then reads our two saved copies (data/currents.js: the current, and the sea's temperature
  // that colours it). The same rule: one overlay at a time.
  ctx.currents = null;
  let currentsImport = null;
  const CURRENTS_OVERLAY = 'currents';
  const wantCurrents = () => currentsImport || (currentsImport = import('./scene/currents.js')
    .then((m) => {
      ctx.currents = m.createCurrents({
        earth: () => worlds.meshFor('earth'),
        saveData: typeof navigator !== 'undefined' && shouldSaveData(navigator.connection),
        tier: ctx.quality ? ctx.quality.tier : 1, // 1200, 2500 or 5000 streaks (scene/currents.js COUNT_BY_TIER)
        reducedMotion: !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) || motionStill(),
        onChange: tellOverlay,
      });
      return ctx.currents;
    })
    .catch((e) => { console.warn('the ocean currents did not load', e); currentsImport = null; return null; }));
  /** The overlays that are streaks of their own module, not a GIBS map. */
  const isStreaks = (id) => id === WIND_OVERLAY || id === CURRENTS_OVERLAY;
  const applyOverlay = (id) => {
    if (id === overlayAsked) return;
    overlayAsked = id;
    const windOn = id === WIND_OVERLAY;
    if (windOn) wantWind().then((w) => { if (w) w.set(overlayAsked === WIND_OVERLAY); else tellOverlay(); });
    else if (ctx.wind) ctx.wind.set(false);
    if (id === CURRENTS_OVERLAY) wantCurrents().then((c) => { if (c) c.set(overlayAsked === CURRENTS_OVERLAY); else tellOverlay(); });
    else if (ctx.currents) ctx.currents.set(false);
    const map = isStreaks(id) ? null : id;
    if (!map && !ctx.earthOverlay) { tellOverlay(); return; }
    wantOverlay().then((o) => { if (o) o.set(isStreaks(overlayAsked) ? null : overlayAsked); else tellOverlay(); });
    tellOverlay();
  };
  /** What is on the globe: the scene module's state(), or its stand-in while the module loads. */
  ctx.overlayState = () => (overlayAsked === WIND_OVERLAY
    ? (ctx.wind ? { ...ctx.wind.state(), id: WIND_OVERLAY } : { id: WIND_OVERLAY, kind: 'wind', status: 'loading' })
    : overlayAsked === CURRENTS_OVERLAY
      ? (ctx.currents ? { ...ctx.currents.state(), id: CURRENTS_OVERLAY } : { id: CURRENTS_OVERLAY, kind: 'currents', status: 'loading' })
      : ctx.earthOverlay ? ctx.earthOverlay.state()
        : { id: overlayAsked, status: overlayAsked ? 'loading' : 'off' });
  ctx.setOverlay = (id) => {
    overlayOwn = id || null;
    const st = ctx.trip.state;
    if (!st || st.phase === 'idle') applyOverlay(overlayOwn);
  };
  // NOAA's reading of the Earth's magnetic weather, for a stop that prints it (`live_note:
  // space-weather`, "The Sun today"). The Sources sheet fetches the same row when it is opened;
  // a trip that wants it asks at its intro, through the same module, and whichever came first the
  // sentence is the one ui/spaceweather.js writes, with the reading's age and where it came from.
  let spaceWeatherImport = null;
  const wantSpaceWeather = () => spaceWeatherImport || (spaceWeatherImport = import('./ui/spaceweather.js')
    .then((m) => m.readSpaceWeather().then((got) => {
      if (got && !ctx.spaceWeather) ctx.spaceWeather = got;
      ctx.spaceWeatherLine = () => (ctx.spaceWeather
        ? m.spaceWeatherLine(ctx.spaceWeather.parsed, ctx.spaceWeather.result, Date.now()) || '' : '');
    }))
    .catch((e) => { console.warn('the space weather did not load', e); spaceWeatherImport = null; }));
  // A BLACK HOLE AT ITS PLACE, IN 3D (scene/portraits.js, scene/blackhole.js; release 1.4.0), for a trip stop
  // that says `portrait: true`, and the sentence under that stop: that it is a model, drawn far larger than it
  // would look, and whose the photograph on its card is (the row's own credit, registry/exotics.yaml).
  ctx.portraits = null;
  let portraitsImport = null;
  let portraitAsked = '';
  const wantPortraits = () => portraitsImport || (portraitsImport = import('./scene/portraits.js')
    .then((m) => { ctx.portraits = m.createPortraits(scene, { tier: () => (ctx.latch && ctx.latch.latched ? 0 : ctx.quality ? ctx.quality.tier : 1), recordById: (id) => ctx.recordById(id) }); return ctx.portraits; }) // 0078 r14-mw-bh
    .catch((e) => { console.warn('the black hole pictures did not load', e); portraitsImport = null; return null; }));
  // OUTSIDE A TRIP TOO (public #426, 2026-10-08): the picture is drawn while the black hole's own
  // card is open on a rung of the ladder, and the card says it is drawn far larger than life
  // (data/layers.js exoticDeparture). In a trip the trip decides: a stop without `portrait: true`
  // shows none, whatever is selected.
  let tripPortrait = '';
  let tripUp = false;
  let cardPortrait = '';
  function syncPortrait() {
    const want = tripUp ? tripPortrait : cardPortrait;
    if (want === portraitAsked) return;
    portraitAsked = want;
    if (want) wantPortraits().then((p) => { if (p && portraitAsked === want) p.show(ctx.recordById(want)); });
    else if (ctx.portraits) ctx.portraits.clear();
  }
  window.addEventListener('sr:select', (e) => {
    const record = e && e.detail;
    cardPortrait = record && record.klass === 'exotic' && record.meta && record.meta.kind === 'blackhole' ? record.id : ''; // 0078 r14-mw-bh: every black hole, in 3D
    syncPortrait();
  });
  // A PULSAR'S PULSE (scene/pulsars.js): asked for the first time the exotics are drawn on a rung
  // of the ladder, from the frame loop.
  ctx.pulsars = null;
  let pulsarsImport = null;
  ctx.wantPulsars = () => pulsarsImport || (pulsarsImport = import('./scene/pulsars.js')
    .then((m) => { ctx.pulsars = m.createPulsars(scene); ctx.pulsars.setRecords(ctx.recordsFor('exotics')); return ctx.pulsars; })
    .catch((e) => { console.warn('the pulsars\u2019 pulses did not load', e); return null; }));
  // 0079 a8: begin
  // EVERY COMET'S ORBIT, PLACE AND TAIL (scene/cometfield.js): asked for the first time the comets layer is drawn on a heliocentric
  // stage, from the frame loop; never at boot. The layer's own 60 keep their glyphs.
  ctx.cometField = null;
  let cometFieldImport = null;
  ctx.wantCometField = () => cometFieldImport || (cometFieldImport = import('./scene/cometfield.js')
    .then((m) => {
      ctx.cometField = m.createCometField(THREE, { scene, recordsFor: (id) => ctx.recordsFor(id), tier: () => (ctx.latch && ctx.latch.latched ? 0 : ctx.quality && Number.isFinite(ctx.quality.describe().tier) ? ctx.quality.describe().tier : 1) });
      return ctx.cometField;
    })
    .catch((e) => { console.warn('the comets\u2019 orbits did not load', e); return null; }));
  // 0079 a8: end
  ctx.portraitLine = (id) => {
    const record = ctx.recordById(id);
    const image = record && record.meta && record.meta.image;
    return image ? fill(COPY.trip.portraitLine, { credit: String(image.credit), licence: String(image.licence) }) : '';
  };
  // 0078 r14-neb-b: a trip stop at a nebula or a galaxy shows its 3D model or its glow, never a photograph, so it
  // has no photograph's credit line under it (ctx.pictureLine is gone; ui/trip.js asks for it only if it is there).
  let skyAsked = '';
  let picturesAsked = null;
  ctx.trip.onChange((st) => {
    const tripping = st && st.phase !== 'idle';
    if (tripping && st.wants && st.wants.portrait) wantPortraits();
    if (tripping && st.wants && st.wants.pictures) {
      // The deep-sky stops' heroes, asked for at the intro so each is there when its stop lands (no photographs: 0078 r14-neb-b).
      const ids = st.wants.pictures;
      if (ids !== picturesAsked) {
        picturesAsked = ids;
        // 0076 W4-a (the hero prefetch): and the deep-sky heroes among them, built with their shaders compiled, before the stop lands
        ctx.wantDeep().then((d) => { if (d && typeof d.prefetch === 'function') d.prefetch(ids, { renderer: ctx.renderer, camera: ctx.camera }); });
      }
    }
    tripPortrait = tripping && st.portrait ? st.portrait.id : '';
    tripUp = !!tripping;
    syncPortrait();
    if (tripping && st.wants && st.wants.figures) ctx.wantFigures();
    if (tripping && st.wants && st.wants.overlay) wantOverlay();
    if (tripping && st.wants && st.wants.spaceWeather) wantSpaceWeather();
    const sky = tripping && st.sky ? st.sky : null;
    const key = sky ? JSON.stringify(sky) : '';
    if (key !== skyAsked) {
      skyAsked = key;
      if (sky) ctx.wantFigures().then((f) => { if (f && skyAsked === key) f.show(sky); });
      else if (ctx.figures) ctx.figures.clear();
    }
    if (tripping && st.exposure) ctx.exposure.hold(st.exposure);
    else if (ctx.exposure) ctx.exposure.release();
    applyOverlay(tripping ? st.overlay || null : overlayOwn);
  });
  // Sound (spec 0035): built now so the panels below can put its button in, and silent until a
  // visitor presses one. Creating it makes no AudioContext and fetches nothing (wireSound).
  ctx.audio = wireSound(ctx);
  // Regular or Compact (spec 0045 req 10), set on <html> before any panel is built.
  ctx.density = createDensity();

  say('Placing Earth…');
  // One frame before any data: the world, the stars, the light.
  worlds.update(clock.now());
  // The camera rig knows nothing about worlds; it is told the radius so it can clamp, and where
  // to look. Earth's radius in scene units is 6371 / unitKm.
  cameraRig.setWorldRadius(6371 / stage.unitKm);
  cameraRig.setWorldCentre({ x: 0, y: 0, z: 0 });
  cameraRig.setTarget({ x: 0, y: 0, z: 0 });
  // 3.5 radii (22 units), or further on a screen too narrow to show the whole globe at that.
  const homeDistance = worldFramingDistance(6371 / stage.unitKm, camera.fov, camera.aspect);
  // THE OPENING (public #287, scene/framing.js openingPlan): a first visit starts farther out and
  // eases in once the boot screen lifts (startOpening, called beside fadeBoot).
  let opening = null;
  try {
    const store = window.localStorage;
    opening = openingPlan({
      seen: !!store.getItem(OPENING_KEY),
      link: !!(link && Object.keys(link).length) || location.hash.length > 1,
      reducedMotion: !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) || motionStill(),
      embed: !!embed,
      hidden: document.hidden,
      automated: navigator.webdriver === true,
    });
    if (opening) store.setItem(OPENING_KEY, '1');
  } catch { opening = null; /* no storage: no way to play it once, so it is not played */ }
  cameraRig.flyTo({ targetScene: { x: 0, y: 0, z: 0 }, distance: homeDistance, ms: 0 });
  const homeAzimuth = cameraRig.saveState().azimuth;
  // THE SHOT AND EVERY WAY OUT OF IT. This is all of the opening that boots: the camera's flight,
  // a class on <html> that puts the panels away while it lasts (ui.css), and the listeners that
  // end it. The words and buttons over it (ui/opening.js) are fetched only when it plays.
  ctx.opening = { live: false, play: (plan) => runOpening(plan || openingPlan({})) };
  function runOpening(plan) {
    if (!plan || ctx.opening.live) return false;
    const root = document.documentElement;
    const far = Math.max(homeDistance * plan.from, plan.fromKm / stage.unitKm);
    const home = { targetScene: { x: 0, y: 0, z: 0 }, distance: homeDistance, azimuth: homeAzimuth };
    const INPUTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    let shot = true; // the camera is still on its way in
    let over = false;
    let overlay = null;
    let guard = 0;
    ctx.opening.live = true;
    root.classList.add('sr-opening-on');
    // Skipped: the home view at once, a cut. Then the visitor's own press or wheel acts on it.
    const land = () => { if (!shot) return; shot = false; cameraRig.flyTo({ ...home, ms: 0 }); };
    const finish = () => {
      if (over) return;
      over = true;
      clearTimeout(guard);
      for (const name of INPUTS) window.removeEventListener(name, onInput, true);
      root.classList.remove('sr-opening-on');
      if (overlay) overlay.remove();
      ctx.opening.live = false;
      window.dispatchEvent(new CustomEvent('sr:opening-end'));
    };
    function onInput(e) {
      land();
      const onButton = e.target && typeof e.target.closest === 'function' && e.target.closest('.sr-opening button');
      if (!onButton) finish();
    }
    for (const name of INPUTS) window.addEventListener(name, onInput, { capture: true, passive: true });
    // A tab that is hidden runs no frames and the flight would never arrive: the guard lands it.
    const arm = (ms) => { clearTimeout(guard); guard = setTimeout(() => { land(); finish(); }, ms); };
    arm(plan.ms + 1500);
    cameraRig.flyTo({ ...home, distance: far, azimuth: homeAzimuth - plan.turn, ms: 0 });
    cameraRig.flyTo({
      ...home, ms: plan.ms, ease: 'inout', targetDelay: 0,
      onArrive: () => { if (shot) { shot = false; finish(); } },
      // Something else took the camera (a trip, a selection): it is theirs, and the words go.
      onCancel: () => { if (shot) { shot = false; finish(); } },
    });
    import('./ui/opening.js')
      .then((m) => { if (!over) overlay = m.createOpening(ctx, { done: finish, hold: arm }); })
      .catch((e) => console.warn('the opening\'s words did not load', e));
    return true;
  }
  const startOpening = () => {
    if (!opening) return;
    const plan = opening;
    opening = null;
    // Only if the camera is still where the boot left it: a trip or a selection made meanwhile
    // has a flight of its own.
    if (!cameraRig.state.flying && !ctx.selected()) runOpening(plan);
  };
  render();
  revealUI();

  // The layer switches, BEFORE the panel that presses them (createRail, whose What to show paints
  // from them). The old panel applied the moment's defaults as it built, through ctx.setLayerOn; until 2026-09-22 that was
  // attached 260 lines below, so the panel's fallback wrote `layer.enabled = false` on every layer
  // off in Wonder -- and `enabled: false` means "the registry switched this layer off": no glyph
  // layer, never loaded, and the box did nothing. "Everything active", the geostationary ring,
  // the famous debris and the reentries read "nothing loaded" on every visit for that reason.
  function isLayerOn(id) {
    const layer = layerRec(id);
    if (!layer) return false;
    if (layer.forcedOff) return false;
    return layer.on !== undefined ? layer.on : !!(layer.moments && layer.moments[moment]);
  }
  ctx.isLayerOn = isLayerOn;
  ctx.setLayerOn = (id, on) => {
    const layer = layerRec(id);
    if (layer) layer.on = on;
    if (layer && on && layer.deferred && typeof ctx.loadLayerNow === 'function') ctx.loadLayerNow(layer);
    const gl = glyphLayers.get(id);
    if (gl) gl.setVisible(on);
    if (id === 'worlds') worlds.setVisible(on);
    if (id === 'stars' && ctx.stars3d) ctx.stars3d.setVisible(on);
    if (id === 'stars') { unplacedOn = !!on; if (ctx.unplaced) ctx.unplaced.setVisible(on); } // 0079 r15-a45
    if (id === 'galaxy' && ctx.galaxy) ctx.galaxy.setVisible(on);
    if (id === 'systems' && ctx.systems) ctx.systems.setVisible(on);
  };

  // TODAY'S CLOUDS (2026-09-28, scene/liveclouds.js): NASA GIBS's geostationary pictures, composed
  // in a worker and cross-faded onto the Earth. Created now so the card and the Sources panel can
  // ask it what the clouds are; it fetches nothing until START_DELAY_MS after the layers are ready, and
  // nothing at all on a connection that saves data.
  ctx.liveClouds = createLiveClouds({
    earth: () => worlds.meshFor('earth'),
    saveData: typeof navigator !== 'undefined' && shouldSaveData(navigator.connection),
    // An open Earth card rewrites its clouds line on this (ui/cards.js): the card repaints on the
    // clock, and at 1x nothing else would tell it the pictures arrived.
    onChange: () => window.dispatchEvent(new CustomEvent('sr:clouds')),
  });
  // Started once the catalogues have landed, not from here (below, on sr:layers-ready): a fixed
  // delay from boot let the ~600 kB of GIBS pictures fall inside a slow first visit. MEASURED
  // 2026-10-01 on CI with the new shell: 758 kB from other hosts, 594 kB of it GIBS, which took the
  // first visit to 6 810 227 B, over first_visit_bytes. The map's own data comes first, the weather
  // after it.

  // THE AURORA (2026-09-30, scene/aurora.js, spec 0053 task 3): NOAA's OVATION forecast of the next
  // hour, drawn on the night side. It fetches nothing until the layers have landed, builds no mesh
  // and compiles no shader until a forecast has arrived, and never runs on a connection that saves
  // data. Its box is the `aurora` layer (data/layers.js `draw: 'aurora'`).
  //
  // OFF THE FIRST VISIT (2026-10-01, internal #192 item 6). scene/aurora.js and data/ovation.js are
  // 19.5 kB gzip that nothing on the first screen draws, and they were in the boot graph (and its
  // modulepreload block) all the same. They now arrive by a dynamic import AURORA_IMPORT_MS after
  // sr:layers-ready, in an idle moment: after the catalogues and the two seconds
  // tests/test_first_visit_bytes.mjs lets the first visit settle, and before the forecast's first
  // look (data/ovation.js START_DELAY_MS, 8 s after the same event, which start() keeps). Until then
  // ctx.aurora is auroraStandIn (above boot), so the Earth card, the Sources sheet and the layers panel ask
  // it what they always asked and are told "waiting". The import replaces it and says so on
  // sr:aurora, which they already repaint on. On a connection that saves data the module is never
  // fetched: the stand-in says why there is no aurora, which is all the real one would say there.
  const auroraSaveData = typeof navigator !== 'undefined' && shouldSaveData(navigator.connection);
  ctx.aurora = auroraStandIn(auroraSaveData, () => isLayerOn('aurora'));
  function loadAuroraLater() {
    if (auroraSaveData) return;
    const readyAt = performance.now();
    const load = () => import('./scene/aurora.js').then((m) => {
      const aurora = m.createAurora({
        earth: () => worlds.meshFor('earth'),
        renderer,
        camera,
        scene,
        saveData: false,
        // The Earth card rewrites its aurora line on this, as it does its clouds line on sr:clouds.
        onChange: () => window.dispatchEvent(new CustomEvent('sr:aurora')),
      });
      ctx.aurora = aurora;
      aurora.start({ elapsedMs: performance.now() - readyAt });
      window.dispatchEvent(new CustomEvent('sr:aurora'));
    }).catch((e) => {
      ctx.aurora.failed = true;
      console.warn('the aurora module did not load', e);
      window.dispatchEvent(new CustomEvent('sr:aurora'));
    });
    afterFirstVisit(AURORA_IMPORT_MS, load);
  }
  if (!embed) window.addEventListener('sr:layers-ready', loadAuroraLater, { once: true });

  // PLANETS AND MOONS SHINE BY THEIR REAL BRIGHTNESS (2026-10-12, scene/worldlights.js, spec 0072
  // requirement 1): one sprite of the stars' own kind on every world whose disc is small, at the
  // magnitude computed for where the camera is. OFF THE FIRST VISIT like the aurora: nothing on the
  // first screen needs it, and until it lands a planet is the small lit disc it always was.
  function loadWorldLightsLater() {
    afterFirstVisit(WORLDLIGHTS_IMPORT_MS, () => import('./scene/worldlights.js').then((m) => {
      ctx.worldLights = m.createWorldLights({ scene, worlds, camera, renderer, magLimit: exposure.look().starLimit });
      exposure.onChange((mode, look) => ctx.worldLights.setMagLimit(look.starLimit));
    }).catch((e) => console.warn('the worlds\' lights did not load', e)));
  }
  if (!embed) {
    if (window.__srLayersReady) loadWorldLightsLater();
    else window.addEventListener('sr:layers-ready', loadWorldLightsLater, { once: true });
  }
  {
    const layer = layerRec('aurora');
    if (layer) {
      // The panel's number for this layer is the forecast's peak probability (copy/en.js
      // controls.layerCountParts.auroraPeak), not a count of records: it has none.
      layer.count = () => ctx.aurora.peak();
      layer.counts = () => [{ key: 'auroraPeak', n: ctx.aurora.peak() }];
    }
  }

  // WEATHER ON EVERY WORLD (2026-10-03, spec 0066, scene/weather/): the Earth's lightning from
  // NOAA's map, the giants' and Venus's air in motion, Mars's season. OFF THE FIRST VISIT like the
  // aurora -- one dynamic import WEATHER_IMPORT_MS after sr:layers-ready -- and NEVER at tier 0 or on
  // a connection that saves data: there ctx.weather stays the stand-in, every world keeps
  // scene/worlds.js's own shader and nothing is fetched. The tier is the boot tier (ctx.quality is
  // made below, before the layers land); a device promoted later keeps what it booted with.
  ctx.weather = weatherStandIn(auroraSaveData, () => isLayerOn('lightning'));
  let weatherAsked = false;
  function loadWeatherLater() {
    const off = auroraSaveData || !ctx.quality || ctx.quality.bootTier < 1;
    if (off) {
      ctx.weather = weatherStandIn(true, () => isLayerOn('lightning'));
      window.dispatchEvent(new CustomEvent('sr:weather'));
      return;
    }
    loadWeather(false);
  }
  function loadWeather(now) {
    if (weatherAsked) return;
    weatherAsked = true;
    const readyAt = performance.now();
    const load = () => import('./scene/weather/index.js').then((m) => {
      const weather = m.createWeather({
        worlds,
        camera,
        reducedMotion: !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) || motionStill(),
        // A world's card rewrites its weather line on this, as the Earth's does its clouds line.
        onChange: () => window.dispatchEvent(new CustomEvent('sr:weather')),
      });
      if (ctx.latch && ctx.latch.latched) weather.latch();
      weather.setLook(ctx.look());
      ctx.weather = weather;
      weather.start({ elapsedMs: performance.now() - readyAt });
      window.dispatchEvent(new CustomEvent('sr:weather'));
    }).catch((e) => {
      ctx.weather.failed = true;
      console.warn('the weather modules did not load', e);
      window.dispatchEvent(new CustomEvent('sr:weather'));
    });
    if (now) load(); else afterFirstVisit(WEATHER_IMPORT_MS, load);
  }
  // The quality setting was raised from Low after the boot (createQuality's setChoice): a device that
  // booted without the weather fetches it now, unless the connection asked to save data.
  ctx.wantWeather = () => {
    if (embed || weatherAsked || auroraSaveData || !ctx.quality || ctx.quality.tier < 1) return;
    ctx.weather = weatherStandIn(false, () => isLayerOn('lightning'));
    loadWeather(true);
  };
  if (!embed) window.addEventListener('sr:layers-ready', loadWeatherLater, { once: true });
  {
    const layer = layerRec('lightning');
    if (layer) {
      // The panel's number for this layer is strikes a minute in NOAA's latest map (copy/en.js
      // controls.layerCountParts.lightningPerMin), not a count of records: it has none.
      layer.count = () => ctx.weather.perMinute();
      layer.counts = () => [{ key: 'lightningPerMin', n: ctx.weather.perMinute() }];
    }
  }

  say('Reading the catalogues…');
  // THE LAYOUT (spec 0061): one sidebar, one tool rail, one time pill. The shell builds the boxes;
  // each module below fills its own. The explore view (wordmark, search, the four tabs, Right now,
  // Trips, Coming up, the status line) is the sidebar's home; the sources sheet is a view of it; the
  // card is seated in it by the shell (ui/shell.js says how). The rail holds What to show (the
  // layers), the postcard and Hide (H); the pill is the clock.
  // The sources sheet is built on first opening (ui/shell.js ensureSources says why).
  const shell = createShell(ctx, { loadSources: (host) => import('./ui/status.js').then((m) => m.createStatus(ctx, host)) });
  createExplore(ctx, shell.host('home'));
  // The Tonight tab (spec 0051 task 2): your sky tonight, the next pass you can see and its countdown.
  // Imported the first time the tab is shown (explore.mountTab runs it then), so it costs a first
  // visit nothing.
  ctx.explore.mountTab('tonight', (host) => {
    import('./ui/tonight.js').then((m) => m.renderTonight(host, ctx)).catch((e) => console.warn('the Tonight tab did not load', e));
  }, { replace: true });
  createRail(ctx, shell.railHost);
  // Issue #251: email alerts, a row that opens in place under Coming up (ui/subscribe.js says why
  // it is there and not over the scene). Imported once the layers have settled: the row is below
  // the fold of the sidebar, and a first visit's bytes are the map's.
  // NO ENDPOINT, NO ROW (2026-10-05). The notifier is not deployed: ui/subscribe.js notifyEndpoint()
  // reads window.SPACE_RADAR_NOTIFY_URL, which nothing sets until a human runs
  // scripts/provision-notifier.sh, and the row answered every address with "Could not reach the
  // subscription service". A form that can only fail is not offered: without the URL the module is
  // not even fetched. The code and its tests stay for the day the URL exists.
  const subscribeLater = () => afterFirstVisit(SUBSCRIBE_MS, () => {
    if (!window.SPACE_RADAR_NOTIFY_URL) return;
    import('./ui/subscribe.js').then((m) => m.createSubscribe({ parent: ctx.explore.subscribeHost })).catch((e) => console.warn('the subscribe row did not load', e));
  });
  if (embed) embed.attach(ctx);
  else if (window.__srLayersReady) subscribeLater();
  else window.addEventListener('sr:layers-ready', subscribeLater, { once: true });
  createTimePill(ctx, shell.timeHost);
  // THE TIMELINE (public #455, ui/scrubber.js): the tape in the pill's upper row, with the marks
  // for what is coming. Imported SCRUBBER_MS after sr:layers-ready, past the two seconds the
  // first-visit measure waits, or at once when somebody reaches for the empty seat.
  let scrubber = null;
  const wantScrubber = () => scrubber || (scrubber = import('./ui/scrubber.js')
    .then((m) => m.createScrubber(ctx, ctx.timePill))
    .catch((e) => { scrubber = null; console.warn('the timeline did not load', e); }));
  ctx.timePill.tapeHost.addEventListener('pointerdown', wantScrubber, { once: true });
  const scrubberLater = () => afterFirstVisit(SCRUBBER_MS, wantScrubber);
  if (window.__srLayersReady) scrubberLater();
  else window.addEventListener('sr:layers-ready', scrubberLater, { once: true });
  // THE PASSPORT (spec 0041, ui/passport.js): where a visitor has been, in this browser only.
  // Asked for by the dated cards once the first visit has settled, and by the trip frame.
  let passport = null;
  // The reel (ui/autopilot.js): fetched when the link says `ambient=` or the Trips section's
  // "Play on its own" is pressed, never at boot. `from` is 'row' for the button.
  let autopilot = null;
  ctx.wantAutopilot = (keys, from) => (autopilot || (autopilot = import('./ui/autopilot.js').then((m) => (ctx.autopilot = m.createAutopilot(ctx)))))
    .then((a) => { a.start(keys, from); return a; })
    .catch((e) => { autopilot = null; document.documentElement.classList.remove('sr-ambient'); console.warn('the autopilot did not load', e); return null; });
  // THE DRIFT (ui/drift.js, spec 0071): the camera wanders from one thing to the next with no end
  // and no route. Fetched when the link says `drift=` or Just watch's row is pressed, never at boot.
  // `keys` is the link (or `{drift: '0'}` to stop); `from` is 'row' for the button.
  let drift = null;
  ctx.wantDrift = (keys, from) => (drift || (drift = import('./ui/drift.js').then((m) => (ctx.drift = m.createDrift(ctx)))))
    .then((d) => { d.apply(keys, from); return d; })
    .catch((e) => { drift = null; console.warn('the drift did not load', e); return null; });
  // A DRAWN WORLD ON A PLAIN STAR STAGE (scene/exostage.js, internal #466): `#imagine=N` opens "An
  // imagined world no. N", an invented and labelled planet. Fetched for that link, never at boot;
  // while it is up the map's scene is hidden and the loop below draws the stage's after it.
  let imagine = null;
  ctx.wantImagine = (n) => (imagine || (imagine = import('./scene/exostage.js').then((m) => (ctx.imagine = m.createImagineStage(ctx, { scene, onChange: (v) => writeUrlState({ imagine: v }) })))))
    .then((s) => { if (n && n !== '0') s.start(n); else s.stop(); return s; })
    .catch((e) => { imagine = null; document.documentElement.classList.remove('sr-imagine-on'); console.warn('the imagined world did not load', e); return null; });
  if (!embed && link && link.imagine && link.imagine !== '0') { document.documentElement.classList.add('sr-imagine-on'); ctx.wantImagine(link.imagine); }
  // 0079 C1: begin
  // A MOMENT (site/js/moments/, internal spec 0079): `#moment=<id>&t=<s>` replays a mission on a stage of its own, one
  // clock driving the vehicles, the camera and the captions. Fetched for that link, never at boot; while it is up the
  // map's scene is hidden and the loop below draws the moment's after it. `t` is the moment's mission second, read
  // raw here because bootLink() keeps the clock keys to itself.
  let momentP = null;
  ctx.wantMoment = (id, t) => (momentP || (momentP = import('./moments/player.js').then((m) => (ctx.replay = m.createMomentPlayer(ctx, { scene })))))
    .then((p) => { if (id && id !== '0') p.open(id, t); else p.close(); return p; })
    .catch((e) => { momentP = null; document.documentElement.classList.remove('sr-moment-on'); console.warn('the moment did not load', e); return null; });
  if (!embed && link && link.moment && link.moment !== '0') { document.documentElement.classList.add('sr-moment-on'); ctx.wantMoment(link.moment, readUrlKeys().t); }
  // 0079 C1: end
  // At sr:layers-ready a reel starts its own trips, and picks up the one a reloaded page's address names.
  const startReel = () => ctx.wantAutopilot(link);
  ctx.wantPassport = () => passport || (passport = import('./ui/passport.js')
    .then((m) => m.createPassport(ctx))
    .catch((e) => { passport = null; console.warn('the passport did not load', e); return null; }));
  // TODAY (internal #270, ui/today.js): the home's dated cards, generated from what is loaded,
  // and under them the way into the debris view (ui/debris.js). TODAY_MS after sr:layers-ready.
  const todayLater = () => afterFirstVisit(TODAY_MS, () => {
    import('./ui/today.js').then((m) => m.createToday(ctx, ctx.explore.todayHost)).catch((e) => console.warn('the dated cards did not load', e));
  });
  if (window.__srLayersReady) todayLater();
  else window.addEventListener('sr:layers-ready', todayLater, { once: true });
  // THE CONTROLS HINT (spec 0068 task 2, ui/keyhint.js): once per visitor, bottom-right, the keys
  // and the gestures that move the camera. Imported KEYHINT_MS after sr:layers-ready, so the first
  // visit's bytes are the map's; it decides for itself whether to show (not seen before, not a
  // trip, not a link). ctx.keyhint.show() opens it on request and imports it if it has to.
  const arrivedByLink = !!(link && (link.trip || link.at || link.event || link.stage)) || ambient || location.hash === '#sources';
  const keyHint = () => import('./ui/keyhint.js').then((m) => m.createKeyHint(ctx, { deepLink: arrivedByLink }));
  ctx.keyhint = { show: (o) => keyHint().then((api) => api.show(o)), toggleAll: () => keyHint().then((api) => api.toggleAll()) };
  const hintLater = () => afterFirstVisit(KEYHINT_MS, () => keyHint().then((api) => api.maybeShow()).catch((e) => console.warn('the controls hint did not load', e)));
  window.addEventListener('sr:layers-ready', hintLater, { once: true });
  // An embed shows no hint and does not fetch it (ui/embed.js): the listener comes off again.
  if (embed) window.removeEventListener('sr:layers-ready', hintLater);
  // A FIRST VISIT'S THREE LINES AND TWO BUTTONS (public #241, #287; ui/welcome.js): once per
  // visitor, at the head of the home. The storage is read here so a returning visitor does not
  // fetch the module at all; ctx.welcome.show() opens it on request (a probe).
  let welcomeApi = null;
  const welcome = () => welcomeApi || (welcomeApi = import('./ui/welcome.js')
    .then((m) => m.createWelcome(ctx, { deepLink: arrivedByLink || location.hash.length > 1, embed: !!embed, automated: navigator.webdriver === true })));
  ctx.welcome = { show: () => welcome().then((api) => api.show()) };
  const welcomeLater = () => afterFirstVisit(WELCOME_MS, () => {
    let seen = true;
    try { seen = !!window.localStorage.getItem('sr:welcome'); } catch { seen = true; /* no memory: no welcome */ }
    if (seen || arrivedByLink) return;
    // After the opening shot, never over it: the shot has the same two buttons (ui/opening.js),
    // and a press on either there is this section seen.
    const show = () => {
      let pressed = false;
      try { pressed = !!window.localStorage.getItem('sr:welcome'); } catch { pressed = true; }
      if (!pressed) welcome().then((api) => api.maybeShow()).catch((e) => { welcomeApi = null; console.warn('the welcome did not load', e); });
    };
    if (ctx.opening && ctx.opening.live) window.addEventListener('sr:opening-end', show, { once: true });
    else show();
  });
  // LAUNCH DAY (public #289, ui/launchchip.js): a countdown chip while a launch is inside a day.
  // RETURN TO BASE (public #241, ui/base.js): a house in the rail while the view is away from home.
  // ctx.returnToBase() works before the module has come (the trip's top bar calls it).
  const baseModule = () => import('./ui/base.js');
  ctx.returnToBase = () => baseModule().then((m) => m.returnToBase(ctx)).catch((e) => console.warn('the way home did not load', e));
  const launchDayLater = () => afterFirstVisit(LAUNCHDAY_MS, () => {
    import('./ui/launchchip.js').then((m) => { ctx.launchChip = m.createLaunchChip(ctx); }).catch((e) => console.warn('the launch chip did not load', e));
    baseModule().then((m) => { ctx.base = m.createBase(ctx, { host: document.getElementById('sr-rail') }); }).catch((e) => console.warn('the way home did not load', e));
  });
  if (embed || ambient) { /* a frame and a reel have no chrome to put them in */ }
  else if (window.__srLayersReady) { welcomeLater(); launchDayLater(); }
  else {
    window.addEventListener('sr:layers-ready', welcomeLater, { once: true });
    window.addEventListener('sr:layers-ready', launchDayLater, { once: true });
  }
  // THE TOUR (internal spec 0074, ui/onboarding.js): the rail's fourth tool rings the real controls
  // one at a time. Fetched on the first press, or for a first visit's arrow at that button TOUR_MS
  // after sr:layers-ready and never over the opening shot; a returning visitor fetches nothing.
  let tour = null;
  ctx.onboardingReady = () => tour || (tour = import('./ui/onboarding.js')
    .then((m) => (ctx.onboarding = m.createOnboarding(ctx, { deepLink: arrivedByLink || location.hash.length > 1, embed: !!embed, automated: navigator.webdriver === true })))
    .catch((e) => { tour = null; console.warn('the tour did not load', e); return null; }));
  ctx.wantOnboarding = (from) => ctx.onboardingReady().then((api) => { if (api) api.toggle(from); return api; });
  const tourLater = () => afterFirstVisit(TOUR_MS, () => {
    let seen = true;
    try { seen = !!window.localStorage.getItem('sr:tour'); } catch { seen = true; /* no memory: no arrow */ }
    if (seen || arrivedByLink || location.hash.length > 1) return;
    const point = () => ctx.onboardingReady().then((api) => api && api.maybeInvite());
    if (ctx.opening && ctx.opening.live) window.addEventListener('sr:opening-end', point, { once: true });
    else point();
  });
  if (embed || ambient) { /* no rail to point at */ } else if (window.__srLayersReady) tourLater();
  else window.addEventListener('sr:layers-ready', tourLater, { once: true });
  // THE SCALE BADGE (public #296, ui/scalebadge.js): "drawn ×N larger" and True size, on the
  // Sun's stage, where every planet is a dot wider than itself. Fetched the first time that stage
  // is entered; the dot's width it is told is scene/orbitrings.js MARKER_PX, the one the dots use.
  let scaleBadge = null;
  const wantScaleBadge = () => {
    if (scaleBadge || embed || ambient || stage.worldId !== 'sun') return;
    scaleBadge = import('./ui/scalebadge.js')
      .then((m) => { ctx.scaleBadge = m.createScaleBadge(ctx, { ids: SYSTEM_RINGS, markerPx: MARKER_PX }); })
      .catch((e) => { scaleBadge = null; console.warn('the scale badge did not load', e); });
  };
  window.addEventListener('sr:stage', wantScaleBadge);
  wantScaleBadge();
  // OFFLINE (site/sw.js, ui/offline.js; issues #290, #453): the service worker is registered
  // OFFLINE_MS after sr:layers-ready, past the keys hint, so nothing it does is a first visit's cost.
  // NEVER IN AN EMBED (public #439): a frame under someone else's headline keeps nothing on the
  // reader's device, and the offline module is not even fetched there.
  // The card, the trips and the model shapes, when idle (WARM_MS, top of this file). In order of
  // what a visitor reaches for first; each is a no-op if a tap or a link already fetched it.
  // In an IDLE moment, as the later layers are: on a machine still busy with its first frames the
  // timer alone would put 640 kB beside the work that is already late.
  const warmLater = () => afterFirstVisit(WARM_MS, () => {
    if (typeof navigator !== 'undefined' && shouldSaveData(navigator.connection)) return;
    wantCards().then(() => ctx.trip.warm()).then(() => warmModels());
  });
  // Not in an embed: a frame under someone else's article shows one object or one trip, and
  // fetches what that needs when it needs it.
  if (embed) { /* nothing is warmed */ }
  else if (window.__srLayersReady) warmLater();
  else window.addEventListener('sr:layers-ready', warmLater, { once: true });
  const offlineLater = () => afterFirstVisit(OFFLINE_MS, () => {
    import('./ui/offline.js').then((m) => m.createOffline(ctx)).catch((e) => console.warn('the offline module did not load', e));
  });
  if (embed) { /* no service worker */ }
  else if (window.__srLayersReady) offlineLater();
  else window.addEventListener('sr:layers-ready', offlineLater, { once: true });
  // One line on the scene when no satellite could be read at all (ui/scenenote.js).
  ctx.sceneNote = createSceneNote(ctx);
  // 0076 W4-c (internal #912): one line on what is on screen, for a screen reader; not in an embed, which has no panels.
  const summaryLater = () => afterFirstVisit(SUMMARY_MS, () => {
    import('./ui/scenesummary.js').then((m) => m.createSceneSummary(ctx)).catch((e) => console.warn('the scene summary did not load', e));
  });
  if (embed) { /* no summary */ }
  else if (window.__srLayersReady) summaryLater();
  else window.addEventListener('sr:layers-ready', summaryLater, { once: true });
  // `#sources` opens the sheet (design §6): a link to "what could this page read" is worth having.
  const openSourcesFromHash = () => { if (location.hash === '#sources') shell.openSources(); };
  openSourcesFromHash();
  window.addEventListener('hashchange', openSourcesFromHash);
  // The trip's frame (the intro, the toolbar, the top bar, the end), after the shell exists: it
  // seats its sheet in the sidebar (the phone's sheet under 900 px) and hides the rail, the pill
  // and the phone's top bar. IMPORTED WHEN THE FIRST TRIP
  // STARTS, not at boot (spec 0061 task 7): it is 50 kB a visitor who never takes a trip need not
  // download, and the first visit was 160 bytes inside its budget before the frame was rebuilt.
  // The frame paints the state it finds when it is made, so nothing a trip did while it loaded is
  // lost; its keys work from then on (Escape before it lands is the card's, as it always was).
  let framing = null;
  const offFrame = ctx.trip.onChange((st) => {
    // A film has no toolbar and no intro card: the frame is never fetched (ui/rendermode.js).
    // Nor has an embed: its trip plays under one line of caption (ui/embed.js).
    if (framing || film || embed || !st || st.phase === 'idle') return;
    framing = import('./ui/tripframe.js')
      .then((m) => { ctx.tripFrame = m.createTripFrame(ctx); offFrame(); })
      .catch((e) => { framing = null; console.warn('the trip frame did not load', e); });
  });
  // The film's cue sheet and its lower third hear the trip from here on (window.__srRender).
  if (film) film.attach(ctx);
  ctx.openAt = (id) => openAt(ctx, id);
  // Names over the scene (spec 0026 req 5): the selection, its train, the nearest notable things.
  const labels = createLabels(ctx, document.getElementById('labels'));
  ctx.labels = labels;
  // The tracked object's reticle, tick, tag and off-screen chevron (spec 0047, ui/hud.js). It asks
  // for the selection's position through ctx.positionOfRecord, the same function follow() reads,
  // so the brackets and the camera can never be on two different answers.
  ctx.positionOfRecord = (record) => positionOfRecord(record);
  ctx.hud = createHud(ctx, document.body);
  // One lap of the selection's orbit (spec 0026 req 13), from the same elements as the dot.
  const orbitLine = createOrbitLine(scene, ctx);
  ctx.orbitLine = orbitLine;
  // The followed object's track over the ground, on the globe (spec 0048 req 3), and its minute
  // marks as names over the scene. Three draw calls while something low is followed, none otherwise.
  ctx.groundTrack = createGroundTrack(scene, ctx);
  ctx.trackLabels = createTrackLabels(ctx, document.getElementById('labels'), ctx.groundTrack);
  // RIDE ALONG (spec 0048 req 8): the camera 60 km behind and 20 km above, looking 400 km ahead
  // along the velocity, which is the same one-second central difference the card's speed row uses.
  ctx.rideAlong = (record) => {
    if (!record) return false;
    leaveGround();
    const at = (tMs) => { const p = propagate(record, tMs); return p ? stage.toScene(p, p.frame, tMs) : null; };
    const getVel = () => {
      const t = clock.now();
      const a = at(t - 500);
      const b = at(t + 500);
      return a && b ? b.sub(a) : null;
    };
    const u = stage.unitKm;
    return cameraRig.rideAlong(() => positionOfRecord(record), getVel, { back: 60 / u, up: 20 / u, lookAhead: 400 / u, ms: 800 });
  };
  // The planets' paths and a dot at each, on the Sun stage while a trip names them (`orbits:` in
  // registry/tours.yaml; scene/orbitrings.js says why "A year in a minute" needs them).
  ctx.orbitRings = createOrbitRings(scene, { renderer, camera });
  setMoment(moment, { silent: true });

  // The rest of the link is applied ONCE the layers have landed (spec 0032 req 2): a trip
  // resolves its stops against records and `at` names one, so before this there is nothing to
  // apply it to. The moment and the clock were applied at boot already, and what is applied here
  // is the link as it was read then, not the hash as it is now (2026-09-23: the hash by now holds
  // the app's own clock, up to a second stale, and re-applying it undid a visitor's first scrub).
  // Registered before the load starts so the event cannot be missed.
  // A trip the visitor has already started by then outranks the link (ui/urlstate.js laterLink).
  // `#go=<id>` (internal #466): the short link a post ends on. The rest of the link applies as
  // ever; the place it names is met by one slow push-in (ui/golink.js), fetched for it. Returns
  // the landing's promise, or null when the link is not one (a film being made reads no link).
  const goLanding = (tripRunning) => {
    if (film || !goId || tripRunning || !link || link.unknownVersion || link.trip || link.event || (link.imagine && link.imagine !== '0')) return null; // 0077 W3-e: event, imagine
    applyUrlState(ctx, { ...link, at: undefined, cam: undefined });
    return import('./ui/golink.js').then((m) => m.land(ctx, goId), () => openAt(ctx, goId));
  };
  // `#drift=…` (ui/drift.js): after the link's own place, so `#at=…&drift=1` starts from there.
  // Hands back what it was given, the landing, so the caller's line reads as it did.
  const startDrift = (landed) => {
    if (!embed && link && link.drift && link.drift !== '0') ctx.wantDrift(link, 'link');
    return landed;
  };
  window.addEventListener('sr:layers-ready', () => {
    const tripRunning = !!(ctx.trip && ctx.trip.state && ctx.trip.state.phase !== 'idle');
    const apply = () => (ambient ? startReel() : startDrift(goLanding(tripRunning) || applyUrlState(ctx, laterLink(link, tripRunning))));
    // A trip's stops may be stars or exoplanets (OFF THE FIRST VISIT): those land first. An `at`
    // waits only if it does not resolve without them (openAt).
    if (link && link.trip && ctx.loadAfterFirstVisit) ctx.loadAfterFirstVisit().then(apply, apply);
    else apply();
    // Today's clouds: their first look is START_DELAY_MS after this, never during the first visit.
    if (!embed) ctx.liveClouds.start();
  }, { once: true });

  // Data arrives in the background, layer by layer, slowest last. Nothing here is awaited by the
  // render loop.
  loadAllLayers(ctx, layerRecords, glyphLayers, scene).then(() => {
    // A flag as well as the event, for a probe that attaches after it fired: the first-visit byte
    // test (spec 0044) waits on it.
    window.__srLayersReady = true;
    window.dispatchEvent(new CustomEvent('sr:layers-ready'));
    // An embed: only what its link names (openAt).
    if (!embed) afterFirstVisit(LATER_LAYERS_MS, () => ctx.loadAfterFirstVisit());
  });
  let shellsLoad = null;
  // A rung of the ladder or a star system's stage draws them: no waiting for the idle moment.
  window.addEventListener('sr:stage', (e) => {
    const id = e && e.detail ? e.detail.worldId : stage.worldId;
    if ((isLadderStage(id) || isSystemStage(id)) && ctx.loadAfterFirstVisit) ctx.loadAfterFirstVisit();
    if ((isLadderStage(id) || isSystemStage(id)) && ctx.loadMoreCatalogues) ctx.loadMoreCatalogues(); // 0079 r15-a45
    // The radio bubble and the microwave background's surface (scene/shells.js, internal #306):
    // two wire spheres, fetched with the first rung and drawn only on rungs.
    if (isLadderStage(id) && !ctx.shells && !shellsLoad) {
      shellsLoad = import('./scene/shells.js').then((m) => { ctx.shells = m.createShells(scene); })
        .catch((err) => { console.warn('the shells did not load', err); shellsLoad = null; });
    }
  });

  // The device tier (scene/quality.js, 2026-09-28), decided before the first frame and acted on
  // only after it: the boot set is the same for every device, and a laptop swaps sharper maps in
  // when the browser is idle (scene/texturetiers.js).
  ctx.quality = createQuality(ctx, renderer, starfield, worlds);

  startLoop({ ctx, resize, render, worlds, glyphLayers, cameraRig, starfield, heroes, lod });
  fadeBoot();
  startOpening();

  // --- interaction ----------------------------------------------------------

  // WHAT A TAP MEANS IS DECIDED WHERE THE VISITOR AIMED (internal pick review, 2026-11-02). The decision
  // is worked out at pointerdown and used at pointerup, so a tap during a flight, the opening or the
  // drift gets what was under the pointer, not what has drifted under it since. A second finger makes
  // the gesture a pinch and never a tap. A long press is for fingers; a mouse click is a click however
  // long it was held. The rules themselves are scene/pickrank.js.
  const pointers = new Set();
  let gesture = null;
  canvas.addEventListener('pointerdown', (e) => {
    pointers.add(e.pointerId);
    if (pointers.size > 1) { if (gesture) gesture.pinched = true; return; }
    const type = e.pointerType === 'touch' ? 'touch' : 'mouse';
    gesture = { id: e.pointerId, type, x: e.clientX, y: e.clientY, t: performance.now(), pinched: false, decision: decisionAt(e.clientX, e.clientY, type) };
  });
  window.addEventListener('pointerup', (e) => { pointers.delete(e.pointerId); });
  const cancelGesture = () => { pointers.clear(); gesture = null; };
  window.addEventListener('pointercancel', cancelGesture);
  canvas.addEventListener('pointerup', (e) => {
    const g = gesture;
    if (!g || g.id !== e.pointerId) return;
    gesture = null;
    if (g.pinched || pointers.size > 1) return;
    // A drag is a camera move, not a tap: 5 px for a mouse, 10 for a finger.
    if (Math.hypot(e.clientX - g.x, e.clientY - g.y) > TAP_SLOP_PX[g.type]) return;
    const held = performance.now() - g.t;
    hideChooser();
    const d = g.decision;
    // A long press on a crowded spot, and a tap the two best of which are too close to tell apart, list what is there instead of guessing.
    if ((g.type === 'touch' && held >= LONG_PRESS_MS) || d.ambiguous) {
      const list = d.list.slice(0, 6);
      if (list.length > 1) { showChooser(list, e.clientX, e.clientY, (rec) => select(rec, pickOpts(list.find((x) => x.record === rec), null)), { layers: LAYERS }); return; }
    }
    let hit = d.winner;
    // From the ground a tap asks "what is that" (sky/skyview.js tapSky): a satellite's dot or a
    // photograph answers as before with its card; a star, a planet or anything else in the sky is
    // named in a tag first, and the same thing tapped again is its card.
    if (ctx.skyView && ctx.skyView.ownsSky) {
      if (hit && (hit.klass === 'world' || hit.klass === 'star' || hit.klass === 'dso')) hit = null;
      hit = hit || skyPicturePick(e.clientX, e.clientY);
      if (!hit && typeof ctx.skyView.tapSky === 'function' && ctx.skyView.tapSky(e.clientX, e.clientY)) return;
    }
    // An empty tap clears the card and leaves the camera where it is: going home is the card's close button.
    if (hit) select(hit, pickOpts(d.list.find((x) => x.record === hit), reachFor(g.type)));
    else deselect({ stay: true });
  });
  /** A tap on something that is only BEHIND the stage opens its card and moves nothing: "Fly to it" is the journey. */
  // A near miss opens the card and flies nowhere (live site, 2026-11-02: a click on dark sky between dense stars
  // flew the camera 2 313 light-years to a faint unnamed star). The camera goes only for a click on the dot itself,
  // or for a choice from the list (reach null).
  function pickOpts(item, reach) {
    const nearMiss = reach && item && item.kind === 'glyph' && Number.isFinite(item.px) && item.px > reach.onDisc;
    // An unnamed catalogue star is a speck among thousands: tapped, it opens its card and "Fly to it" is the journey
    // (live 1.5.4: a tap on dark sky on the stellar rung flew to HYG 20454, 68 light-years off).
    const speck = reach && item && item.record && item.record.meta && item.record.meta.named === false;
    return item && (item.tier === 'backdrop' || nearMiss || speck) ? { from: 'pick', cardOnly: true } : { from: 'pick' };
  }

  // HOVER (mouse only): the same decision a click makes, no more than every 50 ms, with a pointer cursor and the name at the pointer.
  let hover = null;
  canvas.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse') return;
    if (hover) { hover.move(e); return; }
    hover = { move() {}, clear() {} };
    import('./ui/hover.js').then((m) => {
      hover = m.createHover({
        canvas,
        decideAt: (x, y) => decisionAt(x, y, 'mouse'),
        nameOf: (rec) => labelName(rec) || rec.name,
        busy: () => cameraRig.state.flying || (ctx.skyView && ctx.skyView.ownsSky) || inTrip() || pointers.size > 0,
      });
      hover.move(e);
    }).catch(() => {});
  });
  canvas.addEventListener('pointerleave', () => hover && hover.clear());

  /** From the ground, a tap on a nebula's or a galaxy's photograph opens its card (sky/groundpictures.js). */
  function skyPicturePick(clientX, clientY) {
    if (!ctx.skyView || !ctx.skyView.ownsSky || typeof ctx.skyView.pickSky !== 'function') return null;
    const id = ctx.skyView.pickSky(clientX, clientY);
    return id ? (ctx.recordsFor('deep-sky').find((r) => r.id === id) || null) : null;
  }

  /** What a tap at this point means, by what is pointing (scene/pickrank.js): {winner, list, ambiguous}. */
  function decisionAt(clientX, clientY, type) {
    const rect = canvas.getBoundingClientRect();
    const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1;
    // From the ground the air lifts what is low (internal #418): the pick looks where the thing is before the air.
    const [pickX, pickY] = ctx.skyView && ctx.skyView.trueNdc ? ctx.skyView.trueNdc(ndcX, ndcY) : [ndcX, ndcY];
    return pick(pickX, pickY, rect, reachFor(type));
  }

  const BACKDROP_KLASSES = new Set(['star', 'exoplanet', 'dso', 'exotic']);
  /** "local" is of the stage's own scale; "backdrop" is only behind it: a star seen from a world, a galaxy past the rung's far plane. */
  function tierOfRecord(rec) {
    if (!rec) return 'local';
    if (BACKDROP_KLASSES.has(rec.klass) && !isLadderStage(stage.worldId)) return 'backdrop';
    return farOff(rec) && stage.worldId !== 'local-group' ? 'backdrop' : 'local';
  }

  /** Everything within the pointer's reach, from every layer that is on, unranked, each mark with its tier. */
  function candidatesAt(ndcX, ndcY, rect, reach) {
    const vp = { w: rect.width, h: rect.height };
    const r = reach.local; // the layers scan with the wider reach; pickrank.js cuts each tier at its own
    const glyphs = [];
    const add = (list, tier) => { for (const c of list) { c.tier = tier || tierOfRecord(c.record); glyphs.push(c); } };
    for (const layer of LAYERS) {
      if (!isLayerDrawable(layer)) continue;
      const gl = glyphLayers.get(layer.id);
      if (!gl || !gl.pickAll) continue;
      add(gl.pickAll(ndcX, ndcY, 6, r, camera, vp));
    }
    if (isLayerOn('stars') && ctx.stars3d) {
      add(ctx.stars3d.pickAll(ndcX, ndcY, camera, vp, 6, r));
      if (ctx.unplaced) add(ctx.unplaced.pickAll(ndcX, ndcY, camera, vp, 6, r)); // 0079 r15-a45
    }
    // 0076 W4-a (spec 0075 task 63): the local volume's galaxies (scene/deep/localvolume.js), on the Local Group's rung
    if (ctx.deep && ctx.deep.pickAll) add(ctx.deep.pickAll(ndcX, ndcY, camera, vp, 6, r));
    // 0079 a8: begin (every other comet of the feed, scene/cometfield.js)
    if (ctx.cometField && ctx.cometField.pickAll && isLayerDrawable(layerRec('comets'))) add(ctx.cometField.pickAll(ndcX, ndcY, camera, vp, 6, r), 'local');
    // 0079 a8: end
    // A star system's own star and planets, on its stage (spec 0040): the same exo-... records, and what the stage is about.
    if (ctx.systems && ctx.systems.active) add(ctx.systems.pickAll(ndcX, ndcY, camera, vp, 6, r), 'local');
    // Every disc near the pointer, also those too far to be taken: a disc hides the marks behind it.
    const discs = isLayerOn('worlds') && worlds.pickAll ? worlds.pickAll(ndcX, ndcY, camera, vp, r + 24) : [];
    return { glyphs, discs };
  }

  function pick(ndcX, ndcY, rect, reach) {
    // One decision across every layer (scene/pickrank.js): what belongs to the stage beats what is
    // only behind it, a disc hides what is behind it, the nearer mark wins, and two marks too close to
    // tell apart are shown as a list. The layer draw order does not decide.
    const all = candidatesAt(ndcX, ndcY, rect || canvas.getBoundingClientRect(), reach || reachFor('touch'));
    for (const d of all.discs) d.tier = 'local';
    return decide(all.glyphs, all.discs, reach || reachFor('touch'));
  }

  /**
   * @param {Object} record
   * @param {{fly?: boolean, flyMs?: number, flight?: object}} [opts]  `flyMs` and `flight` are the
   *   drift's (ui/drift.js): a flight of several seconds with the cruise ease, where a tap's is 900 ms.
   *   `fly: false` keeps everything else -- the card, the glyph
   *   highlight, `follow`, and the real `sr:select` the rest of the page listens for -- and
   *   suppresses only the 900 ms flight. A guided trip is already on its way to
   *   this object with a flight of its own, and two flights fighting over the camera is what
   *   selecting from inside one used to look like.
   */
  function select(record, opts = {}) {
    // A tap on something that is only BEHIND the stage (pickrank.js tier): its card, and nothing moves, not the stage, not the camera, not a follow.
    if (opts.cardOnly) opts = { ...opts, fly: false };
    // 0079 r15-a45: a star with a direction and no distance has no place to fly to, and the map does not make one up: the card opens, the camera stays.
    if (record && record.meta && record.meta.noDistance && opts.fly !== false) opts = { ...opts, fly: false };
    // From the ground nothing flies: the visitor is standing in a field. The card opens, and the
    // search box turns the sky to what it found (ui/search.js, skyView.pointAtRecord). Without this
    // a star chosen there recentred the whole scene on the stellar rung under the sky view's feet.
    if (ctx.skyView && ctx.skyView.active && opts.fly !== false) opts = { ...opts, fly: false };
    // A STAR SYSTEM NOBODY HAS ASKED FOR YET (internal #466). A star of the catalogue that is a
    // system's host stands for that system's own star; and a member of a system whose rows have not
    // been fetched waits for them, once, then is selected as if they had always been here. If they
    // cannot be had the planet is what it was before: a mark at its star on the stellar rung.
    if (record && ctx.systems && opts.fly !== false) {
      record = ctx.systems.hostRecordFor(record) || record;
      if (!opts.systemTried && ctx.systems.pending(record)) {
        const again = () => select(record, { ...opts, systemTried: true });
        ctx.systems.load().then(again, again);
        return;
      }
    }
    // A selection made for the visitor from a list, the search, the timeline or a link flies the
    // camera somewhere they did not point at: the view before it is kept and the way back offered
    // (internal #274, ui/camundo.js). Not for a tap on the thing itself, which is pointing at it;
    // not for a trip's own stops; not when the caller is the undo.
    const offer = record && opts.fly !== false && opts.from !== 'pick' && opts.undo !== false && record !== selected;
    // `remembered`: the caller took the picture itself, before it moved the clock.
    if (offer && !opts.remembered) rememberView();
    // A planet with a registry/systems.yaml row, or its host star, is a place on its SYSTEM's stage
    // (spec 0040 req 6), where it is drawn at its own size on its orbit; everywhere else it is a mark
    // at its star. The same rule as a star recentring on the stellar rung below, one scale further in.
    const systemStage = ctx.systems ? ctx.systems.stageOfRecord(record) : null;
    const offWorld = stage.worldId !== 'sun' && (isLadderStage(stage.worldId) || isSystemStage(stage.worldId));
    if (systemStage && opts.fly !== false) ctx.setStage(systemStage);
    // A star is a place on the stellar rung: from a world stage its true position is past the far
    // plane, so selecting one recentres on the Sun at one unit = one light-year first. From a star
    // system's stage too: there the rest of the sky is a shell of directions.
    else if (record && ['star', 'exoplanet', 'dso', 'exotic'].includes(record.klass) && !isLadderStage(stage.worldId) && opts.fly !== false) ctx.setStage(farOff(record) ? 'local-group' : 'stellar');
    // A place past the stellar rung's far plane (a billion light-years: TON 618) is met on the Local
    // Group's, where it is drawn at all (spec 0075 task 17, DS-D10).
    else if (farOff(record) && isLadderStage(stage.worldId) && stage.worldId !== 'local-group' && opts.fly !== false) ctx.setStage('local-group');
    // And back: a thing inside the Solar System, chosen on a rung (from search, or the Next list),
    // sits inside the Sun's pixel there and its layer does not draw (isLayerDrawable above), so the
    // camera would fly into one pixel and show nothing. It goes home to the Earth stage first. The
    // Sun is a place on the ladder and stays. From a star system's stage, where nothing of ours is
    // drawn at all, the Sun goes home too.
    else if (record && offWorld && opts.fly !== false
      && !['star', 'exoplanet', 'dso', 'exotic'].includes(record.klass)
      && !(record.klass === 'world' && record.id === 'sun' && isLadderStage(stage.worldId))) ctx.setStage('earth');
    selected = record;
    // A star's own width, for its card (ui/cards.js prints it as an estimate) and its disc.
    if (record && record.klass === 'star' && record.meta && !Number.isFinite(record.meta.widthSuns)) {
      const phys = stars3d.physicalOf(record);
      if (phys) { record.meta.widthSuns = phys.radiusKm / SUN_RADIUS_KM; record.meta.widthFrom = phys.how; }
      // The first star chosen from a world's stage is chosen before the catalogue's binary has
      // landed (it is fetched when the ladder first draws). Its width is known once it has: the
      // card is repainted with it and the camera goes on in, if this star is still the selection.
      else if (stage.worldId === 'stellar') {
        stars3d.ensureGeometry().then(() => {
          const late = selected === record ? stars3d.physicalOf(record) : null;
          if (!late) return;
          record.meta.widthSuns = late.radiusKm / SUN_RADIUS_KM;
          record.meta.widthFrom = late.how;
          wantStarDisc(record);
          showCard(record, ctx);
          if (opts.fly !== false) flyToRecord(record, opts.flyMs, opts.flight);
        });
      }
    }
    wantStarDisc(record);
    // Start the map now, not when the disc grows past the threshold mid-flight: a selected world is
    // about to fill the screen, and a trip's own flight (fly: false) needs it just as much.
    if (record && record.klass === 'world') worlds.preload(record.id);
    for (const gl of glyphLayers.values()) if (gl.setSelected) gl.setSelected(record ? record.id : null);
    if (ctx.hud) ctx.hud.select(record);
    showCard(record, ctx);
    if (ctx.orbitLine) ctx.orbitLine.setRecord(record);
    if (ctx.groundTrack) ctx.groundTrack.set(record);
    if (opts.fly !== false) flyToRecord(record, opts.flyMs, opts.flight);
    else if (opts.cardOnly) cameraRig.stopFollow();
    else if (!(record && record.meta && record.meta.noDistance)) cameraRig.follow(() => positionOfRecord(record)); // 0079 r15-a45: nothing to follow
    window.dispatchEvent(new CustomEvent('sr:select', { detail: record }));
    // A place met on the night side is met from the whole Earth's distance, and the toast says why
    // (flyToRecord): with the way back when the view was moved for the visitor, alone when they
    // pointed at the place themselves.
    const night = !!record && nightArrival === record && opts.fly !== false;
    if (offer) offerUndo(opts.undoName || labelName(record) || record.name, night);
    else if (night && !inTrip() && opts.from !== 'drift') import('./ui/camundo.js').then((m) => m.sayNight(labelName(record) || record.name)).catch(() => {});
  }

  // --- the view before an automatic move, and the way back (internal #274) ------------------------
  let viewBefore = null;
  let viewStamp = -Infinity;
  const inTrip = () => document.documentElement.classList.contains('sr-trip-mode');
  function viewNow() {
    return {
      rig: cameraRig.saveState(), stage: stage.worldId, moment, selected,
      clock: { mode: clock.mode, t: clock.now(), rate: clock.rate, paused: clock.paused },
    };
  }
  /** Keep the view as it is now, before moving it. Calls within ONE_MOVE_MS are one move. */
  function rememberView() {
    if (inTrip()) return null;
    const at = performance.now();
    if (viewBefore && at - viewStamp < ONE_MOVE_MS) return viewBefore;
    viewStamp = at;
    viewBefore = viewNow();
    return viewBefore;
  }
  function restoreView(b) {
    if (!b) return;
    if (b.clock.mode === 'live') { if (clock.mode !== 'live') clock.live(); }
    else if (clock.mode === 'live' || Math.abs(clock.now() - b.clock.t) > 5 * 60e3) { clock.goTo(b.clock.t); clock.setRate(b.clock.rate); }
    if (stage.worldId !== b.stage) ctx.setStage(b.stage);
    if (moment !== b.moment) setMoment(b.moment);
    if (b.selected) select(b.selected, { fly: false, undo: false });
    else deselect();
    cameraRig.restoreState(b.rig, 600);
  }
  /** Offer the way back to the last remembered view, in a toast. `name` is where the view went. */
  function offerUndo(name, night = false) {
    const before = viewBefore;
    if (!before || inTrip()) return;
    import('./ui/camundo.js').then((m) => {
      if (before !== viewBefore || m.sameView(before, viewNow())) return;
      m.offerUndo(ctx, { name, night, undo: () => restoreView(before) });
    }).catch((e) => console.warn('the undo toast did not load', e));
  }
  ctx.rememberView = rememberView;
  ctx.offerUndo = offerUndo;
  // Not while a trip is at a stop: the card there is the stop's own (ui/trip.js paints it with the
  // stop's title and words), and a mission that landed a moment later repainted it as the object's
  // whole card. Seen 2026-10-09 on both Voyager 1 stops ("A gold record, further away than
  // anything" was read aloud under a card that said PROBE, 7 of 7 and "Fly to it").
  ctx.refreshCard = () => { if (selected && !tripOwnsCard(ctx.trip && ctx.trip.state)) showCard(selected, ctx); };
  /**
   * Run `fn` once the scene has been placed at the clock's new time. A world's drawn position,
   * and so a landing site's, is what the last frame put there: SEEN 2026-10-06, the clock set to
   * 20 July 1969 and the Apollo 11 site selected in the same tick flew the camera to where the
   * Moon was in 2026, and it arrived 35 km over the far side with the site behind the Moon. The
   * frame loop runs these right after it has updated the worlds (startLoop, below), whatever a
   * frame takes: a timer raced the frame and lost on a slow renderer. A hidden page gets no
   * frames, and nothing it would show: there it runs at once.
   */
  const afterUpdate = [];
  ctx.afterUpdate = afterUpdate;
  ctx.afterClockJump = (fn) => {
    const run = () => { try { fn(); } catch (e) { console.warn('after a clock jump', e); } };
    if (typeof document !== 'undefined' && document.hidden) { setTimeout(run, 0); return; }
    afterUpdate.push(run);
  };
  /** How far the home view stands from the Earth's centre, in scene units of the Earth's stage. */
  ctx.homeDistance = () => worldFramingDistance(6371 / STAGES.earth.unitKm, camera.fov, camera.aspect);
  /** The whole Earth in view, from wherever the camera is (the debris view asks for it). */
  ctx.frameEarth = (ms = 900) => {
    if (stage.worldId !== 'earth') ctx.setStage('earth');
    if (selected) deselect();
    cameraRig.flyTo({ targetScene: { x: 0, y: 0, z: 0 }, distance: worldFramingDistance(6371 / stage.unitKm, camera.fov, camera.aspect), ms });
  };
  // A MISSION'S EVENTS (public #452, ui/missions.js): fetched the first time a card asks, or a
  // link names an event. ctx.missions is the module once it is here.
  let missionsImport = null;
  ctx.missions = null;
  ctx.wantMissions = () => missionsImport || (missionsImport = import('./ui/missions.js')
    .then((m) => { m.install(ctx); ctx.missions = m; return m; })
    .catch((e) => { missionsImport = null; console.warn('the mission events did not load', e); return null; }));

  /**
   * Fly to a record and follow it. THE one definition: select() uses it, and so does the card's
   * "Fly to it" button through ctx.flyToRecord.
   *
   * The card used to have its own: the record's TRUE position through stage.toScene, at 2 % of its
   * distance. For a satellite that is a different framing of the same place. For a planet it is
   * empty sky -- worlds.js draws Mars along its true direction but ~115x nearer, so the button
   * that says "Fly to it" on Mars's own card flew 1.8 au past the disc and stopped there, looking
   * at nothing. positionOfRecord below says why in as many words; the card never asked it.
   */
  function flyToRecord(record, ms = 900, flight = null) {
    if (!record) return false;
    // `flight`: the rig's own `ease`, `apex` and `targetDelay`, for a caller whose move is a shot
    // and not a tap (ui/drift.js). Where to stand is still decided here, for every caller.
    // `apexFor(pos, distance)` is asked once the destination is known, on the stage it is on.
    const shotTo = (pos, distance) => (flight ? {
      ease: flight.ease, targetDelay: flight.targetDelay, path: flight.path,
      apex: typeof flight.apexFor === 'function' ? flight.apexFor(pos, distance) || undefined : flight.apex,
    } : {});
    if (record.klass === 'world') worlds.preload(record.id);
    const on = teachRigWorld(record);
    const pos = positionOfRecord(record);
    groundArrival = pos && onEarthsGround(record, pos) ? record : null;
    nightArrival = null;
    // A PLACE ON THE NIGHT SIDE IS SHOWN WITH THE EDGE OF DAYLIGHT IN THE PICTURE (internal #420,
    // scene/framing.js nightGroundPose): the whole Earth, as the home view has it, turned so the
    // place and the terminator are both on the disc. The camera looks at the Earth, not at the
    // place: the mark and the reticle say where it is, and the card says why the view is wide.
    const night = groundArrival ? nightGroundPose(pos, worlds.sunDirOf('earth') || worlds.drawnPositionOf('sun')) : null;
    if (night) {
      teachRigWorld(record);
      cameraRig.stopFollow();
      const wide = worldFramingDistance(6371 / stage.unitKm, camera.fov, camera.aspect);
      cameraRig.flyTo({ ...shotTo({ x: 0, y: 0, z: 0 }, wide), targetScene: { x: 0, y: 0, z: 0 }, distance: wide, offset: night.offset, ms });
      nightArrival = record;
      return true;
    }
    if (pos) {
      const distance = arrivalDistance(record, pos);
      const limb = limbPose(record, pos, distance, on);
      // A deep-sky object is approached FROM THE SUN'S SIDE, looking out along the line we see it
      // on (spec 0067). The rig's default arrival is from beyond the subject, looking back at its
      // world, which is right for a satellite and here would show a nebula's photograph from
      // behind, mirrored -- and scene/nebulae.js rightly draws no picture off the line it was
      // taken along. Pi less a few degrees: the same framing, turned around.
      const fromHere = record.klass === 'dso' && isLadderStage(stage.worldId) ? Math.PI - 0.1 : undefined;
      // A world is met on its lit face (issue #419): the rig's default is the far side from the
      // stage's world, which for everything beyond the Earth is the night side.
      let lit = record.klass === 'world' ? litOffset(worlds.sunDirOf(record.id), camera.up, undefined, worlds.faceDirOf(record.id)) : null;
      // A small body is met on its sunlit side too (internal #436): Ceres and Vesta were met from
      // wherever the camera was, which is the night half as often as not.
      if (!lit && record.klass === 'asteroid') {
        const sunAt = worlds.drawnPositionOf('sun');
        if (sunAt) lit = litOffset({ x: sunAt.x - pos.x, y: sunAt.y - pos.y, z: sunAt.z - pos.z }, camera.up);
      }
      // A planet on its own system's stage is met on its lit face too (internal #466): the rig's
      // default met TRAPPIST-1 e from behind, a black disc with a lit rim, which is no way to see a
      // face drawn on it (scene/exoface.js). Its light is its star, wherever that is on the stage.
      if (!lit && record.klass === 'exoplanet' && ctx.systems && ctx.systems.active && ctx.systems.stageOfRecord(record) === stage.worldId) {
        const star = ctx.systems.lightScene();
        if (star) lit = litOffset({ x: star.x - pos.x, y: star.y - pos.y, z: star.z - pos.z }, camera.up);
      }
      // AND THE DISTANCE IS SOLVED AGAIN ON ARRIVAL, once. A squeezed planet's drawn size depends on
      // where the camera is (scene/worlds.js: the neighbour cap is measured from the camera when
      // worlds crowd), and its moons are drawn at its scale: SEEN 2026-10-06, Mimas framed from the
      // Earth arrived a third wider than the screen's free band, because Saturn's drawn disc, and
      // Mimas with it, had grown on the way. Two frames after the flight ends the scene has been
      // placed from the new camera; if the framing is more than 8 % off, a short second move fixes it.
      const settle = record.klass === 'world' ? (reason) => {
        if (reason !== 'done' || typeof requestAnimationFrame !== 'function') return;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (selected !== record || cameraRig.state.flying) return;
          const at = positionOfRecord(record);
          const again = at ? arrivalDistance(record, at) : NaN;
          if (!(again > 0) || Math.abs(again / distance - 1) < 0.08) return;
          const side = litOffset(worlds.sunDirOf(record.id), camera.up, undefined, worlds.faceDirOf(record.id));
          cameraRig.flyTo({ targetScene: at, distance: again, offset: side || undefined, ms: 500, targetDelay: 0 });
        }));
      } : undefined;
      // A system's host star is met from over its orbits, as the trips meet it (40 degrees from the
      // pole, scene/systems.js OVERVIEW_POLAR), not from whatever tilt the camera happened to have
      // (internal #476: Kepler-16 arrived edge-on, LHS 1140 straight down).
      const overview = record.klass === 'star' && ctx.systems && ctx.systems.active && ctx.systems.stageOfRecord(record) === stage.worldId && !limb ? SYSTEM_OVERVIEW_POLAR : undefined; // polar: overview, unless the stage's own subject names one
      const hostSubject = !limb && record.klass !== 'exoplanet' && ctx.systems && ctx.systems.active && typeof ctx.systems.subjectFor === 'function' ? ctx.systems.subjectFor(record) : null;
      const hostPolar = hostSubject && Number.isFinite(hostSubject.polar) ? hostSubject.polar : overview;
      // A child chosen from afar is flown to THROUGH its parent (spec 0075 task 3): the LMC fills the
      // view, then the Tarantula, never a lone dot (scene/deep/deepsky.js pathFor).
      const via = !flight && record.klass === 'dso' && ctx.deep && ms > 0 ? ctx.deep.pathFor(record, cameraRig.state.target, camera) : null;
      cameraRig.flyTo({ ...shotTo(pos, limb ? limb.distance : distance), ...(via ? { path: via } : {}), targetScene: pos, distance: limb ? limb.distance : distance, tilt: limb ? limb.tilt : fromHere, polar: hostPolar, offset: lit || undefined, ms, onArrive: settle });
    }
    // Following something standing on the Moon is following the Moon, which crosses its own
    // radius in about half an hour, so its centre is re-taught with every tick of the target.
    cameraRig.follow(on
      ? () => {
        const c = worlds.drawnPositionOf(on);
        if (c) cameraRig.setWorldCentre(c);
        return positionOfRecord(record);
      }
      : () => positionOfRecord(record));
    return !!pos;
  }

  /**
   * The world a record stands on, when that is not the stage's own: a landing site on the Moon or
   * Mars, seen from the Earth stage. Null for everything else, the stage's own ground included.
   */
  function surfaceWorldOf(record) {
    const f = record && record.propagator === 'fixed' ? parseFrame(record.frame) : null;
    if (!f || f.kind !== 'fixed' || f.world === stage.worldId) return null;
    return worlds.drawnPositionOf(f.world) ? f.world : null;
  }

  /**
   * Tell the rig which world the camera must stay out of, and which way is "outward" when it frames
   * a flight. It was told once, at boot, and kept the stage's world for good -- so every flight to
   * a site on the Moon's near side was framed outward from EARTH's centre, along a line that runs
   * on into the Moon. Measured 2026-09-22 in headless Chrome: Apollo 12, selected, parked the camera
   * 231 km under the lunar surface, drawing the lunar module against the stars; the four older
   * Apollo sites did the same. ui/trip.js already re-teaches the rig at every stop (composeShot); a
   * plain selection now does too, and puts the stage's world back for anything that is not on
   * another world's ground. Returns the world it taught, or null.
   */
  function teachRigWorld(record) {
    const on = surfaceWorldOf(record);
    if (on) {
      cameraRig.setWorldRadius(worlds.drawnRadiusUnits(on));
      cameraRig.setWorldCentre(worlds.drawnPositionOf(on));
      return on;
    }
    const w = WORLDS.find((x) => x.id === stage.worldId);
    // On a star system's stage the ground the camera must stay out of is the star at the origin.
    const r = w ? w.radiusKm / stage.unitKm : ctx.systems && ctx.systems.active ? ctx.systems.starRadiusUnits() : 0;
    cameraRig.setWorldRadius(r);
    cameraRig.setWorldCentre({ x: 0, y: 0, z: 0 });
    return null;
  }

  /**
   * @param {{keepCard?: boolean}} [opts]  `keepCard: true` puts the selection down -- `follow`, the
   *   highlight, the HUD, the 260 px model -- and leaves the card where it is. ui/trip.js lets go of
   *   a stop's subject as the flight away from it starts, and the old card stays up until the next
   *   stop's title replaces it.
   */
  function deselect(opts = {}) {
    // Leaving a place on the Earth's ground gives the whole Earth back (internal #420): the camera
    // was a few thousand kilometres over a storm or a pad, or turned to the night side, and that
    // is not a home view. Not for a trip's own stops, nor from the ground's sky.
    const wasGround = groundArrival && groundArrival === selected && (!opts || opts.keepCard !== true)
      && !(opts && opts.stay) && stage.worldId === 'earth' && !inTrip() && !(ctx.skyView && ctx.skyView.active);
    groundArrival = null;
    selected = null;
    wantStarDisc(null);
    if (ctx.orbitLine) ctx.orbitLine.setRecord(null);
    if (ctx.groundTrack) ctx.groundTrack.set(null);
    for (const gl of glyphLayers.values()) if (gl.setSelected) gl.setSelected(null);
    if (ctx.hud) ctx.hud.clear();
    if (!opts || opts.keepCard !== true) hideCard();
    cameraRig.stopFollow();
    if (wasGround) cameraRig.flyTo({ targetScene: { x: 0, y: 0, z: 0 }, distance: worldFramingDistance(6371 / stage.unitKm, camera.fov, camera.aspect), ms: 900 });
    window.dispatchEvent(new CustomEvent('sr:select', { detail: null }));
  }

  function positionOfRecord(record) {
    // 0079 r15-a45: a star with a direction and no distance has no position to point at: the HUD's chevron would print the made-up distance of its direction
    if (record && record.meta && record.meta.noDistance) return null;
    // A world is drawn where scene/worlds.js put its DISC -- nearer than it is for the planets
    // (PLANET_VIEW, and the card says so). Flying to the true position would arrive at empty sky.
    if (record && record.klass === 'world') return worlds.drawnPositionOf(record.id);
    // A member of the star system whose stage this is: where its orbit puts it (scene/systems.js).
    const onSystem = record && ctx.systems ? ctx.systems.drawnPositionOf(record.id) : null;
    if (onSystem) return onSystem;
    // Deliberately NOT asking the glyph layer: it owns a packed position buffer for drawing, and
    // exposing a per-record lookup would make the camera depend on a layer being visible. The
    // contract's propagate + stage.toScene answers this for any record, drawn or not.
    // ONE INSTANT A FRAME (Ivan, 2026-10-14: a selected object shook). The live clock is the wall
    // clock, read anew by every clock.now(): the camera's follow read it at the top of a frame,
    // the dot and the model were placed at the frame's own `t`, and the brackets asked again after
    // the frame was drawn, a whole render later. 16 ms of a low orbit is 120 m and on a slow phone
    // a render is 60: the brackets led the dot by an amount that changed with every frame's cost.
    // Inside a frame the answer is for the frame's instant (ctx.frameT, set by the loop).
    const tMs = clock.mode === 'live' && Number.isFinite(ctx.frameT) ? ctx.frameT : clock.now();
    const p = propagate(record, tMs);
    if (!p) return null;
    return stage.toScene(p, p.frame, tMs);
  }

  /** A star's width and temperature: a catalogue star's worked out, a planet's host from the Archive. */
  function starPhysicalOf(record) {
    if (!record || !record.meta) return null;
    if (record.klass === 'star') return stars3d.physicalOf(record);
    if (record.klass === 'exoplanet' && record.meta.starRadiusSuns > 0) {
      return { radiusKm: record.meta.starRadiusSuns * SUN_RADIUS_KM, teffK: record.meta.starTeffK > 0 ? record.meta.starTeffK : SUN_TEFF_K, how: 'measured' };
    }
    return null;
  }
  ctx.starPhysicalOf = starPhysicalOf;
  let starDiscImport = null;
  /** The disc is drawn by a module fetched the first time a star is chosen, never at boot. */
  function wantStarDisc(record) {
    const phys = starPhysicalOf(record);
    // The rig may stand as near as two and a half radii of the star it follows, and no nearer than
    // its usual floor to anything else.
    cameraRig.state.minDistance = phys && stage.worldId === 'stellar' ? Math.min(RIG_MIN_DISTANCE, (phys.radiusKm * 2.5) / stage.unitKm) : RIG_MIN_DISTANCE;
    if (!phys && !ctx.starDisc) return;
    if (ctx.starDisc) { ctx.starDisc.set(record, phys); return; }
    if (!starDiscImport) {
      starDiscImport = import('./scene/stardisc.js').then((m) => {
        ctx.starDisc = m.createStarDisc(scene);
        ctx.starDiscHidePx = m.HIDE_POINT_PX;
        const s = ctx.selected();
        ctx.starDisc.set(s, starPhysicalOf(s));
      }).catch((e) => { console.warn('the star disc did not load', e); starDiscImport = null; });
    }
  }

  /** More than a billion light-years from the Sun: past the stellar rung's far plane (1e9 units). */
  /** A card opened by a tap leaves the camera where it was; the card's "Fly to it" is the journey, so it makes the stage hop select() would have. */
  function needsStageHop(record) {
    return !!record && ['star', 'exoplanet', 'dso', 'exotic'].includes(record.klass) && !(ctx.systems && ctx.systems.drawnPositionOf(record.id))
      && (!isLadderStage(stage.worldId) || (farOff(record) && stage.worldId !== 'local-group'));
  }
  function hopTo(record, ms, flight) {
    rememberView();
    select(record, { from: 'journey', flyMs: ms, flight, remembered: true });
    return true;
  }
  function farOff(record) {
    const p = record && record.klass !== 'world' && record.pos;
    return !!p && Math.hypot(p.x, p.y, p.z) > 1e9 * 9460730472580.8;
  }
  function arrivalDistance(record, pos) {
    if (record && record.klass === 'world') {
      // 3.5 radii, or farther when the free part of the screen is narrower than that disc
      // (scene/framing.js discDistance: the Moon on a phone was wider than the phone).
      // The radius it will have when the camera is there: a far moon is drawn no smaller than a
      // pixel, and framing the arrival on that left Ganymede a speck (scene/worlds.js arrivalRadiusUnits).
      const radius = worlds.arrivalRadiusUnits(record.id);
      const el = ctx.renderer && ctx.renderer.domElement;
      const room = freeRoom(el);
      const w = el && el.clientWidth > 0 ? el.clientWidth : window.innerWidth;
      const h = el && el.clientHeight > 0 ? el.clientHeight : window.innerHeight;
      const shareV = room ? Math.min(room.above, room.below) / (h / 2) : 1;
      const shareH = (w - 2 * Math.abs(viewShift.shiftXPx())) / w;
      // No nearer than three radii, whatever the unit: the old floor of 0.05 scene units is 50 km on
      // the Earth's stage, which is inside the drawn Deimos's own arrival and outside nothing else.
      return Math.max(radius * 3.5, discDistance(radius, { fovDeg: camera.fov, aspect: w / h, shareV, shareH }));
    }
    // On its system's stage a planet is a ball of its own size: eight radii, the trip's framing; the
    // host star is the whole system, every orbit in the picture.
    const inSystem = ctx.systems && ctx.systems.active && ctx.systems.stageOfRecord(record) === stage.worldId;
    if (inSystem) return ctx.systems.arrivalDistanceUnits(record);
    // A star whose width is known is met as a disc (scene/stardisc.js, public #436): twelve radii
    // out it is a fifth of the screen wide. On the stellar rung only, where a unit is a light-year
    // and the camera's doubles can stand that close; elsewhere, and for a star with no width, it
    // is a point of light: close, but not inside it.
    const phys = stage.worldId === 'stellar' ? starPhysicalOf(record) : null;
    if (phys) return Math.max(1e-9, (phys.radiusKm * STAR_ARRIVAL_RADII) / stage.unitKm);
    if (record && record.klass === 'star') return 0.4;
    if (record && record.klass === 'exoplanet') return 0.4;
    // Past a billion light-years, no nearer than float32 holds it steady (scene/camera.js, DS-D10).
    if (record && record.klass === 'exotic') return farOff(record) && pos ? Math.max(0.4, steadyDistance(Math.hypot(pos.x, pos.y, pos.z))) : 0.4;
    if (record && record.klass === 'dso') {
      // Frame the object by its own size: a galaxy 200 000 ly across wants the camera well back.
      // 0076 W4-a: a galaxy of the local volume with no catalogued diameter is framed by its distance (a thousandth of it), not as a 20 ly nebula
      const sizeLy = record.meta && Number.isFinite(record.meta.sizeLy) ? record.meta.sizeLy : record.meta && record.meta.distLy > 1e6 ? record.meta.distLy / 1000 : 20;
      const framing = Math.max(1, (sizeLy * 9460730472580.8 * 2.5) / stage.unitKm) * (ctx.deep && ctx.deep.arriveScale ? ctx.deep.arriveScale(record.id) : 1); // 0078 r14-neb-b: a hero's own framing
      // 0076 W4-a (spec 0075 task 64): the steady stand-off is for any place past the stellar rung's far plane, not only the exotics'
      return farOff(record) && pos ? Math.max(framing, steadyDistance(Math.hypot(pos.x, pos.y, pos.z))) : framing;
    }
    // A point on the Earth's own ground (a storm, a launch pad, a site) is met from no nearer than
    // the map can bear (internal #420, scene/framing.js groundDistanceKm).
    if (onEarthsGround(record, pos)) {
      const cv = ctx.renderer && ctx.renderer.domElement;
      return groundDistanceKm(cv && cv.clientHeight > 0 ? cv.clientHeight : window.innerHeight, camera.fov) / stage.unitKm;
    }
    // 0077 W3-d: begin -- a named place on a world's ground (registry/sites.yaml `class: feature`: Copernicus,
    // Tycho, Shackleton, the Moon's south pole) has no model to fill 260 px: it is met from where its own width
    // fills the narrower side of the screen, or from 60 km for a place with no width (a pole).
    if (record && record.meta && record.meta.siteKind === 'feature') {
      const tanV = Math.tan(((camera.fov || 50) * Math.PI) / 360);
      const tanN = Math.min(tanV, tanV * (camera.aspect || 1));
      const widthKm = Number(record.meta.diameterM) > 0 ? Number(record.meta.diameterM) / 1000 : 60;
      return Math.max(0.05, (widthKm / tanN) / stage.unitKm);
    }
    // 0077 W3-d: end
    const layer = layerRec(record.layer);
    const nearKm = (layer && layer.nearKm) || 2000;
    // No farther than the selected model can be drawn at full size (scene/heroes.js
    // closeUpDistance): at 35 % of the stations layer's nearKm the camera parked 7 000 km from
    // Tiangong, and the altitude cap drew the station smaller than the satellites around it.
    const el = ctx.renderer && ctx.renderer.domElement;
    const h = el && el.clientHeight > 0 ? el.clientHeight : window.innerHeight;
    const f = ctx.camera ? ctx.camera.projectionMatrix.elements[5] : 0;
    const close = pos ? closeUpDistance(pos, h, f) : Infinity;
    return Math.max(0.05, Math.min((nearKm * 0.35) / stage.unitKm, close));
  }

  /**
   * Is this thing on the Earth's own ground, on the Earth's stage: a storm, a rocket still on its
   * pad, a ground site? Within GROUND_SKIN_KM of the surface; a climbing rocket leaves the rule as
   * it leaves the ground, and a site on the Moon is the Moon's (surfaceWorldOf).
   */
  const GROUND_SKIN_KM = 50;
  function onEarthsGround(record, pos) {
    if (!record || !pos || stage.worldId !== 'earth' || record.klass === 'world') return false;
    if (surfaceWorldOf(record)) return false;
    return Math.hypot(pos.x, pos.y, pos.z) * stage.unitKm - 6371 < GROUND_SKIN_KM;
  }
  /** The ground arrival whose card is open, so that leaving it gives the whole Earth back. */
  let groundArrival = null;
  /** And the one that was met from the night side's wide view: the toast says so (select()). */
  let nightArrival = null;

  /**
   * The arrival's distance and tilt with the limb of the world below in the picture (spec 0061 req
   * 9, scene/framing.js says why): for anything over or on a world's ground, from the Earth's
   * satellites to a site on the Moon. Null for everything else -- a world, a star, a probe far from
   * any world -- whose arrival stays as arrivalDistance and camera.js FRAMING_TILT have it.
   * `onWorld` is the world teachRigWorld chose (a site on another world's ground), else the stage's.
   */
  function limbPose(record, pos, distance, onWorld) {
    if (!record || !pos || record.klass === 'world') return null;
    if (['star', 'exoplanet', 'dso', 'exotic'].includes(record.klass)) return null;
    if (ctx.systems && ctx.systems.active) return null; // a star system's stage frames its own way
    const worldId = onWorld || stage.worldId;
    const w = WORLDS.find((x) => x.id === worldId);
    if (!w || !(w.radiusKm > 0)) return null;
    const centre = onWorld ? worlds.drawnPositionOf(onWorld) : { x: 0, y: 0, z: 0 };
    if (!centre) return null;
    const R = onWorld ? worlds.drawnRadiusUnits(onWorld) : w.radiusKm / stage.unitKm;
    const r = Math.hypot(pos.x - centre.x, pos.y - centre.y, pos.z - centre.z);
    if (!(r > 0)) return null;
    // Where the camera's up is, seen from the subject: framing.js keeps the world below on screen.
    const up = camera.up;
    const upLen = Math.hypot(up.x, up.y, up.z) || 1;
    const upDot = ((pos.x - centre.x) * up.x + (pos.y - centre.y) * up.y + (pos.z - centre.z) * up.z) / (r * upLen);
    const el = ctx.renderer && ctx.renderer.domElement;
    const h = el && el.clientHeight > 0 ? el.clientHeight : window.innerHeight;
    const layer = layerRec(record.layer);
    // A model's bounding circle is drawn MODEL_SPAN times SELECTED_PX across: MEASURED 2026-10-02,
    // the ISS's reticle 356 px at 1440x900 and 351 at 390x844, which is the drawn diameter + 12
    // (ui/hud.js reticleBox). A thing with no model is its dot inside the reticle.
    const subjectPx = layer && layer.noModel ? 40 : SELECTED_PX * MODEL_SPAN;
    return limbFraming({ r, R, distance, fovDeg: camera.fov, heightPx: h, subjectPx, upDot, room: freeRoom(el) });
  }

  /**
   * Pixels from where the subject will sit to the nearest chrome above and below it: the phone's
   * sheet or card along the bottom, its top bar. The limb is placed inside them (scene/framing.js).
   * MEASURED 2026-10-02 at 390x844 with the ISS card up: the limb was solved for the whole height,
   * landed 177 px under the station, and that was under the card. Where the subject sits is the
   * middle of the free band, capped, which is the rule scene/viewshift.js moves the picture by;
   * it is asked of the page here because the shift itself is still easing in when a flight starts.
   *
   * AND THE CARD IS STILL SLIDING IN. A deep link selects at boot, and the card's box is measured
   * mid-slide, below the screen: MEASURED with `#at=25544` at 390x844, nothing was found covering
   * the bottom, the limb was put 177 px above a subject that then sat at y = 171, and the station
   * arrived with the night side filling the whole screen. A selection always opens the card, and
   * on a phone the card opens at half (docs/ui-guide.md §3.11, 48 % of the height), so that much is
   * taken as covered whatever the box says this frame.
   */
  // The time pill too (internal #421): 560 px of a desktop's width is no bar by the 80 % rule, and
  // it is 102 px tall under the middle of the scene, where every world's lower limb was. And the
  // phone trip's own top bar.
  const BAND_CHROME = '#sr-side, #sr-card, #sr-top, #sr-time, #sr-trip .sr-trip__top';
  const PHONE_CARD_SHARE = 0.48;
  function freeRoom(el) {
    if (!el || typeof el.getBoundingClientRect !== 'function') return null;
    const c = el.getBoundingClientRect();
    const h = c.height;
    if (!(h > 0)) return null;
    let top = 0;
    let bottom = 0;
    for (const node of document.querySelectorAll(BAND_CHROME)) {
      if (node.hidden) continue;
      const r = node.getBoundingClientRect();
      // A bar or a sheet spans the view; the desktop's sidebar is a column, and covers neither.
      // The pill counts when it lies under the subject's column (scene/viewshift.js pillCovers).
      const isPill = node.id === 'sr-time';
      const mid = c.left + c.width / 2 + viewShift.shiftXPx();
      if (!(r.height > 0 && (r.width >= c.width * 0.8 || (isPill && r.left <= mid && r.right >= mid)))) continue;
      const cs = getComputedStyle(node);
      if (cs.visibility === 'hidden' || cs.display === 'none') continue;
      const t = r.top - c.top;
      const b = r.bottom - c.top + (isPill ? PILL_GAP_PX : 0);
      if (b >= h - 4 && t > 0) bottom = Math.max(bottom, h - t);
      else if (t <= h * 0.25 && b < h * 0.5) top = Math.max(top, b);
    }
    const cap = h * MAX_SHIFT_FRACTION;
    if (c.width < 900) {
      // The card is half the height at least; it may be more, and then the subject rides as high as
      // the shift's cap allows, so the room above is counted from there: the limb is then nearer
      // the subject than planned, never off the top (MEASURED at 390x844 with today's card, 62 %
      // of the height: the subject sat at y = 169, the cap, not at 220).
      bottom = Math.max(bottom, h * PHONE_CARD_SHARE);
      const centre = h / 2 - Math.max(-cap, Math.min(cap, (bottom - top) / 2));
      const highest = h / 2 - cap;
      return { above: Math.max(0, highest - top), below: h - bottom - centre };
    }
    const centre = h / 2 - Math.max(-cap, Math.min(cap, (bottom - top) / 2));
    return { above: centre - top, below: h - bottom - centre };
  }

  /**
   * Is this layer drawn right now? On, and -- for a layer that lives on the ladder's rungs (planets
   * around other stars, deep-sky objects, black holes) -- only when the stage is a rung. From a
   * world stage their true positions are past the far plane and, MEASURED 2026-09-08 in the browser,
   * the glyph shader drew them anyway as a green rash over Earth's sky. The layer stays "on" in the
   * panel; it simply has nothing honest to draw from here, and its records still count and search.
   */
  // A TRIP NO LONGER HIDES THE LAYERS AROUND ITS SUBJECT, and the reason is worth keeping.
  //
  // Ivan reported "stations inside the earth" in the stations tour, and asked for the crowd to be
  // hidden while a stop is zoomed in. That was shipped here on 2026-09-17 (public #125) as: while
  // a trip holds still inside the subject's own layer nearKm, draw only the subject's layer.
  //
  // It was measured against the tour afterwards and it was wrong at both ends. `nearKm` is the
  // distance at which scene/heroes.js starts giving a record REAL GEOMETRY -- 20 000 km for the
  // stations layer, deliberately generous so the model exists before you arrive. It is not a
  // statement about framing. So the rule:
  //   - did not fire at the stop Ivan was describing (`far`, 32 000 km, outside the 20 000);
  //   - did fire at `iss` and `tiangong` (3 000 km), which were never the problem,
  // and the result was the next thing he reported: "i dont see many objects now at all".
  //
  // The stations really were inside the Earth, and not because of the crowd: a selected hero is
  // drawn at 260 px whatever the distance, so at 32 000 km the ISS was 4 308 km in radius and
  // reached most of the way to the planet's core. That is fixed where it is caused, in
  // scene/heroes.js (`heroScale`), and this predicate goes back to answering only what it can
  // answer honestly: is the layer on, and does it have anything true to draw from this stage.
  // On a rung of the ladder the whole Solar System is one pixel. A layer of things inside it stacks
  // every glyph on the Sun: measured 2026-09-22, the probes and oddities drew one purple ringed blob
  // where the Sun should be, and with live data every satellite would sit on that pixel for a tap
  // near the Sun to pick. There only the ladder's own layers draw, and the worlds, which keep the
  // Sun's name; stars3d draws the Sun itself as a star.
  const LADDER_SCALE_LAYERS = new Set(['stars', 'galaxy', 'worlds']);
  // ON A STAR SYSTEM'S STAGE (spec 0040) the only things drawn are the system itself and the stars as
  // a sky of directions from it. The exoplanet glyphs for this system would sit on its star, where
  // scene/systems.js already draws the same records at their true places; every other glyph layer is
  // a Solar System thing forty light-years behind the camera.
  const SYSTEM_SCALE_LAYERS = new Set(['stars', 'systems']);
  function isLayerDrawable(layer) {
    if (!layer || !isLayerOn(layer.id)) return false;
    // From the ground (sky/groundsky.js) a pad or a dish is under the horizon, and the landers
    // drew as dots across the face of the sky view's own Moon: no site is drawn there.
    if (layer.klass === 'site' && ctx.skyView && ctx.skyView.ownsSky) return false;
    // Nor a launch: its record is a drawing of an ascent from a pad that is somewhere else on the
    // globe, and from the ground it hung in the sky as an orange dot with a rocket's name, 20
    // degrees up (internal #393 finding 3, seen with the clock on 12 August 2026).
    if (layer.id === 'launches' && ctx.skyView && ctx.skyView.ownsSky) return false;
    if (isSystemStage(stage.worldId)) return SYSTEM_SCALE_LAYERS.has(layer.id);
    const ladder = isLadderStage(stage.worldId);
    if (layer.ladderOnly && !ladder) return false;
    if (ladder && !layer.ladderOnly && !LADDER_SCALE_LAYERS.has(layer.id)) return false;
    return true;
  }
  ctx.isLayerDrawable = isLayerDrawable;


  /**
   * Make a world the centre of the map (spec 0006 req 5, spec 0028 req 2). The stage's floating
   * origin moves to it, its neighbourhood draws at true scale, and the camera is told the new
   * ground. Offered from the world card; not yet done on every select, because the Now moment's
   * sky view still assumes Earth underfoot.
   */
  /**
   * Colour every dot by a key (spec 0026 req 11): registry/colorkeys.yaml's rows through
   * data/colorkeyrules.js. `class` (or an unknown id) puts the class colours back. Layers that load
   * later pick the key up in setRecords through the same function.
   */
  let colourKeyId = 'class';
  ctx.setColourKey = (id) => {
    colourKeyId = id || 'class';
    const key = colourKeyId === 'class' ? null : keyById(colourKeyId);
    const fn = key ? (record) => { const b = bucketOf(key, record); return b ? b.colour : null; } : null;
    for (const gl of glyphLayers.values()) if (gl.recolour) gl.recolour(fn);
    ctx.colourKeyFn = fn;
  };
  ctx.colourKey = () => colourKeyId;
  // 0077 W3-e: begin (internal i#886: "How we know", the confidence filter in What to show; ui/confidence.js decides what is shown)
  // `fn(record) -> boolean` is handed to every glyph layer (scene/glyphs.js setFilter); `classes` is the visitor's choice, for the row's own paint.
  // The layers are all made up front (below), so there is no later one to catch up. Never stored, never in the link: a view, not a setting.
  let confidenceClasses = null;
  ctx.setConfidence = (classes, fn) => {
    confidenceClasses = Array.isArray(classes) && classes.length ? [...classes] : null;
    for (const gl of glyphLayers.values()) gl.setFilter(confidenceClasses ? fn : null);
  };
  ctx.confidence = () => (confidenceClasses ? [...confidenceClasses] : null);
  // 0077 W3-e: end

  ctx.setStage = (stageId) => {
    if (!STAGES[stageId] || stage.worldId === stageId) return false;
    // A world, or a rung of the ladder (stellar, galaxy, local-group: registry/stages.yaml). A rung
    // has no ground, so the camera's clearance sphere is switched off and it arrives a few units out.
    const w = WORLDS.find((x) => x.id === stageId);
    stage.setWorld(stageId);
    worlds.update(clock.now());
    // A star system's stage (spec 0040): the ground is its star, and the camera arrives with every
    // orbit in the picture. sr:stage (above) has already built the system.
    const system = isSystemStage(stageId) && ctx.systems && ctx.systems.active;
    const r = w ? w.radiusKm / stage.unitKm : system ? ctx.systems.starRadiusUnits() : 0;
    cameraRig.setWorldRadius(r);
    cameraRig.setWorldCentre({ x: 0, y: 0, z: 0 });
    cameraRig.stopFollow();
    const distance = w ? worldFramingDistance(r, camera.fov, camera.aspect) : system ? ctx.systems.framingDistanceUnits(stageId) : 5;
    // The new centre of the map is met on its lit face too (the Sun is the light and has none).
    const lit = w && w.id !== 'sun' ? litOffset(worlds.sunDirOf(w.id), camera.up, undefined, worlds.faceDirOf(w.id)) : null;
    cameraRig.flyTo({ targetScene: { x: 0, y: 0, z: 0 }, distance, offset: lit || undefined, ms: 0 });
    return true;
  };
  // The Planets tab (spec 0061, ui/explore.js): the Sun's stage with every planet in the picture.
  //
  // It was a fixed 700 million km, the year trip's first stop, measured to hold Mars's orbit on a
  // 1280 x 800 screen. MEASURED 2026-10-02 at 1440x900: Jupiter projected to y = -36 (off the top),
  // Saturn to x = 3 088 and Neptune to x = 8 351 -- five of the nine worlds the tab lists were off
  // the screen it opens on, and on a phone's narrow width Mars went too. So the distance is SOLVED:
  // the camera looks down on the Sun from SYSTEM_POLAR off the pole of the planets' plane, keeps the
  // side it is on, and stands back exactly far enough for Neptune's whole path, every planet and
  // Pluto to be inside the part of the screen the chrome leaves (viewshift's band), with a margin
  // (scene/framing.js fitDistance). The paths are drawn here (SYSTEM_RINGS, below), so the fit is to
  // the outermost one, not to where Neptune happens to be. The inner planets are close to the Sun
  // at that size, and that is the honest shape of the Solar System; the labels rank all eight
  // planets first on this stage (ui/labels.js).
  const SYSTEM_IDS = ['mercury', 'venus', 'earth', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto'];
  const OUTER_PATH_SAMPLES = 24;
  /** Points along the outermost drawn path, one lap from now. */
  const outerPath = (tMs) => {
    const period = periodMsOfWorld('neptune');
    const out = [];
    if (!period) return out;
    for (let k = 0; k < OUTER_PATH_SAMPLES; k++) {
      const t = tMs + (period * k) / OUTER_PATH_SAMPLES;
      const p = positionOf('neptune', t);
      const v = p ? stage.toScene(p, p.frame, tMs) : null;
      if (v) out.push(v);
    }
    return out;
  };
  const SYSTEM_POLAR = 0.6; // radians off the pole: the orbits read as ellipses, not as a line
  const _fitCam = new THREE.PerspectiveCamera();
  const _fitQ = new THREE.Quaternion();
  const _fitU = new THREE.Vector3();
  const _fitV = new THREE.Vector3();
  ctx.frameSolarSystem = () => {
    if (stage.worldId !== 'sun') return false;
    const pts = SYSTEM_IDS.map((id) => worlds.drawnPositionOf(id)).filter(Boolean).map((p) => p.clone())
      .concat(outerPath(clock.now()));
    const azimuth = Number.isFinite(cameraRig.state.azimuth) ? cameraRig.state.azimuth : 0;
    // The rig's own offset direction for (azimuth, SYSTEM_POLAR), in its up's basis.
    _fitQ.setFromUnitVectors(new THREE.Vector3(0, 1, 0), camera.up.clone().normalize());
    _fitU.set(Math.sin(SYSTEM_POLAR) * Math.sin(azimuth), Math.cos(SYSTEM_POLAR), Math.sin(SYSTEM_POLAR) * Math.cos(azimuth)).applyQuaternion(_fitQ);
    _fitCam.fov = camera.fov;
    _fitCam.aspect = camera.aspect;
    _fitCam.near = camera.near;
    _fitCam.far = camera.far;
    _fitCam.up.copy(camera.up);
    _fitCam.updateProjectionMatrix();
    const project = (p, d) => {
      _fitCam.position.copy(_fitU).multiplyScalar(d);
      _fitCam.lookAt(0, 0, 0);
      _fitCam.updateMatrixWorld();
      _fitV.copy(p).project(_fitCam);
      return _fitV.z < 1 ? { x: _fitV.x, y: _fitV.y } : null;
    };
    const el = renderer.domElement;
    const vw = el.clientWidth || window.innerWidth;
    const vh = el.clientHeight || window.innerHeight;
    const band = {
      w: Math.max(0.3, (vw - 2 * Math.abs(viewShift.shiftXPx())) / vw),
      h: Math.max(0.3, (vh - 2 * Math.abs(viewShift.shiftPx())) / vh),
    };
    const fit = pts.length ? fitDistance(pts, project, { lo: 50, hi: 2e5, band }) : null;
    const distance = Number.isFinite(fit) ? fit : 700e6 / stage.unitKm;
    cameraRig.flyTo({ targetScene: { x: 0, y: 0, z: 0 }, distance, azimuth, polar: SYSTEM_POLAR, ms: 0 });
    return true;
  };

  function setMoment(next, { silent } = {}) {
    if (!MOMENTS.includes(next)) next = 'wonder';
    moment = next;
    for (const layer of LAYERS) layer.on = !!(layer.moments && layer.moments[moment]);
    for (const [id, gl] of glyphLayers) gl.setVisible(isLayerOn(id));
    // The Now door with nothing set used to do nothing (measured). A guessed place, marked as a
    // guess, opens the dome; the panel says the guess out loud and a real place replaces it.
    if (moment === 'now' && !observer) {
      const guess = guessObserver(CITIES);
      if (guess) ctx.setObserver(guess);
    }
    if (moment === 'now' && observer) enterGround();
    else if (ctx.skyView.active) ctx.skyView.exit();
    if (!silent) writeMoment(moment);
    window.dispatchEvent(new CustomEvent('sr:moment', { detail: moment }));
  }

  /**
   * THE GROUND IS THE EARTH'S, SO ITS STAGE IS (Ivan, 2026-10-14: "sometimes when an object is
   * selected it is shaking on the screen"). "See it" on a planet's card opened the sky from the
   * Sun's stage, where a unit is a million kilometres and the Earth is 150 units from the origin.
   * The eye is then 150 units out and a satellite a few thousandths of a unit from it, and the dots
   * are float32 on the GPU: one step at 150 is 15 km, so a geostationary dot sat up to 15 km from
   * where the brackets (float64) put it, a different 15 km every tick as the Earth moved along its
   * orbit. Through binoculars' field that is a few pixels, through a telescope's tens. On the
   * Earth's own stage the eye is 6.4 units from the origin and a step is half a metre.
   */
  // Asked of the page and not of inTrip(): setMoment runs once at boot, before that const is set
  // (a declaration, so it is there for that call too).
  function tripOnScreen() { return document.documentElement.classList.contains('sr-trip-mode'); }
  function enterGround() {
    // A trip's own ground stops go in by the sky view's door, on the stage the trip chose.
    if (stage.worldId !== 'earth' && typeof ctx.setStage === 'function' && !tripOnScreen()) ctx.setStage('earth');
    return ctx.skyView.enter(observer);
  }

  /**
   * "Fly to it", "Follow" and "Ride along" pressed from the ground (Ivan, 2026-10-14: they did
   * nothing there). They did fly: the rig flew, and the sky view put the camera back on the ground
   * every frame. The honest thing is what the words say, so the ground is left first, through the
   * moment's own door (the tab follows), and one line says so. False when there was no ground to leave.
   */
  function leaveGround() {
    if (!ctx.skyView || !ctx.skyView.active || tripOnScreen()) return false;
    setMoment('wonder');
    toast(COPY.sky.leftGround, 4000);
    return true;
  }

  window.addEventListener('sr:observer', () => {
    if (moment === 'now' && observer) enterGround();
  });
  window.addEventListener('hashchange', () => {
    setMoment(readMomentFromHash(), { silent: true });
    // THE WHOLE VIEW, not only at boot and not only `at` (public #331): Back, Forward or an address
    // pasted over this one is a link opened in a tab that is already running, and the clock, the
    // stage, the trip and its stop are as much the view as what is selected. ui/urlstate.js
    // linkChange decides; this carries it out. The app's own writes use replaceState, which fires
    // no hashchange, so this cannot echo.
    const keys = readUrlKeys();
    // `#ambient=…` pasted over a running page starts the reel; taken out of it, stops it.
    const reel = !!(keys.ambient && keys.ambient !== '0');
    if (reel || (ctx.autopilot && ctx.autopilot.engaged)) { ctx.wantAutopilot(keys); if (reel) return; }
    // `#drift=…` pasted over a running page starts the drift; taken out of it, stops it.
    const wander = !!(keys.drift && keys.drift !== '0');
    if (wander || (ctx.drift && ctx.drift.active)) { ctx.wantDrift(keys); if (wander) return; }
    // 0079 C1: begin
    // `#moment=<id>&t=<s>` pasted over a running page opens that moment at that second; taken out of it, closes it.
    const replay = !!(keys.moment && keys.moment !== '0');
    if (replay || (ctx.replay && ctx.replay.active)) { ctx.wantMoment(keys.moment, keys.t); if (replay) return; }
    // 0079 C1: end
    // `#imagine=N` pasted over a running page opens that world; taken out of it, closes it.
    const world = !!(keys.imagine && keys.imagine !== '0');
    if (world || (ctx.imagine && ctx.imagine.active)) { ctx.wantImagine(keys.imagine); if (world) return; }
    const current = typeof ctx.selected === 'function' ? ctx.selected() : null;
    const running = ctx.trip && ctx.trip.state && ctx.trip.state.phase !== 'idle' ? ctx.trip.state : null;
    const plan = linkChange(keys, { at: current ? current.id : null, trip: running ? running.tourId : null, live: clock.mode === 'live', stage: stage.worldId });
    if (!plan) return;
    if (plan.clock && plan.clock.live) clock.live();
    else if (plan.clock) { clock.goTo(plan.clock.goTo); if (clock.rates().includes(plan.clock.rate)) clock.setRate(plan.clock.rate); }
    if (plan.stage && STAGES[plan.stage]) { if (stage.worldId !== plan.stage) ctx.setStage(plan.stage); } else if (plan.stage) linkNote(ctx, COPY.link.unknownStage, ['stage']);
    if (plan.trip && plan.trip.start) { openTrip(ctx, keys); return; }
    if (plan.trip && plan.trip.jump) {
      const index = stopIndex(ctx.trip.tours().find((x) => x.id === running.tourId), plan.trip.jump);
      if (index >= 0 && index !== running.index) ctx.trip.jumpTo(index);
      return;
    }
    if (plan.trip) ctx.trip.stop();
    // A mission's event (ui/missions.js): it selects its own record and sets the clock.
    if (plan.event) { ctx.wantMissions().then((m) => (m ? m.openEventWhenReady(ctx, plan.event) : false)).then((ok) => { if (!ok) linkNote(ctx, COPY.mission.unknown, ['event']); }); return; }
    if (plan.at && plan.at.open) openAt(ctx, plan.at.open);
    else if (plan.at) ctx.deselect();
    if (keys.cam) applyCam(ctx, keys.cam);
  });

  // THE URL IS THE STATE (spec 0017's rule, spec 0032's keys). Two more writers beside the moment,
  // both through ui/urlstate.js so the format has one owner; the trip writes its own keys from
  // ui/trip.js. replaceState throughout: nothing here adds a history entry.
  //
  // `at` is what is selected (req 4). Not the trip's own select of a stop's subject: `trip` and
  // `stop` already say where, and `at` on top would name the same thing twice and outlive it.
  window.addEventListener('sr:select', (e) => {
    const record = e && e.detail;
    if (!record) { clearUrlState(['at']); return; }
    if (ctx.trip && ctx.trip.currentRecordId() === record.id) return;
    writeUrlState({ at: record.id });
  });
  // `stage` is the map's centre when it is not the Earth's (internal #485): written on every
  // stage change, cleared on the Earth, so an address without it can be read as the Earth's map
  // (ui/urlstate.js linkChange). Not during a trip: the trip owns its stages, as it owns `at`.
  window.addEventListener('sr:stage', (e) => {
    if (ctx.trip && ctx.trip.state && ctx.trip.state.phase !== 'idle') return;
    const id = e && e.detail ? e.detail.worldId : stage.worldId;
    if (id === 'earth') clearUrlState(['stage']); else writeUrlState({ stage: id });
  });
  // `t` and `rate`, only when the clock is not live (req 5): a link never carries `t=now`, and
  // `t` absent means now. Trailing-edge throttle at one write a second, because a scrub is a
  // goTo() per pointer event and replaceState a hundred times a second is what browsers rate-limit.
  let clockWroteAt = -Infinity;
  let clockWriteTimer = 0;
  const writeClock = () => {
    clockWriteTimer = 0;
    clockWroteAt = performance.now();
    if (ctx.replay && ctx.replay.active) return; // 0079 C1: `t` is the moment's mission second while one is up
    if (clock.mode === 'live') { clearUrlState(['t', 'rate']); return; }
    const ms = clock.now();
    if (!Number.isFinite(ms)) return;
    writeUrlState({
      t: new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z'),
      rate: clock.rate === 1 ? null : String(clock.rate),
    });
  };
  clock.onChange(() => {
    if (clockWriteTimer) return;
    const wait = 1000 - (performance.now() - clockWroteAt);
    if (wait <= 0) writeClock();
    else clockWriteTimer = setTimeout(writeClock, wait);
  });

  // The one global worth having: it makes the app inspectable from a console on a real phone,
  // which is the only debugger available on the device that matters.
  window.spaceRadar = ctx;
  return ctx;
}

// --- the device tier ---------------------------------------------------------

/** A world's disc, as a share of half the view's height, at which its map tiles' module is fetched. */
const PLANET_TILES_AT = 0.5;
/**
 * The Earth's own mark (2026-10-06): its disc 1.2 times half the view's height, which at this camera's
 * 45 degrees is about 6 400 km up -- outside where its tiles start (registry/tilesets.yaml: about
 * 4 200 km at 900 pixels) and far inside where a first visit stands, so a boot never crosses it.
 */
const EARTH_TILES_AT = 1.2;
/**
 * The Sun's disc, as a share of half the view's height, at which scene/sun.js is fetched (its own
 * SUN_DETAIL_AT; written here too because importing the number would import the module). From the
 * Earth the Sun is 0.011 of half the view: a first visit is five times too far.
 */
const SUN_DETAIL_AT = 0.06;

function createQuality(ctx, renderer, starfield, worlds) {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const mm = (q) => !!(window.matchMedia && window.matchMedia(q).matches);
  const scr = typeof screen !== 'undefined' ? screen : {};
  const pick = chooseTier({
    maxTextureSize: renderer.capabilities && renderer.capabilities.maxTextureSize,
    deviceMemory: nav.deviceMemory,
    hardwareConcurrency: nav.hardwareConcurrency,
    connection: nav.connection,
    // A touch screen with no mouse or trackpad anywhere: a laptop with a touch screen is not a phone.
    coarsePointer: mm('(pointer: coarse)') && !mm('(any-pointer: fine)'),
    screenW: scr.width,
    screenH: scr.height,
  });
  // `?tier=0|1|2` pins the tier (2026-09-29): to compare the tiers on one machine, and for the probes,
  // whose headless SwiftShader would otherwise always read as a T1 laptop. A query key, not the hash:
  // it is not view state and ui/urlstate.js never sees it.
  const forced = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('tier') : null;
  if (forced !== null && /^[012]$/.test(forced)) {
    pick.tier = Number(forced);
    pick.ceiling = pick.tier;
    pick.reasons = ['pinned by ?tier=' + forced];
  }
  // THE VISITOR'S OWN CHOICE (spec 0073 requirement 6; scene/quality.js qualityPlan): Auto, Low, Medium,
  // High, Ultra, remembered in this browser and READ HERE, before the tier is acted on, so a visit
  // that chose Low never fetches or draws a frame of anything higher. Auto is the pick above, as it
  // always was. `autoTier` is what Auto wears: the pick, or the pick after its one promotion.
  let autoTier = pick.tier;
  let autoPromoted = false;
  let plan = qualityPlan(readQualityChoice(), { tier: autoTier, ceiling: pick.ceiling }, false);
  // A manual choice is not promoted: its promoter's ceiling is its own tier.
  const promoterFor = (p) => createTierPromoter({ tier: p.tier, ceiling: p.promote && !autoPromoted ? pick.ceiling : p.tier });
  let promoter = promoterFor(plan);
  const aniso = renderer.capabilities && renderer.capabilities.getMaxAnisotropy ? renderer.capabilities.getMaxAnisotropy() : 8;
  const loader = new THREE.TextureLoader();
  const earthMesh = () => worlds.meshFor('earth');
  const deviceOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;
  const tiers = createTextureTiers({
    tier: plan.tier,
    offline: deviceOffline,
    month: () => new Date(clock.now()).getUTCMonth() + 1,
    // decode() before the texture is handed over: Chrome otherwise decodes a 4k image on the main
    // thread inside the upload, in the middle of a frame.
    load: (url, file) => loader.loadAsync(url).then((tex) => {
      const img = tex.image;
      return img && typeof img.decode === 'function' ? img.decode().then(() => tex, () => tex) : tex;
    }).then((tex) => {
      // One grey channel (the water mask, the night lights) goes up as R8: a quarter of RGBA's GPU
      // memory. Its bytes stay as encoded; earth.js decodes the night map itself.
      if (file.format === 'mono') {
        tex.format = THREE.RedFormat;
        tex.colorSpace = THREE.NoColorSpace;
      } else {
        tex.colorSpace = THREE.SRGBColorSpace;
      }
      tex.anisotropy = aniso;
      tex.needsUpdate = true;
      return tex;
    }),
    targets: {
      earth: {
        ready: () => !!earthMesh() && earthMapsSettled(earthMesh()),
        set: (slot, tex, file) => setEarthMap(earthMesh(), slot, tex, { mono: !!(file && file.format === 'mono'), px: file ? file.px : null }),
      },
      sky: {
        ready: () => !!(starfield.state && starfield.state.milkyway) && !(ctx.latch && ctx.latch.latched),
        set: (tex) => starfield.setMilkyWayMap(tex),
      },
      worlds: {
        ready: (id) => worlds.hasMap(id),
        set: (id, tex) => worlds.setTierMap(id, tex),
        // Device pixels of radius: the share of half the view's height, times half the drawing buffer.
        px: (id) => worlds.discShare(id) * (renderer.domElement ? renderer.domElement.height / 2 : 400),
        selected: () => {
          const s = ctx.selected();
          return s && s.klass === 'world' ? s.id : null;
        },
      },
    },
  });
  // How many worlds may keep a map of their own on this tier (scene/worlds.js MAPS_HELD, internal #157).
  const holdFor = (tier) => worlds.setMapsHeld(MAPS_HELD[Math.min(Math.max(0, tier), MAPS_HELD.length - 1)]);
  // Mercury's relief (scene/worlds.js THE RELIEF): tier 1 and up, and never under the frame latch.
  // The Earth's shell is marched in more steps on a higher tier, and in its fewest for good under the latch.
  // `plan.stripped` is the latch's removals: the latch itself, or Low chosen (which can be left again:
  // `release`). `top`: Ultra marches the shell in the most steps its shader takes.
  // The Earth's clouds stand up (scene/earth.js CLOUD_PARALLAX) on the same permission, and not on a connection that saves data.
  const cloudsSaveData = shouldSaveData(nav.connection);
  const reliefFor = (tier) => { worlds.setReliefTier(tier); worlds.setRelief(tier >= 1 && !plan.stripped); setEarthAirQuality(earthMesh(), tier, plan.stripped, { top: plan.steps === 'top', release: !tiers.latched }); setEarthCloudHeight(earthMesh(), { tier, off: plan.stripped || cloudsSaveData }); };
  holdFor(plan.tier);
  reliefFor(plan.tier);
  const say = () => window.dispatchEvent(new CustomEvent('sr:tier', { detail: api.describe() }));
  // Spec 0065: a close world drawn from the missions' own map tiles. OFF THE FIRST VISIT, like the
  // aurora: scene/tiles.js and its two helpers are imported only once a world is PLANET_TILES_AT of
  // half the view tall (the Earth, where every visit starts: EARTH_TILES_AT) -- nobody boots there -- and never at tier 0, on a
  // connection that saves data, or after the latch. Until then this costs one loop a second.
  let planetTiles = null;
  let planetTilesAsked = false;
  // Read ONCE, as the tier was: Chromium's effectiveType is a running estimate, and a headless boot
  // that began on "4g" read "3g" twenty seconds later (measured 2026-10-03). A layer that came and
  // went with the estimate would be the flapping the latch exists to prevent.
  const tilesSaveData = shouldSaveData(nav.connection);
  function planetTilesWanted() {
    if (planetTilesAsked || tilesSaveData || plan.stripped || tiers.tier < 1) return;
    // OFFLINE (internal #564, the integration pass of 2026-10-10): a kept trip played with no network asked
    // NASA's Moon mosaic for six tiles that could only fail. Asked again by the next tick once the network is back.
    if (deviceOffline()) return;
    if (!worlds.ids().some((id) => id !== 'sun' && worlds.discShare(id) >= (id === 'earth' ? EARTH_TILES_AT : PLANET_TILES_AT))) return;
    planetTilesAsked = true;
    import('./scene/tiles.js').then((m) => {
      planetTiles = m.createPlanetTiles({
        worlds,
        camera: ctx.camera,
        viewport: () => (renderer.domElement ? renderer.domElement.height : 800),
        tier: tiers.tier,
        anisotropy: aniso,
        onChange: say, // the Sources panel prints the mosaic's credit while its tiles are on screen
      });
      if (tiers.latched) planetTiles.latch();
      else planetTiles.setQuality(tiers.tier, plan.stripped); // the choice may have changed while the module was on its way
    }).catch(() => { /* the globe's own map is what is on screen; nothing else depends on this */ });
  }
  // The Sun as a star (2026-10-06, spec 0055 task 3, scene/sun.js): limb darkening, grain, today's
  // sunspot groups and a corona, OFF THE FIRST VISIT like the tiles -- the module is fetched the first
  // time the Sun's disc is SUN_DETAIL_AT of half the view, and NOAA's list of today's groups once,
  // then, unless the connection asked to save data. Under the latch the flat disc comes back.
  let sunAsked = false;
  // The Sun's detail reads its tier when it is made (its shader, how many prominences it holds), so a
  // changed quality makes it again (remakeSun below). The three lists are read once and kept here.
  let sunMade = 0;
  const sunLists = { regions: null, flares: null, cmes: null, asked: false };
  function sunWanted() {
    if (sunAsked || plan.stripped || worlds.discShare('sun') < SUN_DETAIL_AT) return;
    sunAsked = true;
    const made = sunMade;
    import('./scene/sun.js').then((m) => {
      if (made !== sunMade || plan.stripped) { sunAsked = false; return; } // the quality changed on the way
      const detail = m.createSunDetail({ mesh: worlds.meshFor('sun'), camera: ctx.camera, tier: tiers.tier });
      if (!detail) return;
      if (tiers.latched) detail.latch();
      ctx.sunDetail = detail;
      if (sunLists.regions) detail.setRegions(sunLists.regions);
      if (sunLists.flares) detail.setFlares(sunLists.flares);
      if (sunLists.cmes) detail.setCmes(sunLists.cmes);
      // The lists are asked for once a visit, whoever is drawn when they land: `ctx.sunDetail`, not
      // `detail`, so one that arrives after the quality changed goes to the Sun drawn now.
      if (sunLists.asked) return;
      sunLists.asked = true;
      const now = () => ctx.sunDetail;
      if (tilesSaveData || tiers.latched) return;
      import('./data/sunregions.js').then((r) => r.readSunRegions() // our saved copy first (internal #525)
        .then((json) => {
          const list = r.parseSunRegions(json);
          sunLists.regions = list;
          if (now()) now().setRegions(list);
          ctx.sunRegions = list;
          say(); // the Sun's card and the Sources panel read it
        })).catch(() => { /* a Sun with no spots drawn is what the card then describes */ });
      // What the Sun is doing (spec 0073 requirement 2, data/sunactivity.js): NOAA's flares and NASA's
      // coronal mass ejections, each read once, each on its own: a list that cannot be read draws nothing.
      import('./data/sunactivity.js').then((a) => {
        a.readSunEvents().then((json) => { sunLists.flares = a.parseFlares(json); if (now()) now().setFlares(sunLists.flares); say(); }).catch(() => { /* no flare is drawn */ });
        a.readCmes().then((json) => { sunLists.cmes = a.parseCmes(json); if (now()) now().setCmes(sunLists.cmes); say(); }).catch(() => { /* no CME is drawn */ });
      }).catch(() => { /* the Sun without its flares is what the card then describes */ });
    }).catch(() => { sunAsked = false; /* the flat disc is what is on screen; ask again later */ });
  }
  function remakeSun() {
    sunMade += 1;
    if (ctx.sunDetail) { ctx.sunDetail.dispose(); ctx.sunDetail = null; }
    sunAsked = false;
  }
  /**
   * A CHOSEN QUALITY, APPLIED WITH NO RELOAD. Everything that reads the tier follows here:
   *   at once     the maps (up: fetched when idle; down: given back), how many worlds hold one, relief,
   *               the Earth's air steps, the map tiles, the weather shader, the aurora's steps and the
   *               models' light (both ask every frame), the pixel ratio and the sky (ctx.applyQuality)
   *   made again  the Sun's detail (its shader and pools are sized when it is made)
   *   next time   the wind's and the currents' streak counts (read when the layer is switched on), a
   *               star system's faces (when it is next entered) and how many nebula pictures are kept
   *               (when that module first loads)
   * The real latch is not here: after it, qualityPlan answers Low whatever is asked.
   */
  function apply(next) {
    const was = plan;
    plan = next;
    promoter = promoterFor(next);
    tiers.setTier(next.tier);
    holdFor(next.tier);
    reliefFor(next.tier);
    if (planetTiles && !tiers.latched) planetTiles.setQuality(next.tier, next.stripped);
    if (was.tier !== next.tier || was.stripped !== next.stripped) remakeSun();
    if (ctx.dsoGlow) ctx.dsoGlow.setMarks(!next.stripped);
    if (ctx.weather && ctx.weather.setStripped && !tiers.latched) ctx.weather.setStripped(next.stripped);
    if (!next.stripped && ctx.wantWeather) ctx.wantWeather();
    if (ctx.applyQuality) ctx.applyQuality(next);
    say();
  }
  const api = {
    get tier() { return tiers.tier; },
    // The tier the visit began at: the stored choice's, so a visit that chose Low boots as tier 0.
    bootTier: plan.tier,
    ceiling: pick.ceiling,
    reasons: pick.reasons,
    get promoted() { return plan.promote && autoPromoted; },
    /** The setting: what is chosen, what this device is offered, and what the choice means now (qualityPlan). */
    get choice() { return plan.choice; },
    get ultra() { return plan.ultra; },
    get stripped() { return plan.stripped; },
    offered: () => offeredQualities(pick.ceiling, tiers.latched),
    plan: () => ({ ...plan }),
    /** From the row in What to show. Stored unless `how.keep` is false; returns the choice now on. */
    setChoice(next, how = {}) {
      const want = qualityPlan(next, { tier: autoTier, ceiling: pick.ceiling }, tiers.latched);
      if (how.keep !== false) writeQualityChoice(undefined, want.choice);
      if (want.choice !== plan.choice) apply(want);
      return plan.choice;
    },
    /**
     * Slow frames under Ultra (the loop's guard; the governor is held there, so the latch is not fed):
     * back to Auto before anything is taken off for good. True when the visitor is to be told.
     */
    fellBack() {
      const after = qualityAfterLatch(plan.choice);
      if (after.choice !== plan.choice) api.setChoice(after.choice);
      return after.told;
    },
    textures: () => tiers.state(),
    tiles: () => (planetTiles ? planetTiles.state() : null),
    /** The mosaics on screen now, for a world's card (ui/cards.js): scene/tiles.js mosaics(). */
    mosaics: () => (planetTiles ? planetTiles.mosaics() : []),
    credits: () => tiers.credits().concat(planetTiles ? planetTiles.credits() : [],
      ctx.sunDetail && ctx.sunDetail.state().spots > 0 ? [COPY.sun.regionsCredit] : [],
      ctx.sunDetail && ctx.sunDetail.state().flareList > 0 ? [COPY.sun.flaresCredit] : [],
      ctx.sunDetail && ctx.sunDetail.state().cmeList > 0 ? [COPY.sun.cmeCredit] : []),
    describe: () => ({ tier: tiers.tier, bootTier: api.bootTier, promoted: api.promoted, latched: tiers.latched, reasons: pick.reasons, choice: plan.choice, plan: { ...plan }, offered: api.offered() }),
    /** After the first frame (startLoop). */
    start() { tiers.start(); say(); },
    /** Every frame, from startLoop, after the latch has been fed. */
    frame(frameMs, nowMs, latched) {
      const up = promoter.push(frameMs, nowMs, latched);
      if (up !== null) { autoTier = up; autoPromoted = true; plan = { ...plan, tier: up }; tiers.setTier(up); holdFor(up); reliefFor(up); if (planetTiles) planetTiles.setTier(up); say(); }
      if (planetTiles) planetTiles.frame(nowMs);
    },
    tick(nowMs) { tiers.tick(nowMs); planetTilesWanted(); sunWanted(); },
    /**
     * The frame latch tripped: back to the boot maps, for good. A manual choice above Low goes back
     * to Auto (qualityAfterLatch), stored so; returns true when the visitor is to be told so.
     */
    latch() {
      tiers.latch(); holdFor(0);
      const after = qualityAfterLatch(plan.choice);
      if (after.choice !== plan.choice) writeQualityChoice(undefined, after.choice);
      plan = qualityPlan(after.choice, { tier: autoTier, ceiling: pick.ceiling }, true);
      worlds.setRelief(false); setEarthAirQuality(earthMesh(), 0, true); setEarthCloudHeight(earthMesh(), { off: true }); if (ctx.dsoGlow) ctx.dsoGlow.setMarks(false); if (planetTiles) planetTiles.latch(); if (ctx.sunDetail) ctx.sunDetail.latch(); say();
      return after.told;
    },
    /**
     * What the maps hold on the GPU now, by arithmetic from registry/textures.yaml (gpuMiB: pixels,
     * four bytes, a third for mipmaps), beside the renderer's own count of textures. `window.spaceRadar.gpu()`.
     * An estimate of the maps only: models, star buffers and the map tiles of a close world are not in it.
     */
    gpu() {
      const applied = new Map(tiers.state().applied.map((a) => [a.id, a.tier]));
      const byRow = {};
      let mib = 0;
      const add = (id, file) => { const m = gpuMiB(file); if (m > 0) { byRow[id] = Math.round(m * 10) / 10; mib += m; } };
      const held = new Set(worlds.mapsHeld());
      for (const row of TEXTURES) {
        const onScreen = row.world === 'earth' || row.world === 'sky' || row.id === 'saturn-ring';
        if (applied.has(row.id)) add(row.id, variantFor(row, applied.get(row.id)));
        else if (onScreen ? !!variantFor(row, 0) : row.when !== 'asked' && held.has(row.world)) add(row.id, variantFor(row, 0));
      }
      if (ctx.liveClouds && ctx.liveClouds.state && ctx.liveClouds.state().mode !== 'illustrative') { byRow['live-clouds'] = Math.round(LIVE_CLOUDS_MIB * 10) / 10; mib += LIVE_CLOUDS_MIB; }
      return {
        tier: tiers.tier, mapsMiB: Math.round(mib * 10) / 10, byRow,
        worldMaps: [...held], worldMapsMax: MAPS_HELD[Math.min(tiers.tier, MAPS_HELD.length - 1)],
        textures: renderer.info && renderer.info.memory ? renderer.info.memory.textures : null,
      };
    },
  };
  ctx.gpu = () => api.gpu();
  return api;
}

// --- the loop ----------------------------------------------------------------

/**
 * The paths the Sun's stage draws outside a trip: the planets'. Not Pluto's: it runs out to 49 AU,
 * past the frame the whole of Neptune's path is fitted to, and was cut off by the screen's edge.
 * Pluto keeps its name where it is.
 */
const SYSTEM_RINGS = ['mercury', 'venus', 'earth', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune'];

function startLoop({ ctx, resize, render, worlds, glyphLayers, cameraRig, starfield, heroes, lod }) {
  let last = performance.now();
  let picturedAt = 0;
  let picturedKey = '';
  let sinceLayerUpdate = 0;
  // The frame-rate latch (spec 0026 req 18): twenty-frame median over 33 ms for three seconds ->
  // one device pixel per CSS pixel and no Milky Way picture, once, said in the panel.
  const latch = createFrameLatch();
  // `?look=1` (scene/quality.js lookHeld), read once: a tool is looking, and neither the latch nor the
  // governor below is fed. Nothing else asks: the loaders, the tier and the clock run as for a visitor.
  const looking = lookHeld({ search: location.search });
  // The step before it (internal #521): 2, 1.5, 1.25, 1 of the device's pixel ratio; the latch is fed
  // only from the lowest step, and stays the last resort.
  const scaler = createScaleGovernor({ deviceRatio: typeof devicePixelRatio === 'number' ? devicePixelRatio : 1 });
  ctx.scaler = scaler;
  // Read every frame by the aurora's folds; one query object, not a matchMedia call a frame.
  const reducedMotionQuery = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  let sunDoing = ''; // scene/sun.js activity(): what of the Sun's activity is drawn, as last told to the card
  // THE QUALITY SETTING'S HALF OF THE LOOP (createQuality's apply calls it; once here for a stored choice).
  // Low puts on what degrade() below puts on, by the same calls, and can take it off again; Ultra holds
  // the pixel ratio at the top of its ladder and, because the latch is then not fed, runs the latch's
  // own test in a guard of its own. After the real latch nothing here does anything.
  let lowChosen = false;
  let ultraGuard = null;
  const sayFellBack = () => toast(COPY.quality.fellBack, 6000);
  ctx.applyQuality = (plan) => {
    if (latch.latched) return;
    lowChosen = !!plan.stripped;
    if (ctx.rendererApi && ctx.rendererApi.setQuality) ctx.rendererApi.setQuality(lowChosen ? 'low' : 'full');
    if (starfield && starfield.setDetail) starfield.setDetail(lowChosen ? 'low' : 'full');
    if (worlds && worlds.setLatched) worlds.setLatched(lowChosen);
    const held = plan.scale === 'held';
    if (held !== scaler.held) {
      scaler.hold(held);
      if (ctx.rendererApi && ctx.rendererApi.setScale) ctx.rendererApi.setScale(scaler.scale);
      window.dispatchEvent(new CustomEvent('sr:scale', { detail: { ratio: scaler.scale, step: scaler.step } }));
    }
    ultraGuard = held ? createFrameLatch() : null;
  };
  function degrade() {
    ultraGuard = null;
    scaler.freeze();
    if (ctx.renderer && ctx.rendererApi && ctx.rendererApi.setQuality) ctx.rendererApi.setQuality('low');
    if (starfield && starfield.setDetail) starfield.setDetail('low');
    // Spec 0054: earthshine goes off with the latch (scene/worlds.js setLatched).
    if (worlds && worlds.setLatched) worlds.setLatched(true);
    // The tier falls with the latch and only with it: every world back on its boot map.
    if (ctx.quality && ctx.quality.latch()) sayFellBack();
    window.dispatchEvent(new CustomEvent('sr:quality', { detail: { level: 'low', medianMs: Math.round(latch.median()) } }));
  }
  // Read by ui/trip.js, which keeps the star-stretch at 0 on a latched device (spec 0034 req 6),
  // and forced from a console or a browser check the way a slow device would trip it.
  // `latched` is true under the setting's Low too, because Low is the latch's removals: every reader
  // (the star stretch, a star system's faces, the nebula pictures) then does what it does under the
  // latch. `tripped` is the latch alone.
  ctx.latch = {
    get latched() { return latch.latched || lowChosen; },
    get tripped() { return latch.latched; },
    held: looking,
    force() { if (latch.force()) degrade(); return latch.latched; },
  };
  // Read once: every extra hero model is a file to fetch, so a metered or slow connection keeps
  // the model pool at its floor (scene/heroes.js, nextHeroCap).
  const saveData = typeof navigator !== 'undefined' && shouldSaveData(navigator.connection);
  // Spec 0037: the eclipse shaders. They were gated on the frame latch as the spec asked, and on
  // 2026-09-23 the live "Chasing the solar eclipse" trip said "The shadow is not drawn on this
  // device" -- on exactly the slow phones the latch trips on, which is a trip about a shadow with
  // no shadow. The cost does not justify it: #228 measured no difference above noise with the
  // shadow on or off (SwiftShader, Earth filling the screen), and the branch runs only while the
  // Moon is within 1.7 degrees of the Sun from the Earth, i.e. only at an eclipse. So the shadow
  // draws whatever the latch says. ctx.eclipseOverride (true / false / null) stays for probes and
  // tests; nothing in the app sets it, and the "not drawn here" line is reached only through it.
  ctx.latched = () => latch.latched || lowChosen;
  ctx.eclipseOverride = null;
  ctx.eclipseDrawn = () => ctx.eclipseOverride !== false;
  // A stored Low or Ultra is worn from the first frame. Auto, Medium and High ask nothing of the loop.
  if (ctx.quality && (ctx.quality.stripped || ctx.quality.ultra)) ctx.applyQuality(ctx.quality.plan());

  // THE IDLE FRAME RATE (internal #520; scene/quality.js has the whole rule). With nothing moving
  // the loop draws twenty frames a second instead of every one. What "moving" means is ONE list,
  // movingReasons(), filled at the end of each drawn frame below; what the loop cannot see coming
  // (a hand on the page, a resize, a file that has just arrived) wakes it through these listeners.
  const idle = createIdleGate({ enabled: idleCapWanted({ search: location.search, webdriver: navigator.webdriver === true, film: !!ctx.renderMode }) });
  ctx.idle = idle;
  {
    const wake = () => idle.wake(performance.now());
    for (const type of ['pointerdown', 'pointermove', 'pointerup', 'wheel', 'keydown', 'keyup', 'touchstart', 'touchmove', 'resize', 'hashchange', 'focus', 'sr:quality', 'sr:layers-ready', 'sr:opening-end']) {
      window.addEventListener(type, wake, { passive: true, capture: true });
    }
    document.addEventListener('visibilitychange', wake);
  }
  // The camera as the last drawn frame left it, and what the GPU held then: both are compared, not
  // announced, so a flight started from anywhere and a map landing from anywhere are both seen.
  const seenCamera = new Float64Array(18);
  let seenGpu = -1;
  let cameraMovedLast = false;  // the scale governor steps up only while the camera is still
  let wasCapped = false;
  let freeFrameMs = 16;
  function cameraMoved() {
    const cam = ctx.camera;
    const m = cam.matrixWorld.elements;
    const p = cam.projectionMatrix.elements;
    let moved = false;
    for (let i = 0; i < 16; i++) if (seenCamera[i] !== m[i]) { seenCamera[i] = m[i]; moved = true; }
    if (seenCamera[16] !== p[0]) { seenCamera[16] = p[0]; moved = true; }
    if (seenCamera[17] !== p[5]) { seenCamera[17] = p[5]; moved = true; }
    return moved;
  }
  // A layer that moves by itself, and is doing so NOW. Not "the layer is switched on": the aurora
  // and the lightning are on for every first visit, and a rule that read the switch kept the loop
  // at every frame for good (MEASURED in CI on 2026-10-09: the reason at rest was `layer`, always).
  //   - the aurora moves while it is DRAWN with its folds (a live forecast, the layer on, the
  //     Earth big enough on screen): with no forecast, or the Earth a dot, there is nothing to move;
  //   - lightning moves while a map with strikes in it is held: a flash is a third of a second of
  //     flicker and must have every frame. A phone and a data-saving connection hold none;
  //   SO THE CAP IS RARE TODAY on a first visit that has a forecast: the oval's folds keep every
  //   frame. Whether the folds may run at twenty frames a second at rest is a question for an eye,
  //   not for this file (the internal issue beside #520); until it is answered they get all sixty.
  //   - the wind, the ocean currents and a data overlay, once asked for, are taken to be moving.
  // (The pulsars' pulses are drawn on the ladder's stages only, which `stage` already answers.)
  // Asked four times a second: the aurora's state() builds an object.
  let layerMoving = true;
  let layerAskedAt = -Infinity;
  function layerAnimating(nowMs) {
    if (nowMs - layerAskedAt < 250) return layerMoving;
    layerAskedAt = nowMs;
    const aurora = ctx.aurora && typeof ctx.aurora.state === 'function' ? ctx.aurora.state() : null;
    const flashes = ctx.isLayerOn('lightning') && ctx.weather && typeof ctx.weather.perMinute === 'function' ? ctx.weather.perMinute() : 0;
    layerMoving = !!(aurora && aurora.visible && aurora.folds) || flashes > 0 || !!ctx.wind || !!ctx.currents || !!ctx.earthOverlay;
    return layerMoving;
  }
  function gpuChanged() {
    const info = ctx.renderer && ctx.renderer.info && ctx.renderer.info.memory;
    const now = info ? info.textures * 100000 + info.geometries : 0;
    const changed = now !== seenGpu;
    seenGpu = now;
    return changed;
  }

  function frame(nowReal) {
    requestAnimationFrame(frame);
    if (idle.skip(nowReal)) return;
    // This frame's length is ours by choice while the cap is on (and on the frame that ends it):
    // the latch and the tier promoter are fed the device's own frames only.
    const capped = idle.capped(nowReal) || wasCapped;
    wasCapped = idle.capped(nowReal);
    // Never negative. requestAnimationFrame stamps a frame with the time it BEGAN, which can be
    // earlier than the performance.now() `last` was set from -- and a negative first step was added
    // to the 10 Hz accumulator below, holding the glyph and label updates back until real time paid
    // it off: over a minute in headless Chrome (found 2026-09-22 by the famous-stars work), and the
    // "glyph layers read zero for ~20 s after boot" that earlier probes put down to a race. It also
    // nudged the clock backwards and fed the frame-rate latch a negative frame.
    const frameMs = Math.max(0, nowReal - last); // the real duration, before the clamp below
    const dt = Math.min(100, frameMs);           // a backgrounded tab must not lurch on return
    last = nowReal;
    // Not while filming: a film's frames are all one step long (33 ms at 30 fps), which the latch
    // would read as a slow device and answer by throwing the picture's quality away.
    if (!capped) freeFrameMs = frameMs;
    if (!looking && !document.hidden && !ctx.renderMode && !capped && !latch.latched) {
      const st = ctx.trip && ctx.trip.state;
      const shown = !!(st && st.phase !== 'idle') || !!(ctx.autopilot && ctx.autopilot.engaged) || !!(ctx.climb && ctx.climb.state && (ctx.climb.state.active || ctx.climb.state.fading));
      const calm = !shown && !cameraMovedLast;
      // Ultra: the ladder is held, so its own guard runs the latch's test and ends Ultra, not the visit's picture.
      if (ultraGuard && ultraGuard.push(frameMs, nowReal)) { ultraGuard = null; if (ctx.quality && ctx.quality.fellBack()) sayFellBack(); }
      if (scaler.push(frameMs, nowReal, calm, !shown)) {
        if (ctx.rendererApi && ctx.rendererApi.setScale) ctx.rendererApi.setScale(scaler.scale);
        window.dispatchEvent(new CustomEvent('sr:scale', { detail: { ratio: scaler.scale, step: scaler.step } }));
      }
      if (scaler.atFloor && latch.push(frameMs, nowReal)) degrade();
    } else if (!looking && capped && !document.hidden && !ctx.renderMode && !latch.latched && scaler.rest(nowReal)) {
      // The resting view (the idle cap): its frames are slow by choice and never fed above, so the ladder
      // would wait for the next thing that moves. Rest counts as quick (scene/quality.js).
      if (ctx.rendererApi && ctx.rendererApi.setScale) ctx.rendererApi.setScale(scaler.scale);
      window.dispatchEvent(new CustomEvent('sr:scale', { detail: { ratio: scaler.scale, step: scaler.step } }));
    }
    if (ctx.quality && !document.hidden) {
      if (!capped) ctx.quality.frame(frameMs, nowReal, latch.latched);
      if (nowReal - lastTierTick >= 1000) { lastTierTick = nowReal; ctx.quality.tick(nowReal); }
    }

    clock.tick(dt);
    const t = clock.now();
    stage.setTime(t);   // every frame conversion this tick reads it; set it before anything does
    ctx.frameT = t;     // and positionOfRecord answers for it until this frame is done (see there)

    resize();
    if (ctx.viewShift) ctx.viewShift.update(dt);
    // A running climb puts the camera where it has got to, before the rig reads it (scene/climb.js).
    if (ctx.climb) ctx.climb.tick(frameMs);
    cameraRig.update(dt, frameMs);
    worlds.setEclipseAllowed(ctx.eclipseDrawn());
    worlds.update(t);
    // A followed WORLD has only now been placed for this frame: the camera aims at it again
    // (scene/camera.js refollow says what a frame's lag looked like).
    cameraRig.refollow();
    // After the worlds and the camera: each light sits on this frame's disc (scene/worldlights.js).
    if (ctx.worldLights) ctx.worldLights.update(t);
    // What waited for the scene to stand at a new time (ctx.afterClockJump): a flight to somewhere
    // that has just moved. After the worlds, so a world's place is this frame's; the camera's own
    // update comes round again next frame.
    if (ctx.afterUpdate && ctx.afterUpdate.length) {
      // Run what is queued now; a callback that queues another waits for the next frame. In place
      // (no splice(0) array per frame, #529).
      const q = ctx.afterUpdate;
      const n = q.length;
      try { for (let i = 0; i < n; i++) q[i](); } finally { q.copyWithin(0, n); q.length -= n; }
    }
    // The Sun close up, after the worlds have moved: its grain, its corona's plane and today's
    // spots, counted from the meridian that faces the Earth (scene/sun.js).
    if (ctx.sunDetail) {
      const earth = worlds.meshFor('earth');
      ctx.sunDetail.update(t, worlds.discShare('sun'), earth ? earth.position : null, { still: !!(reducedMotionQuery && reducedMotionQuery.matches) || motionStill(), look: ctx.look() });
      // A prominence came over the limb, a flare began or faded, a CME left: the Sun's card says so.
      const doing = ctx.sunDetail.activity();
      if (doing !== sunDoing) { sunDoing = doing; if (ctx.quality) window.dispatchEvent(new CustomEvent('sr:tier', { detail: ctx.quality.describe() })); }
    }
    // Live or static clouds, by how far the clock is from the picture (data/gibs.js cloudMode).
    if (ctx.liveClouds) ctx.liveClouds.tick(t);
    // The aurora after the Earth's update (it reads the Earth's rotation and Sun): its box, the
    // latch (fewer steps, no folds), reduced motion (the folds hold still), the tier, and how big
    // the Earth is drawn (no draw call for a dot).
    if (ctx.aurora) {
      ctx.aurora.setTier(ctx.quality ? ctx.quality.tier : 1);
      ctx.aurora.tick(t, {
        on: ctx.isLayerOn('aurora'),
        latched: latch.latched || lowChosen,
        look: ctx.look(),
        top: !!(ctx.quality && ctx.quality.ultra),
        reducedMotion: !!(reducedMotionQuery && reducedMotionQuery.matches) || motionStill(),
        discShare: worlds.discShare ? worlds.discShare('earth') : 1,
      });
    }
    // The weather after the worlds' update too: the clock's time into the bands, and this second's
    // lightning (scene/weather/). The stand-in's tick is empty.
    if (ctx.weather) {
      ctx.weather.tick(t, {
        on: ctx.isLayerOn('lightning'),
        latched: latch.latched || lowChosen,
        reducedMotion: !!(reducedMotionQuery && reducedMotionQuery.matches) || motionStill(),
        discShare: worlds.discShare ? worlds.discShare('earth') : 1,
        viewportH: ctx.renderer && ctx.renderer.domElement ? ctx.renderer.domElement.height : 800,
        pixelRatio: ctx.renderer && ctx.renderer.getPixelRatio ? ctx.renderer.getPixelRatio() : 1,
      });
    }

    // A stage hand-off has just changed what one unit is (scene/climb.js): every mark is placed again
    // on this frame, not on the next tenth of a second.
    if (ctx.marksStale) { ctx.marksStale = false; sinceLayerUpdate = Infinity; }
    // Glyph positions are the expensive part. At 1x they need no more than ~10 Hz to look
    // continuous at orbital speeds; while scrubbing they need every frame or the motion stutters.
    sinceLayerUpdate += dt;
    const interval = clock.mode === 'live' && clock.rate === 1 ? 100 : 0;
    if (sinceLayerUpdate >= interval) {
      sinceLayerUpdate = 0;
      for (const [id, gl] of glyphLayers) {
        const layer = layerRec(id);
        const drawable = ctx.isLayerDrawable ? ctx.isLayerDrawable(layer) : ctx.isLayerOn(id);
        // EVERY layer, not only the ladder's. This used to be `ladderOnly &&`, which was enough
        // when the ladder was the only reason a layer that is ON is not drawn. It is the only
        // reason again now that the trip's focus is gone, but asking every layer costs a boolean
        // and stops the next reason from arriving as a layer that never comes back.
        if (gl.setVisible) gl.setVisible(drawable);
        if (drawable) gl.update(t, ctx.camera);
      }
    } else if (ctx.selected()) {
      // Between two ticks the selection's own dot is placed again, as the camera that follows it
      // and the brackets round it are (scene/glyphs.js updateSelected).
      for (const gl of glyphLayers.values()) if (gl.updateSelected) gl.updateSelected(t);
    }

    // Labels ride the same tick as the glyphs they sit over, so the two never drift apart.
    if (ctx.labels && sinceLayerUpdate === 0) ctx.labels.update(t);
    if (ctx.orbitLine) ctx.orbitLine.update(t);
    // A craft's own path so far (scene/ephpath.js): here only once ui/missions.js has fetched one.
    if (ctx.ephPath) ctx.ephPath.update(t);
    if (ctx.groundTrack) ctx.groundTrack.update(t);
    if (ctx.orbitRings) {
      const st = ctx.trip && ctx.trip.state;
      const tripping = st && st.phase !== 'idle';
      // Outside a trip the Sun's stage IS the Planets tab, framed on the whole system
      // (frameSolarSystem): at true size every planet there is under a pixel, so their paths and
      // dots are drawn, and the tab's list says the dots are larger than the planets.
      ctx.orbitRings.update(t, tripping ? st.orbits : stage.worldId === 'sun' ? SYSTEM_RINGS : null);
    }

    // The ladder's level of detail, on the same tick: how far the camera is from the Sun, in km.
    if (lod && sinceLayerUpdate === 0) {
      const camKm = stage.fromScene(ctx.camera.position);
      const sunKm = stage.toStageFrame({ x: 0, y: 0, z: 0 }, SUN_INERTIAL, t);
      if (camKm && sunKm) lod.apply(Math.hypot(camKm.x - sunKm.x, camKm.y - sunKm.y, camKm.z - sunKm.z));
    }

    // What the frame cost and what the device has already admitted about itself: scene/heroes.js
    // spends a fast machine's headroom on more models and gives it back when the frames say so.
    if (heroes) heroes.update(t, { frameMs: capped ? freeFrameMs : frameMs, latched: latch.latched || lowChosen, saveData, bandPx: ctx.viewShift ? ctx.viewShift.bandHeightPx() : 0 });
    // Stars fade near the Sun's disc (scene/starfield.js sunGlare), wherever the camera is.
    if (starfield && starfield.setSun) starfield.setSun(worlds.drawnPositionOf('sun'), worlds.drawnRadiusUnits('sun'), ctx.camera);
    if (starfield && starfield.update) starfield.update(ctx.camera);
    if (ctx.stars3d) ctx.stars3d.update(ctx.camera, ctx.renderer);
    if (ctx.unplaced) ctx.unplaced.update(ctx.camera, ctx.renderer); // 0079 r15-a45
    // A sky stop's lens (ui/trip.js `zoom`): eased, so a wider figure opens out and never jumps.
    {
      const st = ctx.trip && ctx.trip.state;
      const want = st && st.phase !== 'idle' && st.zoom > 0 ? st.zoom : 1;
      if (ctx.camera.zoom !== want) {
        // By the wall clock, not by the frame: a slow machine opens the lens in the same second.
        const next = ctx.camera.zoom + (want - ctx.camera.zoom) * (1 - Math.exp(-Math.min(1000, Math.max(0, frameMs)) / 450));
        ctx.camera.zoom = Math.abs(next - want) < 0.002 ? want : next;
        ctx.camera.updateProjectionMatrix();
      }
    }
    if (ctx.figures) ctx.figures.update(ctx.camera, ctx.renderer);
    if (ctx.portraits) ctx.portraits.update(ctx.camera, t, frameMs);
    {
      const exoticsOn = isLadderStage(stage.worldId) && ctx.isLayerDrawable(layerRec('exotics'));
      if (ctx.pulsars) ctx.pulsars.update(ctx.camera, ctx.renderer, exoticsOn, nowReal);
      else if (exoticsOn) ctx.wantPulsars(); // asks once: the promise is kept
    }
    // 0079 a8: begin
    {
      const cometsOn = !isLadderStage(stage.worldId) && ctx.isLayerDrawable(layerRec('comets'));
      if (ctx.cometField) ctx.cometField.update(ctx.camera, ctx.renderer, cometsOn, t, ctx.selected() ? ctx.selected().id : null);
      else if (cometsOn) ctx.wantCometField();
    }
    // 0079 a8: end
    if (ctx.earthOverlay) ctx.earthOverlay.update();
    if (ctx.wind) ctx.wind.update();
    if (ctx.currents) ctx.currents.update();
    if (ctx.systems) {
      ctx.systems.setVisible(ctx.isLayerOn('systems'));
      ctx.systems.update(t, ctx.camera);
    }
    if (ctx.galaxy) ctx.galaxy.update(ctx.camera, ctx.renderer);
    if (ctx.shells) ctx.shells.update(ctx.camera, t);
    if (ctx.dsoGlow) ctx.dsoGlow.update(ctx.camera, ctx.renderer, ctx.isLayerDrawable(layerRec('deep-sky')) && !ctx.deepGlowOff);
    if (ctx.deep) ctx.deep.frame({ camera: ctx.camera, renderer: ctx.renderer, nowMs: nowReal, dtMs: frameMs, frameMs, visible: ctx.isLayerDrawable(layerRec('deep-sky')) });
    // 0078 r14-neb-b: the only photograph in the scene is the one the card's slider draws (scene/nebulae.js).
    if (ctx.nebulae) {
      ctx.nebulae.update(ctx.camera, ctx.renderer, ctx.isLayerDrawable(layerRec('deep-sky')));
      // Our stars step aside inside it, as it carries its own (scene/starholes.js, internal #344).
      { const holes = ctx.nebulae.holes(); if (ctx.starfield && ctx.starfield.setHoles) ctx.starfield.setHoles(holes); if (ctx.stars3d && ctx.stars3d.setHoles) ctx.stars3d.setHoles(holes); if (ctx.unplaced) ctx.unplaced.setHoles(holes); /* 0079 r15-a45 */ }
    }
    // Andromeda's slid-in photograph and her stand-in model never draw over each other (scene/galaxy.js); her two
    // companions' ellipses step back with it and with what M31's hero draws of them (0076 W4-a, spec 0075 task 59).
    {
      const m31Photo = ctx.nebulae ? ctx.nebulae.drawn('dso-m31') : 0;
      if (ctx.galaxy) ctx.galaxy.setAndromedaShare(1 - m31Photo);
      if (ctx.dsoGlow) ctx.dsoGlow.setShapedShare((1 - m31Photo) * (1 - (ctx.deep && ctx.deep.heroShareOf ? ctx.deep.heroShareOf('dso-m32') : 0)));
    }
    // A glow gives way to a drawn deep-sky hero (spec 0075 stage b): its own structure stands where the glow stood; and
    // to the slider's photograph while it is drawn. Asked four times a second; the glow's buffer is rewritten only when
    // the answer changes.
    if (ctx.dsoGlow && nowReal - picturedAt > 250) {
      picturedAt = nowReal;
      const now = new Map();
      if (ctx.nebulae) for (const id of ctx.nebulae.loaded()) { const share = Math.round(ctx.nebulae.drawn(id) * 10) / 10; if (share > 0) now.set(id, share); }
      if (ctx.deep && ctx.deep.heroShares) for (const [id, k] of ctx.deep.heroShares()) { const share = Math.round(k * 10) / 10; if (share > (now.get(id) || 0)) now.set(id, share); }
      const key = [...now].join(' ');
      if (key !== picturedKey) { picturedKey = key; ctx.dsoGlow.setPictured(now); }
    }
    if (ctx.otherLight.layer) ctx.updateOtherLight();
    if (ctx.starDisc) {
      ctx.starDisc.update(ctx.camera, ctx.renderer);
      // Past a few pixels the disc IS the star: the catalogue's point for it is switched off.
      const st = ctx.starDisc.state();
      const rec = st.drawn && st.radiusPx >= ctx.starDiscHidePx ? ctx.selected() : null;
      ctx.stars3d.hidePoint(rec && rec.klass === 'star' && rec.meta ? rec.meta.starIndex : -1);
    }
    if (ctx.skyView.active) ctx.skyView.update(t);
    if (ctx.bareFrame) ctx.bareFrame(); // only space (ui/drift.js): marks and lines are not drawn
    render();
    // A drawn world on its plain stage is drawn over the frame; the map's own scene is hidden while
    // it is up (scene/exostage.js), so that render() above clears and draws nothing.
    if (ctx.imagine && ctx.imagine.active) ctx.imagine.render(t);
    if (ctx.replay && ctx.replay.active) ctx.replay.render(nowReal); // 0079 C1
    // After render(), because render() is what brings the camera's matrices up to this frame: placed
    // before it, the brackets trailed the station by one frame of camera motion.
    // The hand-off between two stages, straight after the frame it copies was drawn (scene/climb.js).
    if (ctx.climb) ctx.climb.afterRender();
    if (ctx.hud) ctx.hud.frame(t);
    // The track's minute marks, on this frame's camera (render() brought its matrices up to date).
    if (ctx.trackLabels) ctx.trackLabels.update();
    ctx.frameT = null;  // the frame is drawn: from here the clock answers for itself again
    // Is anything moving? Asked after the frame is drawn, because render() is what brings the
    // camera's matrices up to it. Any reason and the next frames are drawn one for one.
    {
      const st = ctx.trip && ctx.trip.state;
      idle.drew(nowReal, movingReasons({
        film: !!ctx.renderMode,
        clockMode: clock.mode,
        clockRate: clock.rate,
        trip: !!(st && st.phase !== 'idle'),
        autopilot: !!(ctx.autopilot && ctx.autopilot.engaged),
        sky: !!(ctx.skyView && ctx.skyView.active),
        stage: stage.worldId,
        climb: !!((ctx.opening && ctx.opening.live) || (ctx.climb && ctx.climb.state && (ctx.climb.state.active || ctx.climb.state.fading)) || (ctx.imagine && ctx.imagine.active) || (ctx.replay && ctx.replay.active)), // 0079 C1: a moment
        selected: !!ctx.selected(),
        cameraMoved: (cameraMovedLast = cameraMoved()),
        animatedLayer: layerAnimating(nowReal),
        loading: !window.__srLayersReady || gpuChanged(),
      }));
    }
    // The first frame is on screen: from now on the sharper maps may come, when the browser is idle.
    // AFTER THE FIRST VISIT TOO (2026-10-07, internal #415 item 3): the 4k maps are megabytes, and a
    // laptop used to start fetching them beside the catalogues, inside the stretch the byte gate
    // measures -- headless SwiftShader reads as a laptop, so one local run in twelve was 1.3 MB
    // over with no change to the boot graph. They wait for sr:layers-ready and TIERS_MS like
    // everything else that is not the first screen's; a phone (tier 0) fetches none either way.
    if (!tiersStarted && ctx.quality) {
      tiersStarted = true;
      const go = () => afterFirstVisit(TIERS_MS, () => ctx.quality.start());
      if (window.__srLayersReady) go();
      else window.addEventListener('sr:layers-ready', go, { once: true });
    }
    // 0076 W1-e
    // sr.perf (scene/perf.js): what a frame costs, for TOOLS only. Fetched under ?look=1 or when a tool
    // calls sr.wantPerf(); a visitor's loop pays two property reads. No panel (internal spec 0076 W1-e).
    if (ctx.perf) ctx.perf.frame(nowReal, frameMs);
    else if (!ctx.wantPerf) {
      ctx.wantPerf = () => ctx.wantPerf.p || (ctx.wantPerf.p = import('./scene/perf.js').then((m) => (ctx.perf = m.createPerf(ctx))));
      if (ctx.latch && ctx.latch.held) ctx.wantPerf(); // ?look=1
    }
  }
  let tiersStarted = false;
  let lastTierTick = 0;
  requestAnimationFrame(frame);
}

// --- data --------------------------------------------------------------------

async function loadAllLayers(ctx, layerRecords, glyphLayers, scene) {
  // Cheapest and most interesting first: the station is fifteen objects and it is what people
  // came for. The eleven-thousand-object catalogue is last and off by default.
  // The registry's `enabled: false` (spec 0026 req 8): the layer is not created, not loaded, not listed.
  // Data-saver (spec 0026 req 18): on a slow or metered connection the heavy catalogue files wait
  // until the visitor asks for the layer. The layer stays in the panel, off, and the panel says why.
  const saveData = typeof navigator !== 'undefined' && shouldSaveData(navigator.connection);
  // `load: 'on-demand'` (registry/layers.yaml) is the same wait by design: the active catalogue
  // is 7 MB for 16 587 objects, and nobody asked for it until they ticked the box.
  for (const l of LAYERS) l.deferred = !!(saveData && l.heavy) || l.load === 'on-demand' || MORE_CATALOGUES.has(l.id); // 0079 r15-a45: the stage A4 and A5 catalogues wait for a visitor who needs them
  if (saveData) window.dispatchEvent(new CustomEvent('sr:quality', { detail: { level: 'data-saver' } }));
  const ordered = [...LAYERS].filter((l) => l.enabled !== false).sort((a, b) => (a.priority || 50) - (b.priority || 50));

  // The glyph layers are created up front, in priority order, so the scene's draw order is the
  // priority order whatever order the network answers in. Each one starts empty and fills when
  // its records arrive.
  for (const layer of ordered) {
    // A layer another module already draws (the worlds' discs) gets no glyph layer: two marks for
    // one planet would be two places to tap and one of them wrong.
    if (layer.draw === 'worlds' || layer.draw === 'galaxy' || layer.draw === 'stars3d' || layer.draw === 'systems' || layer.draw === 'aurora' || layer.draw === 'lightning') continue;
    const gl = createGlyphLayer(scene, layer);
    // One mark per object: the dot fades out as that record's 3D model fades in.
    // Read through ctx at call time: this function has no `heroes` of its own (the first version
    // named one, and the browser check said `heroes is not defined` -- no unit test could).
    // A deep-sky dot also gives way to its own glow once that is wider than it (scene/dsoglow.js dotYield).
    const deepSky = layer.id === 'deep-sky';
    gl.setModelOpacity((id) => Math.max(ctx.heroes ? ctx.heroes.drawnOpacity(id) : 0, deepSky && ctx.dsoGlow ? ctx.dsoGlow.dotYield(id) : 0));
    gl.setRecords([]);
    // Hidden until its records arrive; one() then sets the real visibility. This loop runs
    // before the first await, and ctx.isLayerOn is attached to ctx after this function is
    // called -- MEASURED: calling it here threw and no layer ever loaded.
    gl.setVisible(false);
    glyphLayers.set(layer.id, gl);
  }

  // Two lanes. MEASURED 2026-09-08: one-at-a-time, the map spent 150 s on two CelesTrak files
  // while the launches layer's snapshot sat ready on our own origin; a three-slot pool alone did
  // not help, because the three highest-priority layers are all CelesTrak-backed and all three
  // slots hung together. So: a layer that is bundled, or whose every source has a usable snapshot
  // in the harvester's manifest, loads in the LOCAL lane at once -- same origin, cheap, no host to
  // wait for. Everything else goes through a small UPSTREAM pool, where priority still decides who
  // starts first and a slow host delays only its own layer. sources.js caps any one live fetch at
  // 20 s besides, so "slow" is bounded.
  const idsOf = (l) => (Array.isArray(l.sources) ? l.sources : l.source ? [l.source] : []);
  const local = [];
  const upstream = [];
  const later = [];
  for (const layer of ordered) {
    if (layer.deferred) continue; // loads when the visitor switches it on (ctx.loadLayerNow)
    // OFF THE FIRST VISIT (LATER_LAYERS, top of this file): after sr:layers-ready, or when asked.
    if (LATER_LAYERS.has(layer.id)) { later.push(layer); continue; }
    // The aurora has no records to load: scene/aurora.js fetches its own forecast, once the layers have landed.
    if (layer.draw === 'aurora' || layer.draw === 'lightning') continue;
    const srcs = idsOf(layer);
    // One cached manifest read behind these, not a request per layer.
    const snaps = srcs.length ? await Promise.all(srcs.map((id) => sources.snapshotAvailable(id))) : [];
    (srcs.length === 0 || snaps.every(Boolean) ? local : upstream).push(layer);
  }

  // Has this layer finished trying (rows or a failure)? A module loaded AFTER the layers landed
  // (ui/trip.js is) missed their `sr:layer` and asks here instead of waiting for it (internal #454).
  ctx.layerLanded = (id) => layerRecords.has(id);
  ctx.loadLayerNow = (layer) => { if (layer && layer.deferred) { layer.deferred = false; return one(layer); } return Promise.resolve(); };
  let laterLoad = null;
  ctx.laterLayersLoaded = () => later.length === 0 || (laterLoad !== null && later.every((l) => layerRecords.has(l.id)));
  // 0079 r15-a45: begin -- the compact catalogues of stages A4 and A5 (data/a45.js, layers `extremes-more`, `pulsars`, `beyond-more`, `quasars`;
  // about 140 kB in all). Not at the idle moment: when a visitor does something that needs them (the search box taking focus, a ladder
  // stage, a link, a trip), or ticks one's box (loadLayerNow). The IAU's names of exoplanet hosts join the planets' aliases with them.
  const CATALOGUE_LAYERS = [...MORE_CATALOGUES];
  let cataloguesLoad = null;
  ctx.loadMoreCatalogues = () => {
    if (!cataloguesLoad) {
      cataloguesLoad = ctx.loadAfterFirstVisit()
        .then(() => Promise.all(CATALOGUE_LAYERS.map((id) => layerRec(id)).filter(Boolean).map((l) => ctx.loadLayerNow(l))))
        .then(() => import('./data/a45.js'))
        .then((m) => m.loadIau().then((doc) => {
          if (m.iauHostAliases(doc, layerRecords.get('exoplanets')) > 0) window.dispatchEvent(new CustomEvent('sr:layer', { detail: { id: 'exoplanets', count: (layerRecords.get('exoplanets') || []).length } }));
        }))
        .catch((e) => console.warn('the extra catalogues did not load', e));
    }
    return cataloguesLoad;
  };
  // 0079 r15-a45: end
  ctx.loadAfterFirstVisit = () => {
    if (!laterLoad) {
      // The star systems' index rides with the exoplanet table it is joined to (internal #466):
      // thirty-nine more host stars for the search box, and a stage for each. Not the systems
      // themselves: scene/systems.js fetches those the first time one is asked for (select()).
      const moreSystems = () => loadSystemIndex().then((rows) => {
        if (!rows.length) return null;
        addSystemRows(rows);
        addPlanetAliases(rows, layerRecords.get('exoplanets'));
        const layer = layerRec('systems');
        return layer ? one(layer) : null;
      });
      laterLoad = Promise.all(later.map((l) => one(l))).then(moreSystems).then(() => {
        window.dispatchEvent(new CustomEvent('sr:later-layers', { detail: { ids: later.map((l) => l.id) } }));
      });
    }
    return laterLoad;
  };
  // The Stars layer's number before its names have landed: the count the names file holds, shipped
  // in scene/stars3d.js (NAMED_STARS, which tests/test_stars3d.mjs holds to the file), so What to
  // show says "3 390" from the first frame rather than "loading" for a layer that is there.
  for (const layer of later) if (layer.draw === 'stars3d') layer.count = () => ctx.stars3d.count() ?? NAMED_STARS;

  // A big SGP4 catalogue is propagated in a worker (propagate/pool.js, public #285): fetched the
  // first time a layer lands with POOL_MIN_SGP4 such records, never at boot. `?worker=0` keeps the
  // old in-thread path, for measuring one against the other. The number is pool.js's
  // POOL_MIN_RECORDS (tests/test_propagate_worker.mjs holds the two together).
  const POOL_MIN_SGP4 = 2000;
  const poolOff = typeof location !== 'undefined' && /[?&]worker=0\b/.test(location.search);
  function wantPool(gl, records) {
    if (poolOff || !gl.setPool || gl.poolStats() || typeof Worker !== 'function') return;
    if (!records || records.length < POOL_MIN_SGP4) return;
    // SGP4 sets and two-body elements both (the debris view's 14 604 are `kepler`, internal #551).
    let n = 0;
    for (const r of records) if (r && (r.propagator === 'sgp4' || r.propagator === 'kepler')) n++;
    if (n < POOL_MIN_SGP4) return;
    import('./propagate/pool.js')
      .then((m) => { if (!gl.poolStats()) gl.setPool(m.createPropagationPool()); })
      .catch((err) => console.warn('propagation worker not available; propagating in the page', err));
  }

  async function one(layer) {
    const gl = glyphLayers.get(layer.id);
    try {
      const records = layer.draw === 'stars3d' ? await ctx.stars3d.load() : await loadLayer(layer, clock.now());
      layerRecords.set(layer.id, records || []);
      if (layer.draw === 'stars3d') {
        // The panel's count is the number DRAWN, not the number of named records.
        layer.count = () => ctx.stars3d.count() ?? (records || []).length;
        ctx.stars3d.setVisible(ctx.isLayerOn(layer.id));
      }
      if (gl) {
        if (ctx.colourKeyFn && gl.recolour) gl.recolour(ctx.colourKeyFn);
        gl.setRecords(records || []);
        gl.setVisible(ctx.isLayerOn(layer.id));
        wantPool(gl, records);
      }
      window.dispatchEvent(new CustomEvent('sr:layer', { detail: { id: layer.id, count: (records || []).length } }));
    } catch (err) {
      // A layer that fails is a layer that is absent, never a page that is broken.
      console.warn(`layer ${layer.id} did not load`, err);
      layerRecords.set(layer.id, []);
      window.dispatchEvent(new CustomEvent('sr:layer', { detail: { id: layer.id, count: 0, error: String(err) } }));
    }
  }
  // A source that answers AFTER its layers have drawn -- a live file arriving behind a saved copy,
  // or a background refresh once a cadence has passed (data/sources.js) -- redraws those layers.
  // sources.onUpdate() existed and nothing listened, so a refresh reached the cache and never the
  // map. A layer still on its first load is skipped: its own load is about to draw the same data.
  const drawnAt = new Map();
  sources.onUpdate((sourceId, result) => {
    if (!result || result.data == null) return;
    const stamp = result.fetchedAt || 0;
    if (drawnAt.get(sourceId) === stamp) return;
    drawnAt.set(sourceId, stamp);
    for (const layer of ordered) {
      if (layer.deferred || !layerRecords.has(layer.id)) continue;
      if (idsOf(layer).includes(sourceId)) one(layer);
    }
  });

  function pool(list, size) {
    let next = 0;
    const worker = async () => {
      while (next < list.length) await one(list[next++]);
    };
    return Promise.all(Array.from({ length: Math.min(size, list.length) }, worker));
  }
  await Promise.all([pool(local, 6), pool(upstream, 3)]);
}

// --- sound ---------------------------------------------------------------------
//
// Spec 0035, 2026-09-23. The engine, its beds and stings, and the four events they follow. Every
// hook is a listener on something the app already says out loud -- `sr:stage`, `sr:veil`, the
// trip's own onChange -- so no module that moves the camera knows sound exists, and each of them
// still runs in node without it.

function wireSound(ctx) {
  const audio = createAudio();
  const loader = createLoader(audio);
  audio.beds = createBeds(audio, AUDIO, { loader });
  audio.stings = createStings(audio, audio.beds, AUDIO, { loader });
  const here = () => rungOf(ctx.stage.worldId, isLadderStage);
  // Turned on (or the first gesture of a visit that remembered "on"): the bed for where we are.
  // Turned off: the engine ramps the master down and suspends; the bed is kept for next time.
  audio.onChange((on) => { if (on) audio.beds.enter(here()); });
  window.addEventListener('sr:stage', () => audio.beds.enter(here()));
  // The stage sting lands in the black, where the change of room is (spec 0034's veil).
  document.addEventListener('sr:veil', (e) => {
    if (e && e.detail && e.detail.phase === 'covered') audio.stings.play('stage');
  });
  // `arrive` when a stop's flight lands (ui/trip.js arrived() is the only way into `settle`), `end`
  // when the end card shows. Read from phase edges, so a render that repeats a phase is not a
  // second chime, and a Next that collapses a flight still lands exactly once.
  let phase = 'idle';
  if (ctx.trip && typeof ctx.trip.onChange === 'function') {
    ctx.trip.onChange((st) => {
      const next = st && st.phase;
      if (next === phase) return;
      // The arrival chime stands down for a stop that is read aloud (spec 0069): the voice is the
      // arrival, and a chime under its first words is two things at once. `narration` is put on
      // the engine by ui/tripframe.js, which is loaded by the time any flight lands.
      const read = audio.narration && audio.narration.willSpeak(`${st.tourId}/${st.stopId}`);
      if (next === 'settle') { if (!read) audio.stings.play('arrive'); }
      else if (next === 'outro') audio.stings.play('end');
      phase = next;
    });
  }
  return audio;
}

// --- the link ------------------------------------------------------------------
//
// A deep link (spec 0032): `#trip=moon-landings&stop=3`, `#at=europa`, `#t=2027-08-02T10:00:00Z`,
// `#stage=saturn`, in any combination. Read once, at boot. The clock is applied then (every
// position is a function of it, and it needs nothing loaded: ui/urlstate.js bootLink); the rest
// once the layers land, in this order: the stage first (a record is selected on the
// map it is drawn on), then EITHER a trip OR a selection -- a trip selects its own stops, so `at`
// beside `trip` would fight it. A key that names nothing known is ignored and the scene says so
// in one line (ui/scenenote.js); the rest of the link still applies, and the dead key leaves the
// address bar so a link copied from here does not carry it on.

function linkNote(ctx, line, deadKeys) {
  if (ctx.sceneNote && typeof ctx.sceneNote.say === 'function') ctx.sceneNote.say(line);
  if (deadKeys) clearUrlState(deadKeys);
}

function applyUrlState(ctx, st) {
  if (!st) return;
  // A newer format: this reader cannot tell what the keys it does recognise mean in it, so it
  // applies none of them rather than half of a link.
  if (st.unknownVersion) { linkNote(ctx, COPY.link.unknownVersion); return; }
  // `t` and `rate` are not here: ui/urlstate.js bootLink() applied them at boot, and took them out.
  if (st.stage) {
    if (STAGES[st.stage]) ctx.setStage(st.stage);
    else linkNote(ctx, COPY.link.unknownStage, ['stage']);
  }
  // A trip that is not on this map is ignored like any other unknown key: `at` still applies.
  if (st.trip && openTrip(ctx, st)) return;
  // A mission's event (ui/missions.js): it selects its own record, so `at` beside it is not read.
  if (st.event && typeof ctx.wantMissions === 'function') {
    // A known event whose record is in a layer that has not landed is waited for (ui/missions.js openEventWhenReady), not called unknown.
    ctx.wantMissions().then((m) => (m ? m.openEventWhenReady(ctx, st.event) : false)).then((ok) => {
      if (ok) return;
      linkNote(ctx, COPY.mission.unknown, ['event']);
      if (st.at) openAt(ctx, st.at);
    });
    return;
  }
  if (st.at) openAt(ctx, st.at);
  if (st.cam) applyCam(ctx, st.cam);
}

/**
 * A link's `cam` (internal #397): once the flight the link started has landed, stand where the
 * link's camera stood round the same target. The key then leaves the address bar: from here on
 * the camera is the visitor's, and a link copied later says where it is then (ui/share.js).
 * A trip frames its own stops, so a link into one never carries `cam` (ui/urlstate.js laterLink).
 */
// Counted in frames, not in timers: the camera only moves on a frame, so "the flight has landed"
// is "the rig has not been flying for this many frames in a row". Given up after twenty seconds.
const CAM_QUIET_FRAMES = 20;
const CAM_GIVE_UP_MS = 20000;
function applyCam(ctx, text) {
  const pose = parseCam(text);
  clearUrlState(['cam']);
  const rig = ctx.cameraRig;
  if (!pose || !rig || typeof rig.flyTo !== 'function' || typeof requestAnimationFrame !== 'function') return;
  const rad = Math.PI / 180;
  const began = performance.now();
  let quiet = 0;
  const look = () => {
    quiet = rig.state && rig.state.flying ? 0 : quiet + 1;
    if (quiet < CAM_QUIET_FRAMES && performance.now() - began < CAM_GIVE_UP_MS) { requestAnimationFrame(look); return; }
    rig.flyTo({ azimuth: pose.azimuthDeg * rad, polar: pose.polarDeg * rad, distance: pose.distanceKm / stage.unitKm, ms: 600 });
  };
  requestAnimationFrame(look);
}

/** @returns {boolean} whether the link named a trip this map has (and so is starting it). */
function openTrip(ctx, st) {
  const tour = ctx.trip.tours().find((x) => x.id === st.trip);
  if (!tour) { linkNote(ctx, COPY.link.unknownTrip, ['trip', 'stop']); return false; }
  // Through start(), so the intro card and its count are honest: a link into stop 3 still shows
  // "10 stops, about four minutes" and Start -- a decision rather than an ambush (spec 0025 §4) --
  // and Start then flies to stop 3 (ui/trip.js jumpTo). A trip that cannot reach its own minimum
  // today is refused by start() and the panel row says why; nothing to add here.
  // The stop is looked up once start() has resolved: until then ctx.trip.tours() is the index, which
  // has no stops (ui/tripgate.js).
  ctx.trip.start(tour.id).then((plan) => {
    if (!plan || plan.offerable === false) return;
    const index = stopIndex(ctx.trip.tours().find((x) => x.id === tour.id), st.stop);
    if (index > 0) ctx.trip.jumpTo(index);
    // An embed has no intro card to press Start on: the trip plays (ui/embed.js).
    if (ctx.embed) ctx.trip.play();
  });
  return true;
}

/**
 * What `#at=` names. A record id first (`sat-25544`, `europa`), then a bare catalogue number
 * (`25544`), then what a person would type (`iss`, `hubble`), through the same index and alias
 * table as the search box. Only a whole-name, name-prefix or word-prefix match counts: a
 * letters-inside-a-word hit is a guess, and a link must not fly somewhere it did not name.
 */
function resolveAt(ctx, id) {
  const text = String(id || '').trim();
  if (!text) return null;
  // A planet or a star system by its bare id, as `#go=` gives it: `kepler-186-f`, `kepler-186`.
  const direct = ctx.recordById(text) || ctx.recordById(`exo-${text}`) || ctx.recordById(`star-${text}`);
  if (direct) return direct;
  // A number, Alpha-5 too (`100690`, `A0690`); and `int-<designator>`, the id a number of 90 000 and up had before 2026-10-14.
  const sat = satIdOf(text) && ctx.recordById(satIdOf(text));
  if (sat) return sat;
  const records = typeof ctx.records === 'function' ? ctx.records() : [];
  const intl = /^int-(.+)$/.exec(text);
  const byIntl = intl && records.find((r) => r && r.meta && r.meta.intlDesignator === intl[1]);
  if (byIntl) return byIntl;
  const found = findMatches(buildIndex(records, ctx.layers || []), text.replace(/[-_+]+/g, ' '), 1);
  const hit = found.hits[0];
  return hit && !found.fallback && hit.score >= LINK_MIN_SCORE ? hit.record : null;
}

function openAt(ctx, id) {
  const record = resolveAt(ctx, id);
  // A star or an exoplanet before its layer has landed (OFF THE FIRST VISIT): load it, then look again.
  if (!record && ctx.loadAfterFirstVisit && !ctx.laterLayersLoaded()) {
    ctx.loadAfterFirstVisit().then(() => ctx.loadMoreCatalogues()).then(() => openAt(ctx, id), () => openAt(ctx, id)); // 0079 r15-a45: a link may name a pulsar or a cluster
    return;
  }
  if (!record) { linkNote(ctx, COPY.link.unknownAt, ['at']); return; }
  // Spec 0021's rule, as ui/search.js: a record whose layer is off has no mark and no model, so
  // flying to it arrives at empty sky. The layer goes on first, and the panel is told (it paints
  // its checkboxes from its own state, and ignores an event with no `from` as its own echo).
  if (record.layer && !ctx.isLayerOn(record.layer)) {
    ctx.setLayerOn(record.layer, true);
    document.dispatchEvent(new CustomEvent('sr:layer-toggle', { detail: { id: record.layer, on: true, handled: true, from: 'link' } }));
  }
  ctx.select(record, { fly: true });
}

// --- small helpers -----------------------------------------------------------

function readMomentFromHash() {
  return readMoment(MOMENTS) || 'wonder';
}

function revealUI() {
  // The UI modules append their own roots when they are constructed, so there is nothing to
  // un-hide. Kept as the one place that would change if that ever stops being true.
}

function fadeBoot() {
  const boot = document.getElementById('boot');
  if (!boot) return;
  boot.classList.add('gone');
  setTimeout(() => boot.remove(), 700);
}
